import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";

// ─── CONFIG SUPABASE ──────────────────────
const SUPABASE_URL = "https://nmddkyutkwiedkxsjsys.supabase.co";
const SUPABASE_ANON_KEY = "sb_publishable_dafI0othNveI0TqpQu9HDQ_C0AghS6t";

// ─── ELIVIO DESIGN TOKENS ─────────────────
const NAVY = "#0d1b3e";
const TEAL = "#4bbfb5";
const E = {
  navy:        "#0d1b3e",
  navyMid:     "#1a2f5e",
  teal:        "#4bbfb5",
  tealLight:   "#e8f7f6",
  tealMid:     "#3aada3",
  white:       "#ffffff",
  offWhite:    "#f4f6f9",
  gray:        "#6b7a90",
  grayLight:   "#e8ecf2",
  text:        "#1a2235",
  textSub:     "#6b7a90",
  red:         "#ef4444",
  redLight:    "#fee2e2",
  orange:      "#f59e0b",
  orangeLight: "#fef3c7",
  orangeDark:  "#b45309",
  blue:        "#60a5fa",
  blueLight:   "#eff6ff",
  green:       "#10b981",
  greenLight:  "#d1fae5",
  shadowSm:    "0 2px 8px rgba(13,27,62,0.06)",
  shadow:      "0 4px 24px rgba(13,27,62,0.08)",
  shadowMd:    "0 8px 32px rgba(13,27,62,0.12)",
  shadowLg:    "0 16px 48px rgba(13,27,62,0.18)",
  zBase:       1,
  zDropdown:   50,
  zSticky:     100,
  zModal:      200,
  zModalTop:   300,
  zToast:      400,
  zOverlay:    500,
  radius:      16,
  radiusSm:    10,
  radiusXs:    6,
  radiusLg:    20,
  radiusFull:  9999,
  touchTarget: 44,
};

// ─── TOAST SYSTEM ─────────────────────────
let toastListeners = [];
export function toast(message, type = "success", duration = 3500) {
  const id = Date.now();
  toastListeners.forEach(fn => fn({ id, message, type, duration }));
}

function ToastContainer() {
  const [toasts, setToasts] = useState([]);
  useEffect(() => {
    const timers = [];
    const handler = (t) => {
      setToasts(prev => [...prev, t]);
      const timer = setTimeout(() => setToasts(prev => prev.filter(x => x.id !== t.id)), t.duration);
      timers.push(timer);
    };
    toastListeners.push(handler);
    return () => {
      toastListeners = toastListeners.filter(fn => fn !== handler);
      timers.forEach(clearTimeout);
    };
  }, []);

  const icons = { success: "✓", error: "✕", info: "ℹ", warning: "⚠" };
  const colors = { success: TEAL, error: E.red, info: E.blue, warning: E.orange };

  return (
    <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: E.zToast, display: "flex", flexDirection: "column", gap: 8, alignItems: "center", pointerEvents: "none" }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          background: NAVY, color: "white", borderRadius: 14, padding: "12px 20px",
          display: "flex", alignItems: "center", gap: 10,
          boxShadow: E.shadowLg,
          animation: "fadeUp 0.3s ease",
          fontFamily: "'Outfit', sans-serif", fontSize: 14, fontWeight: 600,
          borderLeft: `4px solid ${colors[t.type] || TEAL}`,
          minWidth: 220, maxWidth: 340,
        }}>
          <span style={{ color: colors[t.type] || TEAL, fontSize: 16, fontWeight: 800 }}>{icons[t.type] || "✓"}</span>
          {t.message}
        </div>
      ))}
    </div>
  );
}

// ─── SKELETON LOADER ──────────────────────
function Skeleton({ width = "100%", height = 16, radius = 8, style = {} }) {
  return (
    <div style={{
      width, height, borderRadius: radius,
      background: "linear-gradient(90deg, #e8ecf2 25%, #f4f6f9 50%, #e8ecf2 75%)",
      backgroundSize: "200% 100%",
      animation: "shimmer 1.5s infinite",
      ...style,
    }} />
  );
}

function SkeletonCard() {
  return (
    <div style={{ background: "white", borderRadius: 16, padding: 20, boxShadow: E.shadowSm, marginBottom: 12 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 14 }}>
        <Skeleton width={48} height={48} radius={24} />
        <div style={{ flex: 1, display: "grid", gap: 8 }}>
          <Skeleton width="60%" height={14} />
          <Skeleton width="40%" height={12} />
        </div>
      </div>
      <Skeleton height={12} style={{ marginBottom: 8 }} />
      <Skeleton width="80%" height={12} />
    </div>
  );
}

// ─── SPLASH SCREEN ────────────────────────
function SplashScreen({ onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 1800);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div style={{
      position: "fixed", inset: 0, background: NAVY,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      zIndex: E.zOverlay, animation: "none",
    }}>
      <div style={{ animation: "splashPulse 1.8s ease forwards" }}>
        <img loading="lazy"
          src="https://nmddkyutkwiedkxsjsys.supabase.co/storage/v1/object/public/assets/Elivio%20logo.png"
          alt="Elivio"
          style={{ height: 80, filter: "brightness(0) invert(1)", marginBottom: 24 }}
        />
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        {[0,1,2].map(i => (
          <div key={i} style={{
            width: 8, height: 8, borderRadius: "50%", background: TEAL,
            animation: `dotBounce 1.2s ${i * 0.2}s infinite`,
          }} />
        ))}
      </div>
      <style>{`
        @keyframes splashPulse { 0%{opacity:0;transform:scale(0.8)} 50%{opacity:1;transform:scale(1.05)} 100%{opacity:1;transform:scale(1)} }
        @keyframes dotBounce { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-8px)} }
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
      `}</style>
    </div>
  );
}

// ─── EMPTY STATE ──────────────────────────
function EmptyState({ icon, title, desc, cta, onCta }) {
  return (
    <div style={{ textAlign: "center", padding: "48px 20px", animation: "fadeUp 0.4s ease" }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>{icon}</div>
      <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 18, color: NAVY, marginBottom: 8 }}>{title}</div>
      <p style={{ color: "#6b7a90", fontSize: 14, lineHeight: 1.7, marginBottom: cta ? 20 : 0 }}>{desc}</p>
      {cta && <button onClick={onCta} style={{ background: TEAL, color: "white", border: "none", borderRadius: 12, padding: "12px 24px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}>{cta}</button>}
    </div>
  );
}

// ─── NOTIFICATIONS EMAIL ──────────────────
async function sendNotification(type, to, data) {
  if (!to) { console.error(`sendNotification: email vide pour type=${type}`); return; }
  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/send-email`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ type, to, data }),
    });
    const result = await res.json();
    if (!res.ok || result.error) {
      console.error(`sendNotification erreur type=${type} to=${to}:`, result);
    } else {
      console.log(`sendNotification OK type=${type} to=${to}`);
    }
  } catch (e) {
    console.error("sendNotification exception", e);
  }
}

async function createInAppNotification(userId, type, title, message, actionUrl = "notifications") {
  if (!userId) return;
  try {
    await sb.q("notifications").insert({
      user_id: userId,
      type,
      title,
      message,
      action_url: actionUrl,
      read: false,
    });
  } catch (e) {
    console.error("Notification in-app échouée", e);
  }
}

async function notifyAdmins(type, title, message, actionUrl = "admin") {
  try {
    const admins = await sb.q("profiles").eq("role", "admin").get();
    if (Array.isArray(admins)) {
      await Promise.all(admins.map(a => createInAppNotification(a.id, type, title, message, actionUrl)));
    }
  } catch (e) {
    console.error("Notification admin échouée", e);
  }
}


async function recordCoachProfileClick(coachId, viewerId = null) {
  if (!coachId) return;
  try {
    await sb.q("coach_profile_clicks").insert({
      coach_id: coachId,
      viewer_id: viewerId,
      source: "coach_card",
    });
  } catch (e) {
    console.error("Clic profil coach non enregistré", e);
  }
}

// ─── SUPABASE CLIENT ──────────────────────

// ─── SUPABASE CLIENT ──────────────────────
const sb = {
  token: localStorage.getItem("elivio_token") || null,
  h() {
    return {
      "Content-Type": "application/json",
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${this.token || SUPABASE_ANON_KEY}`,
    };
  },
  setToken(t) {
    this.token = t;
    if (t) localStorage.setItem("elivio_token", t);
    else localStorage.removeItem("elivio_token");
  },
  async signUp(email, password, fullName, role = "client") {
    const r = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
      method: "POST", headers: this.h(),
      body: JSON.stringify({ email, password, data: { full_name: fullName, role } }),
    });
    const d = await r.json();
    if (d.access_token) this.setToken(d.access_token);
    if (d.user?.id) localStorage.setItem(`elivio_role_${d.user.id}`, role);
    return d;
  },
  async signIn(email, password) {
    const r = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
      method: "POST", headers: this.h(),
      body: JSON.stringify({ email, password }),
    });
    const d = await r.json();
    if (d.access_token) this.setToken(d.access_token);
    return d;
  },
  signOut() { this.setToken(null); localStorage.removeItem("elivio_user"); },
  q(table) {
    const ctx = { _t: table, _f: [], _sel: "*", _ord: null, _lim: null, _single: false };
    const api = {
      select(c) { ctx._sel = c; return api; },
      eq(c, v) { ctx._f.push(`${c}=eq.${encodeURIComponent(v)}`); return api; },
      neq(c, v) { ctx._f.push(`${c}=neq.${encodeURIComponent(v)}`); return api; },
      order(c, asc = true) { ctx._ord = `${c}.${asc ? "asc" : "desc"}`; return api; },
      limit(n) { ctx._lim = n; return api; },
      single() { ctx._single = true; return api; },
      async get() {
        let url = `${SUPABASE_URL}/rest/v1/${ctx._t}?select=${ctx._sel}`;
        if (ctx._f.length) url += "&" + ctx._f.join("&");
        if (ctx._ord) url += `&order=${ctx._ord}`;
        if (ctx._lim) url += `&limit=${ctx._lim}`;
        const headers = { ...sb.h(), ...(ctx._single ? { Accept: "application/vnd.pgrst.object+json" } : {}) };
        const r = await fetch(url, { headers });
        return r.json();
      },
      async insert(data) {
        const r = await fetch(`${SUPABASE_URL}/rest/v1/${ctx._t}`, {
          method: "POST",
          headers: { ...sb.h(), Prefer: "return=representation" },
          body: JSON.stringify(data),
        });
        return r.json();
      },
      async update(data) {
        let url = `${SUPABASE_URL}/rest/v1/${ctx._t}`;
        if (ctx._f.length) url += "?" + ctx._f.join("&");
        const r = await fetch(url, {
          method: "PATCH",
          headers: { ...sb.h(), Prefer: "return=representation" },
          body: JSON.stringify(data),
        });
        return r.json();
      },
      async del() {
        let url = `${SUPABASE_URL}/rest/v1/${ctx._t}`;
        if (ctx._f.length) url += "?" + ctx._f.join("&");
        const r = await fetch(url, { method: "DELETE", headers: sb.h() });
        return r.ok;
      },
    };
    return api;
  },
};

// ─── DATA ─────────────────────────────────
const questions = [
  { id: "goal", question: "Quel est votre objectif principal ?", options: [
    { label: "Évoluer professionnellement", value: "career", icon: "📈" },
    { label: "Améliorer mon bien-être", value: "wellness", icon: "🌿" },
    { label: "Booster mes performances sportives", value: "sport", icon: "🏅" },
    { label: "Lancer ou développer mon business", value: "business", icon: "🚀" },
  ]},
  { id: "challenge", question: "Quel est votre plus grand défi ?", options: [
    { label: "Manque de confiance en moi", value: "confidence", icon: "💪" },
    { label: "Gestion du stress ou burn-out", value: "stress", icon: "🧘" },
    { label: "Trouver ma voie, me réorienter", value: "direction", icon: "🧭" },
    { label: "Atteindre mes objectifs plus vite", value: "performance", icon: "⚡" },
  ]},
  { id: "style", question: "Quel style d'accompagnement vous correspond ?", options: [
    { label: "Doux et bienveillant", value: "gentle", icon: "🤝" },
    { label: "Structuré et méthodique", value: "structured", icon: "📋" },
    { label: "Direct et challengeant", value: "direct", icon: "🎯" },
    { label: "Flexible selon les séances", value: "flexible", icon: "🔄" },
  ]},
  { id: "budget", question: "Quel est votre budget par séance ?", options: [
    { label: "Moins de 90€", value: "low", icon: "💶" },
    { label: "Entre 90€ et 120€", value: "medium", icon: "💶" },
    { label: "Entre 120€ et 150€", value: "high", icon: "💶" },
    { label: "Pas de limite", value: "any", icon: "✨" },
  ]},
];

const COACH_TYPES = ["Business", "Vie", "Bien-être", "Sportif", "Carrière", "Performance"];
const ALL_SPECIALTIES = ["Leadership", "Entrepreneuriat", "Gestion du temps", "Gestion du stress", "Confiance en soi", "Transitions de vie", "Mental du sportif", "Préparation compétition", "Reconversion professionnelle", "Communication", "Mindfulness", "Burn-out", "Relations", "Vente", "Équipe", "Productivité", "Négociation", "Estime de soi"];
const DAYS = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];

// ─── NOTIFICATIONS DEMO ───────────────────
const DEMO_NOTIFICATIONS = [
  { type: "success", text: "Nouveau rendez-vous confirmé", icon: "✅" },
  { type: "info", text: "Votre profil a été vu 18 fois cette semaine", icon: "👀" },
  { type: "warning", text: "Ajoutez plus de disponibilités", icon: "📅" },
];


const ACCENT_COLORS = ["#4bbfb5", "#0d1b3e", "#3a86ff", E.orange, E.red, "#8b5cf6", "#ec4899", E.green];

function scoreCoach(coach, ans) {
  let s = 50;
  const types = coach.types || [];
  const specs = (coach.specialties || []).map(x => x.toLowerCase());
  if (ans.goal === "career" && types.includes("Carrière")) s += 20;
  if (ans.goal === "wellness" && (types.includes("Bien-être") || types.includes("Vie"))) s += 20;
  if (ans.goal === "sport" && types.includes("Sportif")) s += 20;
  if (ans.goal === "business" && types.includes("Business")) s += 20;
  if (ans.challenge === "stress" && specs.some(x => x.includes("stress") || x.includes("burn"))) s += 15;
  if (ans.challenge === "confidence" && specs.some(x => x.includes("confiance") || x.includes("estime"))) s += 15;
  if (ans.challenge === "direction" && specs.some(x => x.includes("reconversion") || x.includes("transit"))) s += 15;
  if (ans.challenge === "performance" && (types.includes("Performance") || specs.some(x => x.includes("objectif")))) s += 15;
  if (ans.budget === "low" && coach.rate < 90) s += 10;
  if (ans.budget === "medium" && coach.rate >= 90 && coach.rate <= 120) s += 10;
  if (ans.budget === "high" && coach.rate > 120 && coach.rate <= 150) s += 10;
  if (ans.budget === "any") s += 5;
  return Math.min(99, s);
}

// ─── UI PRIMITIVES ────────────────────────

// Logo SVG Elivio (stylisé)
function ElivioLogo({ size = 32, showText = true }) {
  return (
    <img loading="lazy"
      src="https://nmddkyutkwiedkxsjsys.supabase.co/storage/v1/object/public/assets/Elivio%20logo.png"
      alt="Elivio"
      style={{ height: size, width: "auto", display: "block" }}
    />
  );
}

function Avatar({ initials, color = E.teal, size = 48, photoUrl }) {
  if (photoUrl) {
    return (
      <img loading="lazy"
        src={photoUrl}
        alt={initials}
        style={{
          width: size, height: size, borderRadius: "50%",
          objectFit: "cover", flexShrink: 0,
          border: `2px solid ${color}44`,
          boxShadow: `0 4px 12px ${color}22`,
        }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: `linear-gradient(135deg, ${color}, ${color}cc)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.34, fontWeight: 700, color: "#fff",
      flexShrink: 0, fontFamily: "'Outfit', sans-serif",
      boxShadow: `0 4px 12px ${color}44`,
    }}>{initials}</div>
  );
}

function Stars({ rating, interactive, onRate }) {
  const [hover, setHover] = useState(0);
  return (
    <span>
      {[1, 2, 3, 4, 5].map(i => (
        <span key={i}
          onClick={() => interactive && onRate?.(i)}
          onMouseEnter={() => interactive && setHover(i)}
          onMouseLeave={() => interactive && setHover(0)}
          style={{ color: i <= (hover || rating) ? E.orange : E.grayLight, fontSize: interactive ? 24 : 14, cursor: interactive ? "pointer" : "default", letterSpacing: 1 }}
        >★</span>
      ))}
      {!interactive && rating > 0 && <span style={{ color: E.gray, marginLeft: 4, fontSize: 12 }}>{rating}</span>}
    </span>
  );
}

function Tag({ label, color = E.teal }) {
  return (
    <span style={{
      fontSize: 11, fontWeight: 600,
      background: `${color}15`,
      color: color,
      border: `1px solid ${color}30`,
      borderRadius: 20, padding: "2px 10px",
      fontFamily: "'Outfit', sans-serif",
    }}>{label}</span>
  );
}

function Card({ children, style = {}, onClick, hover = false }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => hover && setHovered(true)}
      onMouseLeave={() => hover && setHovered(false)}
      style={{
        background: E.white,
        borderRadius: E.radius,
        boxShadow: hovered ? E.shadowMd : E.shadow,
        border: `1px solid ${hovered ? E.teal + "44" : E.grayLight}`,
        transition: "all 0.2s ease",
        transform: hovered ? "translateY(-3px)" : "none",
        cursor: onClick ? "pointer" : "default",
        ...style,
      }}
    >{children}</div>
  );
}

function Btn({ children, onClick, variant = "primary", full, disabled, small }) {
  const [hovered, setHovered] = useState(false);
  const styles = {
    primary: {
      background: hovered ? E.tealMid : E.teal,
      color: E.white, border: "none",
      boxShadow: hovered ? `0 6px 20px ${E.teal}55` : `0 4px 14px ${E.teal}33`,
    },
    navy: {
      background: hovered ? E.navyMid : E.navy,
      color: E.white, border: "none",
      boxShadow: hovered ? `0 6px 20px ${E.navy}55` : `0 4px 14px ${E.navy}22`,
    },
    outline: {
      background: "transparent",
      color: E.navy,
      border: `2px solid ${E.navy}`,
    },
    outlineTeal: {
      background: "transparent",
      color: E.teal,
      border: `2px solid ${E.teal}`,
    },
    ghost: {
      background: hovered ? E.offWhite : "transparent",
      color: E.gray,
      border: `1px solid ${E.grayLight}`,
    },
    danger: {
      background: "rgba(239,68,68,0.08)",
      color: E.red,
      border: "1px solid rgba(239,68,68,0.2)",
    },
    success: {
      background: "rgba(16,185,129,0.08)",
      color: E.green,
      border: "1px solid rgba(16,185,129,0.2)",
    },
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...styles[variant],
        borderRadius: E.radiusSm,
        padding: small ? "6px 14px" : "12px 22px",
        fontSize: small ? 12 : 14,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        width: full ? "100%" : "auto",
        fontFamily: "'Outfit', sans-serif",
        transition: "all 0.15s ease",
        letterSpacing: 0.2,
      }}
    >{children}</button>
  );
}

function Input({ label, type = "text", value, onChange, placeholder, rows }) {
  const [focused, setFocused] = useState(false);
  const s = {
    width: "100%",
    background: E.white,
    border: `1.5px solid ${focused ? E.teal : E.grayLight}`,
    borderRadius: E.radiusSm,
    padding: "11px 14px",
    color: E.text,
    fontSize: 14,
    fontFamily: "'Outfit', sans-serif",
    outline: "none",
    transition: "border-color 0.15s",
    boxShadow: focused ? `0 0 0 3px ${E.teal}15` : "none",
  };
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <div style={{ color: E.gray, fontSize: 12, fontWeight: 600, marginBottom: 8, fontFamily: "'Outfit', sans-serif" }}>{label}</div>}
      {rows
        ? <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows}
            onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
            style={{ ...s, resize: "none" }} />
        : <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
            onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
            style={s} />
      }
    </div>
  );
}

function Divider() {
  return <div style={{ height: 1, background: E.grayLight, margin: "20px 0" }} />;
}

function SectionLabel({ children }) {
  return (
    <div style={{
      fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 11,
      color: E.teal, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 14,
    }}>{children}</div>
  );
}

// ─── AUTH MODAL ───────────────────────────
function AuthModal({ onClose, onAuth, defaultMode = "signin" }) {
  const [mode, setMode] = useState(defaultMode);
  useEffect(() => { setMode(defaultMode); }, [defaultMode]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("client");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  const handle = async () => {
    setLoading(true); setError("");
    try {
      const res = mode === "signin"
        ? await sb.signIn(email, password)
        : await sb.signUp(email, password, name, role);
      if (res.error || res.msg) setError(res.error_description || res.msg || "Erreur");
      else if (mode === "signup") {
        // Affiche le message de vérification email
        setEmailSent(true);
      } else {
        const userId = res.user?.id;
        const userMeta = res.user?.user_metadata || {};
        const finalRole = userMeta.role || "client";
        onAuth({ email, name: userMeta.full_name || email.split("@")[0], id: userId, role: finalRole });

        // Gestion du parrainage à l'inscription
        try {
          const refCode = new URLSearchParams(window.location.search).get("ref");
          if (refCode && mode === "signup") {
            // Trouve le parrain par son code
            const referrers = await sb.q("profiles").get();
            const referrer = Array.isArray(referrers) ? referrers.find(p => generateReferralCode(p.id) === refCode) : null;
            if (referrer && referrer.id !== userId) {
              // Crée le référral
              await sb.q("referrals").insert({ referrer_id: referrer.id, referred_id: userId, code: refCode, status: "pending" });
              // Notif au parrain
              await createInAppNotification(referrer.id, "referral", "Nouveau filleul 🎉", `Un ami vient de s'inscrire avec votre lien de parrainage !`);
            }
          }
        } catch(e) { console.error("Referral error", e); }

        onClose();
      }
    } catch { setError("Erreur réseau"); }
    setLoading(false);
  };

  // Écran de confirmation email envoyé
  if (emailSent) return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(13,27,62,0.5)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: E.zModal, padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: E.white, borderRadius: 20, padding: 32, maxWidth: 400, width: "100%", boxShadow: E.shadowMd, textAlign: "center" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}><ElivioLogo size={36} /></div>
        <div style={{ width: 64, height: 64, background: E.tealLight, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 28 }}>✉️</div>
        <h3 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 20, color: E.navy, marginBottom: 10 }}>Vérifiez votre email</h3>
        <p style={{ color: E.gray, fontSize: 14, lineHeight: 1.7, marginBottom: 8 }}>
          Un email de confirmation a été envoyé à
        </p>
        <p style={{ color: E.navy, fontWeight: 700, fontSize: 14, marginBottom: 20 }}>{email}</p>
        <p style={{ color: E.gray, fontSize: 13, lineHeight: 1.6, marginBottom: 24 }}>
          Cliquez sur le lien dans l'email pour activer votre compte, puis revenez vous connecter.
        </p>
        <Btn full onClick={onClose} variant="outline">Fermer</Btn>
      </div>
    </div>
  );

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(13,27,62,0.5)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: E.zModal, padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: E.white, borderRadius: 20, padding: 32, maxWidth: 400, width: "100%", boxShadow: E.shadowMd }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}><ElivioLogo size={36} /></div>
        <h3 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 20, color: E.navy, marginBottom: 4, textAlign: "center" }}>
          {mode === "signin" ? "Connexion" : "Créer un compte"}
        </h3>
        <p style={{ color: E.gray, fontSize: 13, textAlign: "center", marginBottom: 24 }}>
          {mode === "signin" ? "Bon retour sur Elivio" : "Rejoignez la communauté Elivio"}
        </p>

        {mode === "signup" && (
          <>
            <Input label="Prénom & nom" value={name} onChange={setName} placeholder="Sophie Martin" />
            <div style={{ marginBottom: 14 }}>
              <div style={{ color: E.gray, fontSize: 12, fontWeight: 600, marginBottom: 8, fontFamily: "'Outfit', sans-serif" }}>Je suis…</div>
              <div style={{ display: "flex", gap: 8 }}>
                {[["client", "👤 Je cherche un coach"], ["coach", "🎯 Je suis coach"]].map(([v, l]) => (
                  <button key={v} onClick={() => setRole(v)} style={{
                    flex: 1, padding: "10px 8px", borderRadius: E.radiusSm, cursor: "pointer",
                    background: role === v ? E.tealLight : E.offWhite,
                    border: `2px solid ${role === v ? E.teal : E.grayLight}`,
                    color: role === v ? E.teal : E.gray,
                    fontSize: 12, fontWeight: 700, fontFamily: "'Outfit', sans-serif",
                    transition: "all 0.15s",
                  }}>{l}</button>
                ))}
              </div>
            </div>
          </>
        )}
        <Input label="Email" type="email" value={email} onChange={setEmail} placeholder="vous@email.com" />
        <Input label="Mot de passe" type="password" value={password} onChange={setPassword} placeholder="••••••••" />
        {error && <div style={{ color: E.red, fontSize: 13, marginBottom: 12, background: "rgba(239,68,68,0.06)", borderRadius: 8, padding: "8px 12px" }}>{error}</div>}
        <Btn full onClick={handle} disabled={loading}>
          {loading ? "Chargement…" : mode === "signin" ? "Se connecter" : "Créer mon compte"}
        </Btn>
        <div style={{ textAlign: "center", marginTop: 16, color: E.gray, fontSize: 13 }}>
          {mode === "signin" ? "Pas encore de compte ? " : "Déjà inscrit ? "}
          <span onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
            style={{ color: E.teal, cursor: "pointer", fontWeight: 700 }}>
            {mode === "signin" ? "S'inscrire" : "Se connecter"}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── PAGE PUBLIQUE COACH (MODALE) ─────────
function CoachDetailModal({ coach, user, onBook, onClose }) {
  const [reviews, setReviews] = useState([]);
  const [availabilities, setAvailabilities] = useState([]);
  const [modalBookings, setModalBookings] = useState([]);
  const [modalBlockedSlots, setModalBlockedSlots] = useState([]);
  const [aiText, setAiText] = useState("");
  const [loadingAI, setLoadingAI] = useState(true);
  const [tab, setTab] = useState("about");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    sb.q("reviews").eq("coach_id", coach.id).order("created_at", false).limit(10).get()
      .then(d => setReviews(Array.isArray(d) ? d : []));
    sb.q("availabilities").eq("coach_id", coach.id).order("day_of_week").get()
      .then(d => setAvailabilities(Array.isArray(d) ? d : []));
    sb.q("bookings").eq("coach_id", coach.id).neq("status", "cancelled").get()
      .then(d => setModalBookings(Array.isArray(d) ? d : []));
    sb.q("blocked_slots").eq("coach_id", coach.id).get()
      .then(d => setModalBlockedSlots(Array.isArray(d) ? d : []));

    (async () => {
      try {
        const res = await fetch("https://api.anthropic.com/v1/messages", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            model: "claude-sonnet-4-20250514", max_tokens: 1000,
            messages: [{ role: "user", content: `Présente ce coach en 3 phrases percutantes pour donner envie. Coach: ${coach.name}, spécialités: ${(coach.specialties || []).join(", ")}, bio: ${coach.bio}, tarif: ${coach.rate}€, note: ${coach.avg_rating}/5. En français, chaleureux, sans titre.` }],
          }),
        });
        const d = await res.json();
        setAiText(d.content?.map(b => b.text || "").join("") || "");
      } catch { setAiText(""); }
      setLoadingAI(false);
    })();
  }, [coach]);

  const DAYS = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
  const avgRating = parseFloat(coach.avg_rating) || 0;
  const color = coach.color || E.teal;

  // Même calcul que la modale de réservation : uniquement les créneaux réellement réservables
  const countSlotsForWeek = (weekOffset) => {
    const now = new Date();

    const bookedSlots = new Set(
      (Array.isArray(modalBookings) ? modalBookings : []).map(b => {
        const dt = new Date(b.scheduled_at);
        return `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}-${dt.getHours()}`;
      })
    );

    const isAlreadyBooked = (dt) => {
      const key = `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}-${dt.getHours()}`;
      return bookedSlots.has(key);
    };

    const isBlocked = (dt) => {
      const h = dt.getHours();
      const dateStr = dt.toDateString();
      return (Array.isArray(modalBlockedSlots) ? modalBlockedSlots : []).some(b => {
        const bd = new Date(b.blocked_date + "T00:00:00");
        const startH = parseInt(b.start_time?.slice(0,2) || "0");
        const endH = parseInt(b.end_time?.slice(0,2) || "0");
        return bd.toDateString() === dateStr && startH <= h && endH > h;
      });
    };

    let count = 0;
    availabilities.forEach(av => {
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay() + (weekOffset * 7));
      startOfWeek.setHours(0, 0, 0, 0);

      const slotDate = new Date(startOfWeek);
      slotDate.setDate(startOfWeek.getDate() + av.day_of_week);

      if (slotDate < now && weekOffset === 0) return;

      const startH = parseInt(av.start_time?.slice(0, 2) || "9");
      const endH = parseInt(av.end_time?.slice(0, 2) || "17");

      for (let h = startH; h < endH; h++) {
        const dt = new Date(slotDate);
        dt.setHours(h, 0, 0, 0);
        if (dt < now) continue;
        if (isAlreadyBooked(dt)) continue;
        if (isBlocked(dt)) continue;
        count += 1;
      }
    });
    return count;
  };

  const slotsThisWeek = countSlotsForWeek(0);
  const slotsNextWeek = countSlotsForWeek(1);

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: `Coach ${coach.name} — Elivio`, url });
    } else {
      navigator.clipboard?.writeText(url);
      setCopied(true);
      const t = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(t);
    }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(13,27,62,0.55)", backdropFilter: "blur(8px)", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: E.zModal, padding: "0" }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: E.white, borderRadius: "24px 24px 0 0",
        width: "100%", maxWidth: 560,
        maxHeight: "92vh", overflowY: "auto",
        boxShadow: "0 -8px 40px rgba(13,27,62,0.15)",
      }}>

        {/* Header coloré */}
        <div style={{ background: `linear-gradient(135deg, ${E.navy}, ${E.navyMid})`, padding: "24px 24px 28px", position: "relative" }}>
          {/* Boutons top */}
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
            <button onClick={onClose} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%", width: 44, height: 44, cursor: "pointer", color: E.white, fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }} aria-label="Fermer">✕</button>
            <button onClick={handleShare} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 20, cursor: "pointer", color: E.white, fontSize: 12, fontWeight: 600, padding: "6px 14px", fontFamily: "'Outfit', sans-serif" }}>
              {copied ? "✓ Copié !" : "🔗 Partager"}
            </button>
          </div>

          {/* Profil */}
          <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
            <Avatar initials={coach.avatar} color={color} size={68}
            photoUrl={coach.photo_status === "approved" ? coach.photo_url : null} />
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 20, color: E.white, marginBottom: 4 }}>{coach.name}</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                {(coach.types || []).map(t => (
                  <span key={t} style={{ fontSize: 11, fontWeight: 600, background: `${color}30`, color: E.white, borderRadius: 20, padding: "2px 10px" }}>{t}</span>
                ))}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Stars rating={avgRating} />
                <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 12 }}>{coach.total_reviews || 0} avis · {coach.total_sessions || 0} séances</span>
              </div>
            </div>
          </div>

          {/* Stats rapides */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 20 }}>
            {[
              ["💶", `${coach.rate}€`, "par séance"],
              ["📅", coach.availability_label || "Flexible", "disponibilité"],
              ["🏅", `${coach.years_experience || "—"}`, "ans d'exp."],
            ].map(([icon, val, label]) => (
              <div key={label} style={{ background: "rgba(255,255,255,0.1)", borderRadius: 12, padding: "10px 8px", textAlign: "center" }}>
                <div style={{ fontSize: 16, marginBottom: 4 }}>{icon}</div>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 13, color: E.white }}>{val}</div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)" }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: `1px solid ${E.grayLight}`, background: E.white, position: "sticky", top: 0, zIndex: 10 }}>
          {[["about", "À propos"], ["availability", "Disponibilités"], ["reviews", `Avis (${reviews.length})`]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} style={{
              flex: 1, padding: "13px 8px", background: "transparent", border: "none",
              cursor: "pointer", fontSize: 13, fontWeight: 700,
              fontFamily: "'Outfit', sans-serif",
              color: tab === k ? E.teal : E.gray,
              borderBottom: `2px solid ${tab === k ? E.teal : "transparent"}`,
              marginBottom: -1, transition: "all 0.15s",
            }}>{l}</button>
          ))}
        </div>

        <div style={{ padding: "20px 24px 32px" }}>

          {/* Créneaux disponibles résumé */}
          {availabilities.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, background: slotsThisWeek > 0 ? E.tealLight : E.orangeLight, borderRadius: 10, padding: "8px 14px" }}>
              <span style={{ fontSize: 14 }}>📅</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: slotsThisWeek > 0 ? E.teal : E.orangeDark, fontFamily: "'Outfit', sans-serif" }}>
                {slotsThisWeek > 0
                  ? `${slotsThisWeek} créneau${slotsThisWeek > 1 ? "x" : ""} disponible${slotsThisWeek > 1 ? "s" : ""} cette semaine`
                  : slotsNextWeek > 0
                    ? `Pas de créneau cette semaine — ${slotsNextWeek} disponible${slotsNextWeek > 1 ? "s" : ""} la semaine prochaine`
                    : "Aucun créneau disponible prochainement"
                }
              </span>
            </div>
          )}

          {/* TAB: À propos */}
          {tab === "about" && (
            <>
              {/* Pitch IA */}
              {(loadingAI || aiText) && (
                <div style={{ background: E.tealLight, borderLeft: `4px solid ${E.teal}`, borderRadius: `0 ${E.radiusSm}px ${E.radiusSm}px 0`, padding: "14px 16px", marginBottom: 20 }}>
                  {loadingAI
                    ? <span style={{ color: E.teal, fontSize: 13, fontStyle: "italic" }}>Analyse en cours…</span>
                    : <p style={{ color: E.navy, fontSize: 14, lineHeight: 1.7, margin: 0, fontFamily: "'Outfit', sans-serif" }}>{aiText}</p>
                  }
                </div>
              )}

              {/* Bio */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 11, color: E.teal, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 10 }}>Présentation</div>
                <p style={{ color: E.textSub, fontSize: 14, lineHeight: 1.7, margin: 0,
          whiteSpace: "pre-wrap",
          overflowWrap: "break-word",
          wordBreak: "break-word",
          maxWidth: "100%",
        }}>{coach.bio}</p>
              </div>

              {/* Spécialités */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 11, color: E.teal, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 10 }}>Spécialités</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {(coach.specialties || []).map(s => (
                    <span key={s} style={{ fontSize: 12, color: E.gray, background: E.offWhite, borderRadius: 20, padding: "4px 12px", fontFamily: "'Outfit', sans-serif" }}>{s}</span>
                  ))}
                </div>
              </div>

              {/* Certification */}
              {coach.certification && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 11, color: E.teal, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 10 }}>Certification</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, background: E.offWhite, borderRadius: 10, padding: "10px 14px" }}>
                    <span style={{ fontSize: 18 }}>🏅</span>
                    <span style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: 13, color: E.navy }}>{coach.certification}</span>
                  </div>
                </div>
              )}

              {/* LinkedIn */}
              {coach.linkedin_url && (
                <a href={coach.linkedin_url} target="_blank" rel="noopener noreferrer" style={{
                  display: "flex", alignItems: "center", gap: 8,
                  background: "#f0f4ff", borderRadius: 10, padding: "10px 14px",
                  textDecoration: "none", marginBottom: 20,
                }}>
                  <span style={{ fontSize: 18 }}>💼</span>
                  <span style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: 13, color: "#0077b5" }}>Voir le profil LinkedIn</span>
                </a>
              )}
            </>
          )}

          {/* TAB: Disponibilités */}
          {tab === "availability" && (
            <div>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 11, color: E.teal, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 14 }}>Créneaux disponibles</div>
              {availabilities.length === 0 ? (
                <div style={{ color: E.gray, fontSize: 14, textAlign: "center", padding: "32px 0", fontFamily: "'Outfit', sans-serif" }}>
                  Contactez le coach pour connaître ses disponibilités.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 8 }}>
                  {availabilities.map(a => (
                    <div key={a.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: E.offWhite, borderRadius: 10, padding: "12px 16px" }}>
                      <span style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 13, color: E.navy, minWidth: 40 }}>{DAYS[a.day_of_week]}</span>
                      <span style={{ color: E.gray, fontSize: 13 }}>{a.start_time?.slice(0, 5)} → {a.end_time?.slice(0, 5)}</span>
                      <span style={{ background: E.tealLight, color: E.teal, fontSize: 11, fontWeight: 600, borderRadius: 20, padding: "2px 10px" }}>Disponible</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB: Avis */}
          {tab === "reviews" && (
            <div>
              {reviews.length === 0 ? (
                <div style={{ color: E.gray, fontSize: 14, textAlign: "center", padding: "32px 0" }}>Pas encore d'avis.</div>
              ) : (
                <>
                  {/* Moyenne */}
                  <div style={{ display: "flex", alignItems: "center", gap: 16, background: E.offWhite, borderRadius: 14, padding: "16px 20px", marginBottom: 16 }}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 40, color: E.navy, lineHeight: 1 }}>{avgRating.toFixed(1)}</div>
                      <Stars rating={avgRating} />
                    </div>
                    <div>
                      <div style={{ color: E.navy, fontWeight: 700, fontSize: 14, fontFamily: "'Outfit', sans-serif" }}>{reviews.length} avis</div>
                      <div style={{ color: E.gray, fontSize: 12 }}>{coach.total_sessions || 0} séances réalisées</div>
                    </div>
                  </div>

                  <div style={{ display: "grid", gap: 10 }}>
                    {reviews.map(r => (
                      <div key={r.id} style={{ background: E.white, border: `1px solid ${E.grayLight}`, borderRadius: 12, padding: "14px 16px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                          <Stars rating={r.rating} />
                          <span style={{ color: E.gray, fontSize: 11 }}>{new Date(r.created_at).toLocaleDateString("fr-FR")}</span>
                        </div>
                        {r.comment && <p style={{ color: E.textSub, fontSize: 13, lineHeight: 1.6, margin: 0 }}>{r.comment}</p>}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* CTA Réserver */}
          <div style={{ marginTop: 24, position: "sticky", bottom: 0 }}>
            {coach.has_availabilities ? (
              <Btn full onClick={() => { onClose(); onBook(coach); }} color={color}>
                Réserver une séance avec {coach.name?.split(" ")[0]}
              </Btn>
            ) : (
              <div style={{ background: E.offWhite, borderRadius: 12, padding: "14px", textAlign: "center" }}>
                <p style={{ color: E.gray, fontSize: 13, margin: 0, fontFamily: "'Outfit', sans-serif" }}>
                  📅 Ce coach n'a pas encore renseigné ses disponibilités.<br/>
                  <span style={{ fontSize: 12 }}>Revenez bientôt ou contactez-le directement.</span>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
// ─── FORMAT BIO ───────────────────────────
function FormatBio({ text, style }) {
  if (!text) return null;
  const phoneRegex = /(\+?\d[\d\s\-().]{7,}\d)/g;
  const parts = text.split(phoneRegex);
  return (
    <p style={style}>
      {parts.map((part, i) =>
        phoneRegex.test(part)
          ? <a key={i} href={`tel:${part.replace(/\s/g, "")}`} style={{ color: TEAL, textDecoration: "none" }} onClick={e => e.stopPropagation()}>{part}</a>
          : part
      )}
    </p>
  );
}

function CoachCard({ coach, score, onBook, onSelect, user }) {
  const [bioExpanded, setBioExpanded] = useState(false);
  const bioLong = (coach.bio || "").length > 100;
  const reviewCount = coach.review_count || coach.total_reviews || coach.reviews_count || 0;

  const handleViewProfile = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    await recordCoachProfileClick(coach.id, user?.id || null);
    onSelect(coach);
  };

  return (
    <Card hover onClick={e => { if (!e.defaultPrevented) onSelect(coach); }} style={{ padding: 20, position: "relative", width: "100%", boxSizing: "border-box" }}>
      {score !== undefined && (
        <div style={{
          position: "absolute", top: 16, right: 16,
          background: TEAL, color: E.white,
          borderRadius: 20, padding: "3px 12px",
          fontSize: 12, fontWeight: 700, fontFamily: "'Outfit', sans-serif",
        }}>{score}% match</div>
      )}
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
        <Avatar initials={coach.avatar} color={coach.color || E.teal} size={54}
          photoUrl={coach.photo_status === "approved" ? coach.photo_url : null} />
        <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
            <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 16, color: E.navy, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1 }}>{coach.name}</div>
            <button onClick={handleViewProfile} style={{
              background: E.white,
              color: E.teal,
              border: `1.5px solid ${E.teal}`,
              borderRadius: 12,
              padding: "7px 10px",
              fontSize: 11,
              fontWeight: 800,
              cursor: "pointer",
              fontFamily: "'Outfit', sans-serif",
              whiteSpace: "nowrap",
              boxShadow: "0 3px 10px rgba(75,191,181,0.10)",
            }}>
              👤 Voir le profil
            </button>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 5 }}>
            {(coach.types || []).map(t => <Tag key={t} label={t} color={coach.color || E.teal} />)}
          </div>
          <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
            <Stars rating={parseFloat(coach.avg_rating) || 0} />
            <span style={{ color: E.gray, fontSize: 12 }}>({reviewCount} avis)</span>
          </div>
        </div>
      </div>

      <div style={{ margin: "14px 0 12px" }} onClick={e => { e.stopPropagation(); e.preventDefault(); }}>
        <p style={{
          color: E.textSub, fontSize: 13, lineHeight: 1.65, margin: 0,
          wordBreak: "break-word", overflowWrap: "break-word",
          overflow: bioExpanded ? "visible" : "hidden",
          display: bioExpanded ? "block" : "-webkit-box",
          WebkitLineClamp: bioExpanded ? "unset" : 2,
          WebkitBoxOrient: "vertical",
        }}>{coach.bio}</p>
        {bioLong && (
          <button onClick={e => { e.stopPropagation(); setBioExpanded(v => !v); }} style={{
            background: "transparent", border: "none", color: E.teal, fontSize: 12, fontWeight: 700,
            cursor: "pointer", padding: "4px 0", fontFamily: "'Outfit', sans-serif", display: "block",
          }}>
            {bioExpanded ? "Voir moins ▲" : "Voir plus ▼"}
          </button>
        )}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
        {(coach.specialties || []).slice(0, 3).map(s => (
          <span key={s} style={{ fontSize: 11, color: E.gray, background: E.offWhite, borderRadius: 6, padding: "3px 9px", fontFamily: "'Outfit', sans-serif", whiteSpace: "nowrap" }}>{s}</span>
        ))}
      </div>

      <Divider />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <span style={{ color: E.navy, fontWeight: 800, fontSize: 18, fontFamily: "'Outfit', sans-serif" }}>{coach.rate}€</span>
          <span style={{ color: E.gray, fontSize: 12 }}> / séance</span>
        </div>
        {coach.has_availabilities ? (
          <Btn small onClick={e => { e.stopPropagation(); onBook(coach); }}>Réserver</Btn>
        ) : (
          <span style={{ fontSize: 11, color: E.gray, fontStyle: "italic", fontFamily: "'Outfit', sans-serif" }}>Pas de créneaux</span>
        )}
      </div>
    </Card>
  );
}

// ─── BOOKING MODAL ────────────────────────
function BookingModal({ coach, user, onClose }) {
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [done, setDone] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(0); // 0 = semaine courante

  useEffect(() => {
    // Charge les disponibilités, réservations existantes ET créneaux bloqués
    Promise.all([
      sb.q("availabilities").eq("coach_id", coach.id).get(),
      sb.q("bookings").eq("coach_id", coach.id).neq("status", "cancelled").get(),
      sb.q("blocked_slots").eq("coach_id", coach.id).get(),
    ]).then(([avs, existingBookings, blocked]) => {
      if (!Array.isArray(avs) || avs.length === 0) { setLoadingSlots(false); return; }

      // Créneaux déjà réservés — clé = "YYYY-MM-DD-HH" timezone-safe
      const bookedSlots = new Set(
        (Array.isArray(existingBookings) ? existingBookings : []).map(b => {
          const dt = new Date(b.scheduled_at);
          return `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}-${dt.getHours()}`;
        })
      );

      // Créneaux bloqués
      const blockedList = Array.isArray(blocked) ? blocked : [];

      const isBlocked = (dt) => {
        const h = dt.getHours();
        const dateStr = dt.toDateString();
        return blockedList.some(b => {
          const bd = new Date(b.blocked_date + "T00:00:00");
          const startH = parseInt(b.start_time?.slice(0,2) || "0");
          const endH = parseInt(b.end_time?.slice(0,2) || "0");
          return bd.toDateString() === dateStr && startH <= h && endH > h;
        });
      };

      const isAlreadyBooked = (dt) => {
        const key = `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}-${dt.getHours()}`;
        return bookedSlots.has(key);
      };

      const generated = [];
      const now = new Date();

      for (let week = 0; week < 4; week++) {
        for (const av of avs) {
          const targetDay = av.day_of_week;
          const startOfWeek = new Date(now);
          startOfWeek.setDate(now.getDate() - now.getDay() + (week * 7));

          const slotDate = new Date(startOfWeek);
          slotDate.setDate(startOfWeek.getDate() + targetDay);

          if (slotDate < now) continue;

          const startH = parseInt(av.start_time?.slice(0, 2) || "9");
          const endH = parseInt(av.end_time?.slice(0, 2) || "17");

          // Minimum 2h dans le futur
          const minDt = new Date(now.getTime() + 2 * 60 * 60 * 1000);

          for (let h = startH; h < endH; h++) {
            const dt = new Date(slotDate);
            dt.setHours(h, 0, 0, 0);

            // Exclut les créneaux passés ou dans moins de 2h
            if (dt < minDt) continue;

            // Exclut les réservés
            if (isAlreadyBooked(dt)) continue;

            // Exclut les bloqués
            if (isBlocked(dt)) continue;

            // Détecte si c'est aujourd'hui
            const isToday = dt.toDateString() === now.toDateString();

            generated.push({
              datetime: dt,
              label: dt.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }),
              time: `${String(h).padStart(2, "0")}:00`,
              week,
              isToday,
            });
          }
        }
      }

      generated.sort((a, b) => a.datetime - b.datetime);
      // Déduplique par datetime exact
      const seen = new Set();
      const deduped = generated.filter(s => {
        const key = s.datetime.toISOString();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setSlots(deduped);
      setLoadingSlots(false);
    });
  }, [coach.id]);

  const weeks = [...new Set(slots.map(s => s.week))];

  // Auto-sélectionne la première semaine avec des créneaux
  useEffect(() => {
    if (weeks.length > 0 && !weeks.includes(selectedWeek)) {
      setSelectedWeek(weeks[0]);
    }
  }, [slots]);

  const currentSlots = slots.filter(s => s.week === selectedWeek);

  // Groupe par jour
  const byDay = currentSlots.reduce((acc, s) => {
    const key = s.label;
    if (!acc[key]) acc[key] = [];
    acc[key].push(s);
    return acc;
  }, {});

  const book = async () => {
    if (!selectedSlot) return;
    setLoading(true);

    // Vérifie qu'aucune autre réservation n'existe sur ce créneau (protection double réservation)
    const existing = await sb.q("bookings")
      .eq("coach_id", coach.id)
      .eq("scheduled_at", selectedSlot.datetime.toISOString())
      .neq("status", "cancelled")
      .get();

    if (Array.isArray(existing) && existing.length > 0) {
      toast("Ce créneau vient d'être réservé par quelqu'un d'autre. Veuillez en choisir un autre.", "error");
      // Recharge les créneaux
      setLoading(false);
      setSelectedSlot(null);
      return;
    }

    await sb.q("bookings").insert({
      client_id: user.id,
      coach_id: coach.id,
      scheduled_at: selectedSlot.datetime.toISOString(),
      notes,
      status: "pending",
    });

    // Vérifie si c'est la première réservation pour compléter le parrainage
    try {
      const existingBookings = await sb.q("bookings").eq("client_id", user.id).get();
      if (Array.isArray(existingBookings) && existingBookings.length === 1) {
        // C'est la première réservation — cherche un parrainage en attente
        const pendingRefs = await sb.q("referrals").eq("referred_id", user.id).eq("status", "pending").get();
        if (Array.isArray(pendingRefs) && pendingRefs.length > 0) {
          const ref = pendingRefs[0];
          await sb.q("referrals").eq("id", ref.id).update({ status: "completed", completed_at: new Date().toISOString() });
          // Notif au parrain et au filleul
          await createInAppNotification(ref.referrer_id, "referral_completed", "Parrainage validé ! 🎉", "Votre filleul vient de faire sa première réservation. Votre réduction est activée !");
          await createInAppNotification(user.id, "referral_completed", "Réduction de parrainage ! 🎁", "Vous avez été parrainé. Votre réduction est activée sur cette séance !");
        }
      }
    } catch(e) { console.error("Referral completion error", e); }

    // Notifications email coach + récap client
    try {
      // Récupère l'email coach directement depuis coaches + profiles
      const coachRecord = await sb.q("coaches").eq("id", coach.id || coach.coach_id).single().get();
      const coachUserId = coachRecord?.user_id;
      let coachEmailFinal = coach.email || null;

      if (!coachEmailFinal && coachUserId) {
        const coachProfile = await sb.q("profiles").eq("id", coachUserId).single().get();
        coachEmailFinal = coachProfile?.email || null;
      }

      if (coachEmailFinal) {
        await sendNotification("nouvelle_reservation", coachEmailFinal, {
          coach_name: coach.name,
          client_name: user.name,
          date: selectedSlot.datetime.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }),
          time: selectedSlot.time,
          notes: notes || "",
        });
      }

      // Notif in-app coach
      if (coachUserId) {
        await createInAppNotification(
          coachUserId,
          "nouvelle_reservation",
          "Nouvelle demande de RDV",
          `${user.name} souhaite réserver une séance le ${selectedSlot.datetime.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} à ${selectedSlot.time}.`,
          "coach-dashboard"
        );
      }

      // Email client — utilise l'email du user connecté
      const clientEmail = user.email;
      if (clientEmail) {
        await sendNotification("demande_client", clientEmail, {
          coach_name: coach.name,
          date: selectedSlot.datetime.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }),
          time: selectedSlot.time,
          rate: coach.rate,
        });
        await createInAppNotification(
          user.id,
          "demande_client",
          "Demande envoyée",
          `Votre demande de RDV avec ${coach.name} a bien été envoyée.`,
          "client-dashboard"
        );
      }
    } catch(e) { console.error("Notif erreur", e); }

    setLoading(false);
    setDone(true);
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(13,27,62,0.5)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: E.zModalTop, padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: E.white, borderRadius: 20, padding: 24, maxWidth: 440, width: "100%", boxShadow: E.shadowMd, maxHeight: "88vh", overflowY: "auto" }}>
        {done ? (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <div style={{ width: 64, height: 64, borderRadius: "50%", background: E.tealLight, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: 28 }}>✓</div>
            <h3 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 20, color: E.navy, marginBottom: 8 }}>Demande envoyée !</h3>
            <p style={{ color: E.gray, fontSize: 14, marginBottom: 20 }}>{coach.name} vous confirmera le RDV très bientôt.</p>
            <Btn variant="outline" onClick={onClose}>Fermer</Btn>
          </div>
        ) : (
          <>
            {/* Header coach */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <Avatar initials={coach.avatar} color={coach.color || E.teal} size={44}
                photoUrl={coach.photo_status === "approved" ? coach.photo_url : null} />
              <div>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, color: E.navy }}>{coach.name}</div>
                <div style={{ color: E.gray, fontSize: 13 }}>{coach.rate}€/séance · 60 min</div>
              </div>
              <button onClick={onClose} style={{ marginLeft: "auto", background: E.offWhite, border: "none", borderRadius: "50%", width: 40, height: 40, cursor: "pointer", color: E.gray, fontSize: 16 }} aria-label="Fermer">✕</button>
            </div>

            {loadingSlots ? (
              <div style={{ textAlign: "center", padding: "32px 0", color: E.teal, fontFamily: "'Outfit', sans-serif" }}>
                Chargement des disponibilités…
              </div>
            ) : slots.length === 0 ? (
              <div style={{ textAlign: "center", padding: "32px 0" }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>📅</div>
                <p style={{ color: E.gray, fontSize: 14, fontFamily: "'Outfit', sans-serif" }}>
                  Ce coach n'a pas encore renseigné ses disponibilités.<br/>
                  Contactez-le directement.
                </p>
              </div>
            ) : (
              <>
                {/* Sélecteur semaine */}
                {weeks.length > 1 && (
                  <div style={{ display: "flex", gap: 8, marginBottom: 16, overflowX: "auto" }}>
                    {weeks.map(w => (
                      <button key={w} onClick={() => setSelectedWeek(w)} style={{
                        padding: "5px 12px", borderRadius: 20, cursor: "pointer", fontSize: 12, fontWeight: 600,
                        background: selectedWeek === w ? E.navy : E.offWhite,
                        border: `1px solid ${selectedWeek === w ? E.navy : E.grayLight}`,
                        color: selectedWeek === w ? E.white : E.gray,
                        fontFamily: "'Outfit', sans-serif", whiteSpace: "nowrap",
                      }}>
                        {w === 0 ? "Cette semaine" : `Semaine +${w}`}
                      </button>
                    ))}
                  </div>
                )}

                {/* Créneaux par jour */}
                <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 16 }}>
                  {Object.keys(byDay).length === 0 ? (
                    <p style={{ color: E.gray, fontSize: 13, textAlign: "center", fontFamily: "'Outfit', sans-serif" }}>
                      Aucun créneau disponible cette semaine.
                    </p>
                  ) : (
                    Object.entries(byDay).map(([day, daySlots]) => (
                      <div key={day}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: E.navy, fontFamily: "'Outfit', sans-serif", marginBottom: 8, textTransform: "capitalize" }}>
                          {day}
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                          {daySlots.map((s, i) => {
                            const isSelected = selectedSlot?.datetime.toISOString() === s.datetime.toISOString();
                            return (
                              <button key={i} onClick={() => setSelectedSlot(s)} style={{
                                padding: "8px 14px",
                                borderRadius: 10,
                                cursor: "pointer",
                                fontSize: 13,
                                fontWeight: 700,
                                fontFamily: "'Outfit', sans-serif",
                                background: isSelected ? E.teal : E.offWhite,
                                border: `2px solid ${isSelected ? E.teal : E.grayLight}`,
                                color: isSelected ? E.white : E.navy,
                                transition: "all 0.15s",
                                boxShadow: isSelected ? `0 4px 12px ${E.teal}44` : "none",
                              }}>
                                {s.time}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Message optionnel */}
                {selectedSlot && (
                  <div style={{ animation: "fadeUp 0.2s ease" }}>
                    <div style={{ background: E.tealLight, borderRadius: 10, padding: "12px 14px", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 16 }}>✓</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: E.navy, fontFamily: "'Outfit', sans-serif" }}>
                        {selectedSlot.datetime.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} à {selectedSlot.time}
                      </span>
                    </div>
                    {/* Avertissement créneau aujourd'hui */}
                    {selectedSlot.isToday && (
                      <div style={{ background: E.orangeLight, border: `1px solid ${E.orange}44`, borderRadius: 10, padding: "10px 14px", marginBottom: 10, display: "flex", gap: 8, alignItems: "flex-start" }}>
                        <span style={{ fontSize: 15, flexShrink: 0 }}>⚠️</span>
                        <span style={{ fontSize: 12, color: E.orangeDark, lineHeight: 1.5 }}>
                          Créneau aujourd'hui — il se peut que le coach n'ait pas le temps de confirmer à temps. Pensez à le contacter directement.
                        </span>
                      </div>
                    )}
                    {/* Prix total */}
                    <div style={{ background: E.offWhite, borderRadius: 10, padding: "10px 14px", marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 13, color: E.gray, fontFamily: "'Outfit', sans-serif" }}>Séance de 60 min</span>
                      <span style={{ fontSize: 16, fontWeight: 800, color: E.navy, fontFamily: "'Outfit', sans-serif" }}>{coach.rate}€</span>
                    </div>
                    <Input label="Message pour le coach (optionnel)" value={notes} onChange={setNotes} rows={2} placeholder="Ce que vous souhaitez travailler…" />
                  </div>
                )}

                <div style={{ display: "flex", gap: 8 }}>
                  <Btn variant="ghost" onClick={onClose}>Annuler</Btn>
                  <Btn full onClick={book} disabled={loading || !selectedSlot}>
                    {loading ? <><Spinner size={14} color="white" /> Envoi…</> : "Confirmer la réservation"}
                  </Btn>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── REVIEW MODAL ─────────────────────────
function ReviewModal({ booking, user, onClose, onDone }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!rating) return;
    setLoading(true);
    await sb.q("reviews").insert({ booking_id: booking.id, client_id: user.id, coach_id: booking.coach_id, rating, comment, is_public: true });
    setLoading(false); onDone(); onClose();
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(13,27,62,0.5)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: E.zModalTop, padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: E.white, borderRadius: 20, padding: 28, maxWidth: 400, width: "100%", boxShadow: E.shadowMd }}>
        <h3 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 20, color: E.navy, marginBottom: 8 }}>Laisser un avis</h3>
        <p style={{ color: E.gray, fontSize: 13, marginBottom: 20 }}>Votre retour aide toute la communauté Elivio.</p>
        <div style={{ marginBottom: 16 }}><Stars rating={rating} interactive onRate={setRating} /></div>
        <Input label="Votre commentaire" value={comment} onChange={setComment} rows={4} placeholder="Décrivez votre expérience…" />
        <div style={{ display: "flex", gap: 8 }}>
          <Btn variant="ghost" onClick={onClose}>Annuler</Btn>
          <Btn full onClick={submit} disabled={!rating || loading}>{loading ? <><Spinner size={14} color="white" /> Envoi…</> : "Publier l'avis"}</Btn>
        </div>
      </div>
    </div>
  );
}

// ─── COACH ONBOARDING ─────────────────────
// ─── CONFETTI ─────────────────────────────
function Confetti() {
  const colors = [E.teal, E.navy, E.orange, "#ec4899", "#8b5cf6", E.green];
  const pieces = Array.from({ length: 40 }, (_, i) => ({
    id: i,
    color: colors[i % colors.length],
    left: Math.random() * 100,
    delay: Math.random() * 0.8,
    size: Math.random() * 8 + 6,
    rotation: Math.random() * 360,
  }));
  return (
    <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 999, overflow: "hidden" }}>
      {pieces.map(p => (
        <div key={p.id} style={{
          position: "absolute",
          left: `${p.left}%`,
          top: -20,
          width: p.size,
          height: p.size,
          background: p.color,
          borderRadius: Math.random() > 0.5 ? "50%" : 2,
          transform: `rotate(${p.rotation}deg)`,
          animation: `confettiFall 2s ${p.delay}s ease-in forwards`,
        }} />
      ))}
      <style>{`
        @keyframes confettiFall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

// ─── COACH ONBOARDING ─────────────────────
function CoachOnboarding({ user, onDone }) {
  const [step, setStep] = useState(-1); // -1 = écran de bienvenue
  const [form, setForm] = useState({
    name: user.name || "", bio: "", rate: 100, types: [], specialties: [],
    availability_label: "Flexible", linkedin_url: "", certification: "",
    years_experience: 0, color: E.teal,
    avatar: user.name ? user.name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) : "CO",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [confetti, setConfetti] = useState(false);
  const [coachCount] = useState(Math.floor(Math.random() * 20) + 35); // 35-55 coaches

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleArr = (k, v) => set(k, form[k].includes(v) ? form[k].filter(x => x !== v) : [...form[k], v]);

  // Score de complétude
  const completeness = [
    form.name?.length > 2,
    form.bio?.length > 50,
    form.types?.length > 0,
    form.specialties?.length > 0,
    form.rate > 0,
    !!form.certification,
    !!form.linkedin_url,
    form.years_experience > 0,
  ];
  const score = Math.round((completeness.filter(Boolean).length / completeness.length) * 100);

  // Estimation revenus
  const revenueEstimate = form.rate * 5 * 4;

  // Confettis
  const confettiTimer = useRef(null);
  const stepTimer = useRef(null);

  const triggerConfetti = () => {
    if (confettiTimer.current) clearTimeout(confettiTimer.current);
    setConfetti(true);
    confettiTimer.current = setTimeout(() => setConfetti(false), 2500);
  };

  const nextStep = () => {
    triggerConfetti();
    if (stepTimer.current) clearTimeout(stepTimer.current);
    stepTimer.current = setTimeout(() => setStep(s => s + 1), 300);
  };

  useEffect(() => () => {
    if (confettiTimer.current) clearTimeout(confettiTimer.current);
    if (stepTimer.current) clearTimeout(stepTimer.current);
  }, []);

  const submit = async () => {
    if (!form.name) { setError("Merci de renseigner votre nom."); return; }
    if (!user?.id) { setError("Erreur : utilisateur non connecté."); return; }
    setLoading(true);
    setError("");
    try {
      const result = await sb.q("coaches").insert({ ...form, user_id: user.id, status: "pending" });
      if (Array.isArray(result) && result.length > 0 || result !== null) {
        try {
          const adminProfile = await sb.q("profiles").eq("role", "admin").single().get();
          if (adminProfile?.email) {
            await sendNotification("nouveau_coach", adminProfile.email, {
              coach_name: form.name,
              types: (form.types || []).join(", "),
              rate: form.rate,
            });
          }
        } catch(e) {}
        triggerConfetti();
        setDone(true);
      } else {
        setError("Erreur lors de l'envoi, réessayez.");
      }
    } catch (e) {
      setError("Erreur réseau, réessayez.");
    }
    setLoading(false);
  };

  const TIPS = {
    bio: "💡 Les coaches avec une bio de +100 mots reçoivent 3x plus de demandes",
    rate: `💡 Le tarif moyen sur Elivio est de 85€/séance`,
    types: "💡 Sélectionnez au moins 2 types pour apparaître dans plus de recherches",
    specialties: "💡 Plus vous avez de spécialités, plus votre matching IA sera précis",
  };

  // Écran de bienvenue
  if (step === -1) return (
    <div style={{ padding: "40px 20px", animation: "fadeUp 0.5s ease", maxWidth: 480, margin: "0 auto", textAlign: "center" }}>
      <div style={{ fontSize: 64, marginBottom: 16 }}>🌟</div>
      <h1 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 28, color: E.navy, marginBottom: 12, lineHeight: 1.2 }}>
        Bonjour {form.name?.split(" ")[0] || "!"} 👋
      </h1>
      <p style={{ color: E.gray, fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
        Rejoignez <strong style={{ color: E.teal }}>{coachCount} coaches</strong> qui accompagnent déjà leurs clients sur Elivio et développez votre activité.
      </p>

      <div style={{ display: "grid", gap: 10, marginBottom: 32, textAlign: "left" }}>
        {[
          ["⚡", "3 minutes", "Pour créer votre profil"],
          ["🎯", "Matching IA", "Clients qualifiés envoyés automatiquement"],
          ["📅", "Agenda intégré", "Gérez vos RDV sans effort"],
          ["⭐", "Avis clients", "Construisez votre réputation"],
        ].map(([icon, title, desc]) => (
          <div key={title} style={{ display: "flex", alignItems: "center", gap: 12, background: E.offWhite, borderRadius: 12, padding: "12px 16px" }}>
            <span style={{ fontSize: 22, flexShrink: 0 }}>{icon}</span>
            <div>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 13, color: E.navy }}>{title}</div>
              <div style={{ fontSize: 12, color: E.gray }}>{desc}</div>
            </div>
          </div>
        ))}
      </div>

      <Btn full onClick={() => setStep(0)}>Créer mon profil coach →</Btn>
      <p style={{ fontSize: 12, color: E.gray, marginTop: 12 }}>Gratuit · Sans engagement · Validation sous 24h</p>
    </div>
  );

  // Écran de succès
  if (done) return (
    <div style={{ padding: "40px 20px", animation: "fadeUp 0.5s ease", maxWidth: 480, margin: "0 auto", textAlign: "center", position: "relative" }}>
      {confetti && <Confetti />}
      <div style={{ width: 80, height: 80, borderRadius: "50%", background: E.tealLight, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 36, animation: "fadeUp 0.5s ease" }}>🎉</div>
      <h1 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 26, color: E.navy, marginBottom: 8 }}>
        Bravo {form.name?.split(" ")[0]} !
      </h1>
      <p style={{ color: E.gray, fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
        Votre dossier a été soumis avec succès.<br/>
        L'équipe Elivio le validera dans les <strong>24 heures</strong>.
      </p>

      {/* Badge de bienvenue */}
      <div style={{ background: `linear-gradient(135deg, ${E.navy}, #17346e)`, borderRadius: 20, padding: "20px 24px", marginBottom: 24, color: E.white, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -20, right: -20, width: 80, height: 80, background: `${E.teal}22`, borderRadius: "50%" }} />
        <div style={{ fontSize: 32, marginBottom: 8 }}>🏅</div>
        <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 16, marginBottom: 4 }}>Badge "Nouveau Coach Elivio"</div>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>Débloqué · Visible après validation</div>
      </div>

      {/* Prochaines étapes */}
      <div style={{ display: "grid", gap: 8, marginBottom: 28, textAlign: "left" }}>
        {[
          ["📧", "Email de confirmation envoyé"],
          ["⏱", "Validation sous 24h par notre équipe"],
          ["📅", "Ajoutez vos disponibilités après validation"],
          ["📷", "Uploadez votre photo pour + de visibilité"],
        ].map(([icon, text]) => (
          <div key={text} style={{ display: "flex", alignItems: "center", gap: 10, background: E.offWhite, borderRadius: 10, padding: "10px 14px" }}>
            <span style={{ fontSize: 16 }}>{icon}</span>
            <span style={{ fontSize: 13, color: E.navy, fontFamily: "'Outfit', sans-serif" }}>{text}</span>
          </div>
        ))}
      </div>

      <Btn full onClick={onDone}>Accéder à mon espace →</Btn>
    </div>
  );

  const STEP_LABELS = ["Profil", "Expertise", "Tarif"];

  return (
    <div style={{ padding: "24px 0 40px", animation: "fadeUp 0.4s ease" }}>
      {confetti && <Confetti />}

      {/* Progress bar animée */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span style={{ color: E.teal, fontSize: 12, fontWeight: 700, fontFamily: "'Outfit', sans-serif", letterSpacing: 1, textTransform: "uppercase" }}>
            Étape {step + 1} / {STEP_LABELS.length}
          </span>
          <span style={{ color: E.teal, fontSize: 12, fontWeight: 700, fontFamily: "'Outfit', sans-serif" }}>
            {score}% complété
          </span>
        </div>
        <div style={{ height: 6, background: E.grayLight, borderRadius: 3, overflow: "hidden" }}>
          <div style={{
            height: "100%",
            width: `${((step + 1) / STEP_LABELS.length) * 100}%`,
            background: `linear-gradient(90deg, ${E.teal}, #8be3da)`,
            borderRadius: 3,
            transition: "width 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)",
          }} />
        </div>
        <div style={{ display: "flex", gap: 4, marginTop: 8 }}>
          {STEP_LABELS.map((label, i) => (
            <div key={label} style={{ flex: 1, textAlign: "center" }}>
              <div style={{
                width: 24, height: 24, borderRadius: "50%", margin: "0 auto 4px",
                background: i < step ? E.teal : i === step ? E.navy : E.grayLight,
                color: i <= step ? E.white : E.gray,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 700, fontFamily: "'Outfit', sans-serif",
                transition: "all 0.3s",
              }}>
                {i < step ? "✓" : i + 1}
              </div>
              <div style={{ fontSize: 10, color: i === step ? E.navy : E.gray, fontFamily: "'Outfit', sans-serif", fontWeight: i === step ? 700 : 400 }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 20 }}>

        {/* ── ÉTAPE 0 : Profil ── */}
        {step === 0 && (
          <div style={{ animation: "fadeUp 0.3s ease" }}>
            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 22, fontWeight: 800, color: E.navy, marginBottom: 4 }}>Votre profil public</h2>
            <p style={{ color: E.gray, fontSize: 14, marginBottom: 20 }}>Ces informations seront visibles par les clients.</p>

            {/* Nom avec validation temps réel */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <label style={{ color: E.gray, fontSize: 12, fontWeight: 600, fontFamily: "'Outfit', sans-serif" }}>Nom complet *</label>
                {form.name?.length > 2 && <span style={{ color: E.teal, fontSize: 12, fontWeight: 700 }}>✓ Parfait</span>}
              </div>
              <input value={form.name} onChange={e => set("name", e.target.value)}
                placeholder="Sophie Marchand"
                style={{ width: "100%", boxSizing: "border-box", background: E.offWhite, border: `1.5px solid ${form.name?.length > 2 ? E.teal : E.grayLight}`, borderRadius: E.radiusSm, padding: "10px 14px", color: E.text, fontSize: 14, outline: "none", fontFamily: "'Outfit', sans-serif", transition: "border 0.2s" }}
              />
            </div>

            {/* Bio avec compteur */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <label style={{ color: E.gray, fontSize: 12, fontWeight: 600, fontFamily: "'Outfit', sans-serif" }}>Présentation publique *</label>
                <span style={{ fontSize: 11, color: form.bio?.length >= 100 ? E.teal : E.gray, fontWeight: 600 }}>
                  {form.bio?.length || 0}/100 {form.bio?.length >= 100 ? "✓" : ""}
                </span>
              </div>
              <textarea value={form.bio} onChange={e => set("bio", e.target.value)}
                placeholder="Décrivez votre approche, vos valeurs, votre méthode…"
                rows={4}
                style={{ width: "100%", boxSizing: "border-box", background: E.offWhite, border: `1.5px solid ${form.bio?.length >= 100 ? E.teal : E.grayLight}`, borderRadius: E.radiusSm, padding: "10px 14px", color: E.text, fontSize: 14, outline: "none", fontFamily: "'Outfit', sans-serif", resize: "vertical", transition: "border 0.2s" }}
              />
              {form.bio?.length > 0 && form.bio?.length < 100 && (
                <div style={{ marginTop: 8, fontSize: 11, color: E.orange, background: E.orangeLight, borderRadius: 6, padding: "4px 10px", display: "inline-block" }}>
                  💡 Conseil : une bio de +100 caractères reçoit 3x plus de demandes ({100 - form.bio.length} caractères restants)
                </div>
              )}
              {form.bio?.length >= 100 && (
                <div style={{ marginTop: 8, fontSize: 11, color: E.teal, background: E.tealLight, borderRadius: 6, padding: "4px 10px", display: "inline-block" }}>
                  ✓ Parfait !
                </div>
              )}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
              <div>
                <label style={{ color: E.gray, fontSize: 12, fontWeight: 600, marginBottom: 8, display: "block", fontFamily: "'Outfit', sans-serif" }}>Années d'expérience</label>
                <input type="number" value={form.years_experience} onChange={e => set("years_experience", parseInt(e.target.value) || 0)} placeholder="ex: 5"
                  style={{ width: "100%", boxSizing: "border-box", background: E.offWhite, border: `1.5px solid ${form.years_experience > 0 ? E.teal : E.grayLight}`, borderRadius: E.radiusSm, padding: "10px 14px", color: E.text, fontSize: 14, outline: "none", fontFamily: "'Outfit', sans-serif" }}
                />
              </div>
              <div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <label style={{ color: E.gray, fontSize: 12, fontWeight: 600, marginBottom: 8, display: "block", fontFamily: "'Outfit', sans-serif" }}>Certification</label>
                  {form.certification && <span style={{ color: E.teal, fontSize: 11, fontWeight: 700 }}>✓</span>}
                </div>
                <input value={form.certification} onChange={e => set("certification", e.target.value)}
                  placeholder="ICF, RNCP…"
                  style={{ width: "100%", boxSizing: "border-box", background: E.offWhite, border: `1.5px solid ${form.certification ? E.teal : E.grayLight}`, borderRadius: E.radiusSm, padding: "10px 14px", color: E.text, fontSize: 14, outline: "none", fontFamily: "'Outfit', sans-serif" }}
                />
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <label style={{ color: E.gray, fontSize: 12, fontWeight: 600, marginBottom: 8, display: "block", fontFamily: "'Outfit', sans-serif" }}>LinkedIn (optionnel)</label>
                {form.linkedin_url && <span style={{ color: E.teal, fontSize: 11, fontWeight: 700 }}>✓</span>}
              </div>
              <input value={form.linkedin_url} onChange={e => set("linkedin_url", e.target.value)}
                placeholder="https://linkedin.com/in/…"
                style={{ width: "100%", boxSizing: "border-box", background: E.offWhite, border: `1.5px solid ${form.linkedin_url ? E.teal : E.grayLight}`, borderRadius: E.radiusSm, padding: "10px 14px", color: E.text, fontSize: 14, outline: "none", fontFamily: "'Outfit', sans-serif" }}
              />
            </div>

            {/* Score de complétude */}
            <div style={{ background: E.tealLight, borderRadius: 12, padding: "12px 16px", marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: E.navy, fontFamily: "'Outfit', sans-serif" }}>Complétude du profil</span>
                <span style={{ fontSize: 12, fontWeight: 800, color: E.teal }}>{score}%</span>
              </div>
              <div style={{ height: 4, background: "rgba(75,191,181,0.2)", borderRadius: 2, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${score}%`, background: TEAL, borderRadius: 2, transition: "width 0.4s ease" }} />
              </div>
            </div>
          </div>
        )}

        {/* ── ÉTAPE 1 : Expertise ── */}
        {step === 1 && (
          <div style={{ animation: "fadeUp 0.3s ease" }}>
            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 22, fontWeight: 800, color: E.navy, marginBottom: 4 }}>Votre expertise</h2>
            <p style={{ color: E.gray, fontSize: 14, marginBottom: 20 }}>Aidez-nous à vous matcher avec les bons clients.</p>

            <div style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ color: E.gray, fontSize: 12, fontWeight: 600, fontFamily: "'Outfit', sans-serif" }}>Type de coaching <span style={{ color: E.red }}>*</span></div>
                {form.types.length > 0
                  ? <span style={{ color: E.teal, fontSize: 11, fontWeight: 700 }}>✓ Sélectionné</span>
                  : <span style={{ color: E.gray, fontSize: 11 }}>1 seul</span>
                }
              </div>
              <div style={{ background: E.orangeLight, borderRadius: 8, padding: "6px 10px", marginBottom: 10, fontSize: 11, color: E.orangeDark }}>
                💡 Choisissez <strong>1 seul type</strong> — cela permet un matching précis avec les bons clients.
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {COACH_TYPES.map(t => {
                  const selected = form.types.includes(t);
                  const disabled = !selected && form.types.length >= 1;
                  return (
                    <button key={t} onClick={() => { if (!disabled) toggleArr("types", t); }} style={{
                      padding: "8px 16px", borderRadius: 20, cursor: disabled ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 600,
                      background: selected ? E.teal : disabled ? "#f4f6f9" : E.offWhite,
                      border: `2px solid ${selected ? E.teal : disabled ? E.grayLight : E.grayLight}`,
                      color: selected ? E.white : disabled ? "#c0c8d4" : E.gray,
                      fontFamily: "'Outfit', sans-serif", transition: "all 0.15s",
                      transform: selected ? "scale(1.05)" : "scale(1)",
                      opacity: disabled ? 0.5 : 1,
                    }}>{t}</button>
                  );
                })}
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ color: E.gray, fontSize: 12, fontWeight: 600, fontFamily: "'Outfit', sans-serif" }}>Spécialités</div>
                <span style={{ color: form.specialties.length >= 3 ? E.red : E.teal, fontSize: 11, fontWeight: 700 }}>
                  {form.specialties.length}/3 {form.specialties.length >= 3 ? "— max atteint" : "sélectionnée" + (form.specialties.length > 1 ? "s" : "")}
                </span>
              </div>
              <div style={{ background: E.orangeLight, borderRadius: 8, padding: "6px 10px", marginBottom: 10, fontSize: 11, color: E.orangeDark }}>
                💡 Choisissez <strong>3 spécialités maximum</strong> — un profil ciblé attire 3x plus de clients.
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {ALL_SPECIALTIES.map(s => {
                  const selected = form.specialties.includes(s);
                  const disabled = !selected && form.specialties.length >= 3;
                  return (
                    <button key={s} onClick={() => { if (!disabled) toggleArr("specialties", s); }} style={{
                      padding: "5px 12px", borderRadius: 20, cursor: disabled ? "not-allowed" : "pointer", fontSize: 12,
                      background: selected ? E.tealLight : disabled ? "#f4f6f9" : E.offWhite,
                      border: `1.5px solid ${selected ? E.teal : E.grayLight}`,
                      color: selected ? E.teal : disabled ? "#c0c8d4" : E.gray,
                      fontFamily: "'Outfit', sans-serif", transition: "all 0.15s",
                      opacity: disabled ? 0.5 : 1,
                    }}>{s}</button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── ÉTAPE 2 : Tarif + Aperçu ── */}
        {step === 2 && (
          <div style={{ animation: "fadeUp 0.3s ease" }}>
            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 22, fontWeight: 800, color: E.navy, marginBottom: 4 }}>Tarif & personnalisation</h2>
            <p style={{ color: E.gray, fontSize: 14, marginBottom: 20 }}>Finalisez votre profil.</p>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
              <div>
                <label style={{ color: E.gray, fontSize: 12, fontWeight: 600, marginBottom: 8, display: "block", fontFamily: "'Outfit', sans-serif" }}>Tarif par séance (€) *</label>
                <input type="number" value={form.rate} onChange={e => set("rate", parseInt(e.target.value) || 0)} placeholder="ex: 80"
                  style={{ width: "100%", boxSizing: "border-box", background: E.offWhite, border: `1.5px solid ${form.rate > 0 ? E.teal : E.grayLight}`, borderRadius: E.radiusSm, padding: "10px 14px", color: E.text, fontSize: 14, outline: "none", fontFamily: "'Outfit', sans-serif" }}
                />
              </div>
              <div>
                <label style={{ color: E.gray, fontSize: 12, fontWeight: 600, marginBottom: 8, display: "block", fontFamily: "'Outfit', sans-serif" }}>Disponibilité</label>
                <input value={form.availability_label} onChange={e => set("availability_label", e.target.value)}
                  placeholder="ex : Semaine + week-end"
                  style={{ width: "100%", boxSizing: "border-box", background: E.offWhite, border: `1.5px solid ${E.grayLight}`, borderRadius: E.radiusSm, padding: "10px 14px", color: E.text, fontSize: 14, outline: "none", fontFamily: "'Outfit', sans-serif" }}
                />
              </div>
            </div>

            {/* Estimation revenus */}
            {form.rate > 0 && (
              <div style={{ background: `linear-gradient(135deg, ${E.navy}, #17346e)`, borderRadius: 14, padding: "16px 20px", marginBottom: 16, color: E.white }}>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", marginBottom: 4, fontFamily: "'Outfit', sans-serif" }}>💰 Estimation de revenus</div>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 22, color: E.teal, marginBottom: 4 }}>
                  {revenueEstimate.toLocaleString("fr-FR")}€/mois
                </div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>
                  Avec 5 séances/semaine à {form.rate}€
                </div>
              </div>
            )}

            {/* Couleur */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: E.gray, fontSize: 12, fontWeight: 600, marginBottom: 10, fontFamily: "'Outfit', sans-serif" }}>Couleur de profil</div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {ACCENT_COLORS.map(c => (
                  <div key={c} onClick={() => set("color", c)} style={{
                    width: 32, height: 32, borderRadius: "50%", background: c, cursor: "pointer",
                    border: form.color === c ? `3px solid ${E.navy}` : "3px solid transparent",
                    boxShadow: form.color === c ? `0 0 0 2px ${c}` : "none",
                    transition: "all 0.15s", transform: form.color === c ? "scale(1.2)" : "scale(1)",
                  }} />
                ))}
              </div>
            </div>

            {/* Aperçu temps réel */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: E.gray, fontSize: 12, fontWeight: 600, marginBottom: 10, fontFamily: "'Outfit', sans-serif" }}>👁 Aperçu de votre carte</div>
              <Card style={{ padding: 20 }}>
                <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                  <Avatar initials={form.avatar} color={form.color} size={54} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 16, color: E.navy }}>{form.name || "Votre nom"}</div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 5 }}>
                      {form.types.map(t => <Tag key={t} label={t} color={form.color} />)}
                    </div>
                    <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
                      <Stars rating={5} />
                      <span style={{ color: E.gray, fontSize: 12 }}>Nouveau coach</span>
                    </div>
                  </div>
                </div>
                <p style={{ color: E.textSub, fontSize: 13, lineHeight: 1.65, margin: "14px 0 12px" }}>
                  {form.bio?.slice(0, 120) || "Votre présentation apparaîtra ici…"}{form.bio?.length > 120 ? "…" : ""}
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                  {(form.specialties || []).slice(0, 4).map(s => (
                    <span key={s} style={{ fontSize: 11, color: E.gray, background: E.offWhite, borderRadius: 6, padding: "3px 9px" }}>{s}</span>
                  ))}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: `1px solid ${E.grayLight}`, paddingTop: 12 }}>
                  <div>
                    <span style={{ color: E.navy, fontWeight: 800, fontSize: 18 }}>{form.rate}€</span>
                    <span style={{ color: E.gray, fontSize: 12 }}> / séance</span>
                  </div>
                  <div style={{ background: TEAL, color: E.white, borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 700 }}>Réserver</div>
                </div>
              </Card>
            </div>
          </div>
        )}

        {error && <div style={{ color: E.red, fontSize: 13, background: "rgba(239,68,68,0.06)", borderRadius: 8, padding: "8px 12px" }}>{error}</div>}

        <div style={{ display: "flex", gap: 8 }}>
          {step > 0 && <Btn variant="ghost" onClick={() => setStep(s => s - 1)}>← Retour</Btn>}
          {step < 2
            ? <Btn full onClick={nextStep}>Continuer →</Btn>
            : <Btn full onClick={submit} disabled={loading}>{loading ? <><Spinner size={14} color="white" /> Envoi…</> : "Soumettre mon profil 🚀"}</Btn>
          }
        </div>
      </div>
    </div>
  );
}


// ─── CENTRE DE NOTIFICATIONS IN-APP ───────
function NotificationPanel({ userId, compact = false, onNavigate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadNotifications = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data = await sb.q("notifications")
        .eq("user_id", userId)
        .order("created_at", false)
        .limit(compact ? 4 : 10)
        .get();

      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Chargement notifications échoué", e);
      setItems([]);
    }
    setLoading(false);
  }, [userId, compact]);

  useEffect(() => { loadNotifications(); }, [loadNotifications]);

  const isRead = (n) => n.read === true;

  const markRead = async (notification) => {
    if (!notification?.id) return;
    try {
      await sb.q("notifications").eq("id", notification.id).update({ read: true });
      setItems(prev => prev.map(n => n.id === notification.id ? { ...n, read: true } : n));
    } catch (e) {
      console.error("Lecture notification échouée", e);
    }
    const target = notification.action_url || notification.url || notification.link;
    if (target && onNavigate) {
      const clean = String(target).replace(/^https?:\/\/[^/]+/i, "").replace(/^\//, "");
      if (clean.includes("admin")) onNavigate("admin");
      else if (clean.includes("coach")) onNavigate("coach-dashboard");
      else if (clean.includes("client") || clean.includes("booking") || clean.includes("rdv")) onNavigate("client-dashboard");
    }
  };

  const markAllRead = async () => {
    const unread = items.filter(n => !isRead(n));
    await Promise.all(unread.map(n => sb.q("notifications").eq("id", n.id).update({ read: true })));
    setItems(prev => prev.map(n => ({ ...n, read: true })));
  };

  const iconFor = (type) => {
    if (!type) return "🔔";
    if (type.includes("reservation") || type.includes("rdv") || type.includes("booking")) return "📅";
    if (type.includes("avis") || type.includes("review")) return "⭐";
    if (type.includes("photo")) return "📷";
    if (type.includes("coach")) return "🧑‍💼";
    if (type.includes("annule") || type.includes("cancel")) return "❌";
    if (type.includes("confirm") || type.includes("valide")) return "✅";
    return "🔔";
  };

  const unreadCount = items.filter(n => !isRead(n)).length;

  return (
    <Card style={{ padding: "18px 20px", marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
        <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 18, color: E.navy }}>
          🔔 Notifications
          {unreadCount > 0 && (
            <span style={{ marginLeft: 8, background: TEAL, color: E.white, borderRadius: 20, padding: "2px 8px", fontSize: 11 }}>
              {unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button onClick={markAllRead} style={{ background: "transparent", border: "none", color: E.teal, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}>
            Tout lire
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ color: E.gray, fontSize: 13 }}>Chargement…</div>
      ) : items.length === 0 ? (
        <div style={{ background: E.offWhite, borderRadius: 14, padding: 16, color: E.gray, fontSize: 13, textAlign: "center", fontFamily: "'Outfit', sans-serif" }}>
          Aucune notification pour le moment.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {items.map(n => (
            <button key={n.id} onClick={() => markRead(n)} style={{
              background: isRead(n) ? E.offWhite : E.tealLight,
              border: `1px solid ${isRead(n) ? E.grayLight : `${E.teal}35`}`,
              borderRadius: 14,
              padding: "12px 14px",
              display: "flex",
              alignItems: "flex-start",
              gap: 12,
              textAlign: "left",
              cursor: "pointer",
              fontFamily: "'Outfit', sans-serif",
            }}>
              <div style={{ width: 38, height: 38, borderRadius: 12, background: isRead(n) ? E.white : E.teal, color: isRead(n) ? E.teal : E.white, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
                {iconFor(n.type)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: E.navy, fontWeight: 800, marginBottom: 3 }}>
                  {n.title || "Notification"}
                </div>
                <div style={{ fontSize: 12, color: E.gray, lineHeight: 1.45 }}>
                  {n.message || ""}
                </div>
                {n.created_at && (
                  <div style={{ fontSize: 10, color: E.gray, marginTop: 5 }}>
                    {new Date(n.created_at).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </div>
                )}
              </div>
              {!isRead(n) && <span style={{ width: 8, height: 8, borderRadius: "50%", background: TEAL, marginTop: 6 }} />}
            </button>
          ))}
        </div>
      )}
    </Card>
  );
}



// ─── TO DO LIST COACH ↔ CLIENT ─────────────
function TaskProgress({ tasks }) {
  const total = tasks.length;
  const done = tasks.filter(t => t.status === "done").length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <Card style={{ padding: 18, background: `linear-gradient(135deg, ${E.navy}, ${E.navyMid})`, color: E.white }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div>
          <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 17 }}>🎯 Plan d'action partagé</div>
          <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 12, marginTop: 2 }}>
            {done}/{total} tâche{total > 1 ? "s" : ""} terminée{done > 1 ? "s" : ""}
          </div>
        </div>
        <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 28, color: E.teal }}>{pct}%</div>
      </div>
      <div style={{ height: 10, borderRadius: 20, background: "rgba(255,255,255,0.12)", overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", borderRadius: 20, background: "linear-gradient(90deg,#4bbfb5,#8be3da)", transition: "width .4s ease" }} />
      </div>
    </Card>
  );
}

function TaskStatusBadge({ status }) {
  const cfg = {
    todo: ["À faire", E.orange, E.orangeLight],
    in_progress: ["En cours", "#3b82f6", "#dbeafe"],
    done: ["Terminé", E.teal, E.tealLight],
  }[status || "todo"];
  return <span style={{ fontSize: 10, fontWeight: 800, color: cfg[1], background: cfg[2], borderRadius: 20, padding: "3px 9px", fontFamily: "'Outfit', sans-serif" }}>{cfg[0]}</span>;
}

function TaskCard({ task, role, onStatus, onDelete }) {
  const priority = {
    low: ["Basse", E.gray, "🏳️", "#f4f6f9"],
    normal: ["Normale", E.teal, "🚩", E.tealLight],
    high: ["Prioritaire", E.red, "🚩", E.redLight],
  }[task.priority || "normal"];

  const isDone = task.status === "done";
  return (
    <Card style={{ padding: "14px 16px", border: isDone ? `1.5px solid ${E.teal}35` : `1px solid ${E.grayLight}`, background: isDone ? E.tealLight : E.white }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <button onClick={() => onStatus(task, isDone ? "todo" : "done")} style={{
          width: 30, height: 30, borderRadius: "50%", border: `2px solid ${isDone ? E.teal : E.grayLight}`,
          background: isDone ? E.teal : E.white, color: E.white, cursor: "pointer", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900,
        }}>{isDone ? "✓" : ""}</button>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start", marginBottom: 5 }}>
            <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 14, color: E.navy, textDecoration: isDone ? "line-through" : "none" }}>
              {task.title}
            </div>
            <TaskStatusBadge status={task.status} />
          </div>

          {role === "coach" && <ClientName clientId={task.client_id} />}

          {task.description && (
            <p style={{ color: E.gray, fontSize: 12, lineHeight: 1.55, margin: "6px 0 10px" }}>{task.description}</p>
          )}

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 10, fontWeight: 800, color: priority[1], background: priority[3], borderRadius: 20, padding: "4px 10px", fontFamily: "'Outfit', sans-serif", display: "inline-flex", alignItems: "center", gap: 4 }}>
              <span>{priority[2]}</span> {priority[0]}
            </span>
            {task.due_date && (
              <span style={{ fontSize: 10, color: E.gray, background: E.offWhite, borderRadius: 20, padding: "3px 9px", fontFamily: "'Outfit', sans-serif" }}>
                📅 {new Date(task.due_date).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
              </span>
            )}
            {task.completed_at && (
              <span style={{ fontSize: 10, color: E.teal, background: E.white, borderRadius: 20, padding: "3px 9px", fontFamily: "'Outfit', sans-serif" }}>
                ✓ Fait le {new Date(task.completed_at).toLocaleDateString("fr-FR")}
              </span>
            )}
          </div>

          {role === "coach" && (
            <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
              {task.status !== "todo" && <Btn small variant="ghost" onClick={() => onStatus(task, "todo")}>À faire</Btn>}
              {task.status !== "in_progress" && <Btn small variant="ghost" onClick={() => onStatus(task, "in_progress")}>En cours</Btn>}
              {task.status !== "done" && <Btn small variant="success" onClick={() => onStatus(task, "done")}>Terminé</Btn>}
              <Btn small variant="danger" onClick={() => onDelete(task.id)}>Supprimer</Btn>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function CoachTasksPanel({ coachData, bookings, tasks, setTasks, onRefresh }) {
  const uniqueClients = Array.from(
    new Map(bookings.filter(b => b.client_id).map(b => [b.client_id, b])).values()
  );
  const [clientNames, setClientNames] = useState({});
  const [selectedClient, setSelectedClient] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ client_id: "", title: "", description: "", due_date: "", priority: "normal" });

  useEffect(() => {
    const ids = [...new Set(uniqueClients.map(b => b.client_id).filter(Boolean))];
    ids.forEach(id => {
      if (!clientNames[id]) {
        sb.q("profiles").eq("id", id).single().get().then(p => {
          if (p && !p.error) setClientNames(prev => ({ ...prev, [id]: p.full_name || p.email || "Client" }));
        });
      }
    });
  }, [uniqueClients.length]);

  useEffect(() => {
    if (!form.client_id && uniqueClients[0]?.client_id) {
      setForm(f => ({ ...f, client_id: uniqueClients[0].client_id }));
    }
  }, [uniqueClients.length]);

  const getName = (id) => clientNames[id] || "Client";

  // Double validation : terminé = coach ET client ont validé
  const isFullyDone = (t) => t.status === "done" && t.client_validated === true;
  const isCoachDone = (t) => t.status === "done";
  // Filtres
  const filtered = tasks.filter(t => {
    const clientOk = selectedClient === "all" || t.client_id === selectedClient;
    const statusOk = statusFilter === "all" ? true
      : statusFilter === "todo" ? t.status === "todo"
      : statusFilter === "in_progress" ? t.status === "in_progress"
      : statusFilter === "coach_done" ? (isCoachDone(t) && !isFullyDone(t))
      : statusFilter === "done" ? isFullyDone(t)
      : true;
    return clientOk && statusOk;
  });

  // Stats par client
  const clientStats = (clientId) => {
    const clientTasks = tasks.filter(t => t.client_id === clientId);
    const done = clientTasks.filter(t => isFullyDone(t)).length;
    const total = clientTasks.length;
    return { done, total, pct: total > 0 ? Math.round((done / total) * 100) : 0 };
  };

  const createTask = async () => {
    if (!form.client_id) { toast("Choisissez un client", "error"); return; }
    if (!form.title.trim()) { toast("Ajoutez un titre", "error"); return; }
    setCreating(true);
    const inserted = await sb.q("tasks").insert({
      coach_id: coachData.id, client_id: form.client_id,
      title: form.title.trim(), description: form.description.trim() || null,
      due_date: form.due_date || null, priority: form.priority, status: "todo",
    });
    const created = Array.isArray(inserted) ? inserted[0] : null;
    if (created?.id) {
      setTasks(prev => [created, ...prev]);
      await createInAppNotification(form.client_id, "task_created", "Nouvelle action à réaliser", `${coachData.name} vous a assigné : ${form.title.trim()}`, "client-dashboard");
      toast("Action ajoutée ✓", "success");
      setForm(f => ({ ...f, title: "", description: "", due_date: "", priority: "normal" }));
      setShowForm(false);
    } else { toast("Erreur lors de la création", "error"); }
    setCreating(false);
  };

  const updateStatus = async (task, status) => {
    const payload = { status, completed_at: status === "done" ? new Date().toISOString() : null };
    await sb.q("tasks").eq("id", task.id).update(payload);
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, ...payload } : t));
    if (status === "done") {
      await createInAppNotification(task.client_id, "task_coach_validated", "✅ Votre coach a validé une action", `"${task.title}" — validez-la aussi de votre côté pour la marquer terminée.`, "client-dashboard");
      toast("Action validée côté coach — en attente du client", "info");
    }
  };

  const deleteTask = async (id) => {
    if (!window.confirm("Supprimer cette action ?")) return;
    await sb.q("tasks").eq("id", id).del();
    setTasks(prev => prev.filter(t => t.id !== id));
  };

  const pendingValidation = tasks.filter(t => isCoachDone(t) && !isFullyDone(t));

  return (
    <div style={{ display: "grid", gap: 16 }}>

      {/* Alerte double validation en attente */}
      {pendingValidation.length > 0 && (
        <div style={{ background: E.orangeLight, border: `1.5px solid ${E.orange}44`, borderRadius: 14, padding: "12px 16px", display: "flex", gap: 10, alignItems: "flex-start" }}>
          <span style={{ fontSize: 20 }}>⏳</span>
          <div>
            <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 13, color: E.orangeDark }}>
              {pendingValidation.length} action{pendingValidation.length > 1 ? "s" : ""} en attente de validation client
            </div>
            <div style={{ fontSize: 12, color: E.orangeDark, opacity: 0.8, marginTop: 2 }}>
              Vous avez validé — le client doit confirmer de son côté.
            </div>
          </div>
        </div>
      )}

      {/* Progression par client */}
      {uniqueClients.length > 0 && (
        <Card style={{ padding: "18px 20px" }}>
          <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 14, color: E.navy, marginBottom: 14 }}>
            📊 Plan d'action par client
          </div>
          <div style={{ display: "grid", gap: 12 }}>
            {uniqueClients.map(b => {
              const { done, total, pct } = clientStats(b.client_id);
              if (total === 0) return null;
              return (
                <div key={b.client_id}
                  onClick={() => setSelectedClient(selectedClient === b.client_id ? "all" : b.client_id)}
                  style={{ cursor: "pointer", background: selectedClient === b.client_id ? E.tealLight : E.offWhite, borderRadius: 12, padding: "12px 14px", border: `1.5px solid ${selectedClient === b.client_id ? E.teal : "transparent"}`, transition: "all 0.15s" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                    <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 13, color: E.navy }}>
                      👤 {getName(b.client_id)}
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 800, color: pct === 100 ? E.teal : E.navy }}>{done}/{total} · {pct}%</span>
                  </div>
                  <div style={{ height: 7, background: "rgba(0,0,0,0.08)", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: pct === 100 ? E.teal : `linear-gradient(90deg, ${E.teal}, ${E.tealMid})`, borderRadius: 4, transition: "width 0.5s ease" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Formulaire de création */}
      <Card style={{ padding: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: showForm ? 14 : 0 }}>
          <SectionLabel>Créer une action</SectionLabel>
          <button onClick={() => setShowForm(s => !s)} style={{ background: showForm ? E.redLight : E.tealLight, color: showForm ? E.red : E.teal, border: "none", borderRadius: 20, padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}>
            {showForm ? "✕ Annuler" : "+ Nouvelle action"}
          </button>
        </div>
        {showForm && (
          uniqueClients.length === 0
            ? <EmptyState icon="👥" title="Aucun client pour le moment" desc="Dès qu'un client aura réservé, vous pourrez lui assigner des actions." />
            : <div style={{ display: "grid", gap: 10 }}>
                <div>
                  <div style={{ color: E.gray, fontSize: 11, fontWeight: 700, marginBottom: 5, fontFamily: "'Outfit', sans-serif" }}>Client</div>
                  <select value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))} style={{ width: "100%", background: E.offWhite, border: `1.5px solid ${E.grayLight}`, borderRadius: E.radiusSm, padding: "10px 12px", color: E.text, fontSize: 13, outline: "none", fontFamily: "'Outfit', sans-serif" }}>
                    <option value="">Choisir un client…</option>
                    {uniqueClients.map(b => <option key={b.client_id} value={b.client_id}>👤 {getName(b.client_id)}</option>)}
                  </select>
                </div>
                <Input label="Titre de l'action" value={form.title} onChange={v => setForm(f => ({ ...f, title: v }))} placeholder="Ex : Mettre à jour mon profil LinkedIn" />
                <Input label="Description / consigne" value={form.description} onChange={v => setForm(f => ({ ...f, description: v }))} rows={2} placeholder="Consigne claire, livrable ou objectif." />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div>
                    <div style={{ color: E.gray, fontSize: 11, fontWeight: 700, marginBottom: 5, fontFamily: "'Outfit', sans-serif" }}>Échéance</div>
                    <input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} style={{ width: "100%", boxSizing: "border-box", background: E.offWhite, border: `1.5px solid ${E.grayLight}`, borderRadius: E.radiusSm, padding: "10px 12px", color: E.text, fontSize: 13, outline: "none" }} />
                  </div>
                  <div>
                    <div style={{ color: E.gray, fontSize: 11, fontWeight: 700, marginBottom: 5, fontFamily: "'Outfit', sans-serif" }}>Priorité</div>
                    <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} style={{ width: "100%", boxSizing: "border-box", background: E.offWhite, border: `1.5px solid ${E.grayLight}`, borderRadius: E.radiusSm, padding: "10px 12px", color: E.text, fontSize: 13, outline: "none", fontFamily: "'Outfit', sans-serif" }}>
                      <option value="low">🏳️ Basse</option>
                      <option value="normal">🚩 Normale</option>
                      <option value="high">🔴 Prioritaire</option>
                    </select>
                  </div>
                </div>
                <Btn onClick={createTask} disabled={creating}>{creating ? <><Spinner size={14} color="white" /> Ajout…</> : "+ Ajouter au parcours client"}</Btn>
              </div>
        )}
      </Card>

      {/* Suivi des actions */}
      <Card style={{ padding: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <SectionLabel>Suivi des actions</SectionLabel>
        </div>

        {/* Filtres client */}
        {uniqueClients.length > 1 && (
          <div style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 10, paddingBottom: 4 }}>
            <button onClick={() => setSelectedClient("all")} style={{ flex: "0 0 auto", padding: "6px 12px", borderRadius: 20, border: "none", cursor: "pointer", background: selectedClient === "all" ? E.navy : E.offWhite, color: selectedClient === "all" ? E.white : E.gray, fontSize: 11, fontWeight: 700, fontFamily: "'Outfit', sans-serif" }}>
              Tous ({tasks.length})
            </button>
            {uniqueClients.map(b => (
              <button key={b.client_id} onClick={() => setSelectedClient(selectedClient === b.client_id ? "all" : b.client_id)} style={{ flex: "0 0 auto", padding: "6px 12px", borderRadius: 20, border: "none", cursor: "pointer", background: selectedClient === b.client_id ? E.teal : E.offWhite, color: selectedClient === b.client_id ? E.white : E.gray, fontSize: 11, fontWeight: 700, fontFamily: "'Outfit', sans-serif" }}>
                👤 {getName(b.client_id)} ({tasks.filter(t => t.client_id === b.client_id).length})
              </button>
            ))}
          </div>
        )}

        {/* Filtres statut */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
          {[
            ["all", "Toutes", tasks.filter(t => selectedClient === "all" || t.client_id === selectedClient).length],
            ["todo", "À faire", tasks.filter(t => t.status === "todo" && (selectedClient === "all" || t.client_id === selectedClient)).length],
            ["in_progress", "En cours", tasks.filter(t => t.status === "in_progress" && (selectedClient === "all" || t.client_id === selectedClient)).length],
            ["coach_done", "⏳ Att. client", tasks.filter(t => isCoachDone(t) && !isFullyDone(t) && (selectedClient === "all" || t.client_id === selectedClient)).length],
            ["done", "✅ Terminées", tasks.filter(t => isFullyDone(t) && (selectedClient === "all" || t.client_id === selectedClient)).length],
          ].map(([k, l, count]) => (
            <button key={k} onClick={() => setStatusFilter(k)} style={{ padding: "5px 10px", borderRadius: 20, border: "none", cursor: "pointer", background: statusFilter === k ? E.teal : E.offWhite, color: statusFilter === k ? E.white : E.gray, fontSize: 11, fontWeight: 700, fontFamily: "'Outfit', sans-serif" }}>
              {l} ({count})
            </button>
          ))}
        </div>

        {filtered.length === 0
          ? <EmptyState icon="✅" title="Aucune action ici" desc="Changez les filtres ou créez une nouvelle action." />
          : <div style={{ display: "grid", gap: 10 }}>
              {filtered.map(t => {
                const coachValidated = isCoachDone(t);
                const clientValidated = t.client_validated === true;
                const fullyDone = isFullyDone(t);
                return (
                  <div key={t.id} style={{ background: fullyDone ? E.tealLight : coachValidated ? E.orangeLight : E.offWhite, borderRadius: 14, padding: "14px 16px", border: `1.5px solid ${fullyDone ? E.teal : coachValidated ? E.orange : E.grayLight}33` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", gap: 6, marginBottom: 4, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: E.gray, background: E.white, borderRadius: 20, padding: "2px 8px" }}>
                            👤 {getName(t.client_id)}
                          </span>
                          <span style={{ fontSize: 11, fontWeight: 700, color: t.priority === "high" ? E.red : t.priority === "normal" ? E.teal : E.gray, background: E.white, borderRadius: 20, padding: "2px 8px" }}>
                            {t.priority === "high" ? "🔴 Prioritaire" : t.priority === "normal" ? "🚩 Normale" : "🏳️ Basse"}
                          </span>
                          {t.due_date && <span style={{ fontSize: 11, color: new Date(t.due_date) < new Date() && !fullyDone ? E.red : E.gray, background: E.white, borderRadius: 20, padding: "2px 8px" }}>📅 Échéance {new Date(t.due_date + "T00:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}</span>}
                          {t.created_at && <span style={{ fontSize: 11, color: E.gray, background: E.white, borderRadius: 20, padding: "2px 8px" }}>🗓 Créée le {new Date(t.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}</span>}
                        </div>
                        <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 14, color: E.navy, marginBottom: t.description ? 4 : 0, textDecoration: fullyDone ? "line-through" : "none", opacity: fullyDone ? 0.6 : 1 }}>{t.title}</div>
                        {t.description && <div style={{ fontSize: 12, color: E.gray, lineHeight: 1.5 }}>{t.description}</div>}

                        {/* Statut double validation */}
                        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, color: coachValidated ? E.teal : E.gray }}>
                            <span style={{ width: 16, height: 16, borderRadius: "50%", background: coachValidated ? E.teal : E.grayLight, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "white" }}>{coachValidated ? "✓" : ""}</span>
                            Coach
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, color: clientValidated ? E.teal : E.gray }}>
                            <span style={{ width: 16, height: 16, borderRadius: "50%", background: clientValidated ? E.teal : E.grayLight, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "white" }}>{clientValidated ? "✓" : ""}</span>
                            Client
                          </div>
                          {clientValidated && !coachValidated && <span style={{ fontSize: 11, color: E.teal, fontWeight: 700 }}>⬅️ Client a validé !</span>}
                          {coachValidated && !clientValidated && <span style={{ fontSize: 11, color: E.orangeDark, fontStyle: "italic" }}>⏳ En attente du client</span>}
                          {fullyDone && <span style={{ fontSize: 11, color: E.teal, fontWeight: 700 }}>✨ Terminée</span>}
                        </div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
                        {!coachValidated && (
                          <button onClick={() => updateStatus(t, "done")} style={{ background: E.teal, color: E.white, border: "none", borderRadius: 8, padding: "7px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}>
                            ✓ Valider
                          </button>
                        )}
                        {coachValidated && !fullyDone && (
                          <button onClick={() => updateStatus(t, "in_progress")} style={{ background: E.offWhite, color: E.gray, border: `1px solid ${E.grayLight}`, borderRadius: 8, padding: "7px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}>
                            ↩ Annuler
                          </button>
                        )}
                        <button onClick={() => deleteTask(t.id)} style={{ background: E.redLight, color: E.red, border: "none", borderRadius: 8, padding: "7px 8px", fontSize: 13, cursor: "pointer" }} aria-label="Supprimer">×</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
        }
      </Card>
    </div>
  );
}

function ClientTasksPanel({ user, tasks, setTasks, coaches }) {
  const [filter, setFilter] = useState("active");

  // Double validation : fullyDone = coach ET client ont validé
  const isCoachDone = (t) => t.status === "done";
  const isClientDone = (t) => t.client_validated === true;
  const isFullyDone = (t) => isCoachDone(t) && isClientDone(t);
  const isWaitingCoach = (t) => isClientDone(t) && !isCoachDone(t);

  const activeTasks = tasks.filter(t => !isFullyDone(t));
  const doneTasks = tasks.filter(t => isFullyDone(t));
  const shown = filter === "active" ? activeTasks : filter === "done" ? doneTasks : tasks;

  const updateTaskStatus = async (task) => {
    // Client marque comme fait → client_validated = true
    if (isClientDone(task)) return; // déjà validé
    const fullyDone = isCoachDone(task); // coach a déjà validé → terminé des deux côtés
    const payload = {
      client_validated: true,
      status: fullyDone ? "done" : task.status,
      completed_at: fullyDone ? new Date().toISOString() : null,
    };
    await sb.q("tasks").eq("id", task.id).update(payload);
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, ...payload } : t));
    const coach = coaches.find(c => c.id === task.coach_id);
    if (coach?.user_id) {
      await createInAppNotification(
        coach.user_id,
        fullyDone ? "task_done" : "task_client_validated",
        fullyDone ? "✅ Action terminée des deux côtés !" : "⏳ Client a validé son côté",
        `${user.name || "Votre client"} a validé : "${task.title}"${fullyDone ? " — Terminée !" : ""}`,
        "coach-dashboard"
      );
    }
    toast(fullyDone ? "🎉 Action terminée par les deux !" : "✓ Votre validation est enregistrée — en attente du coach", "success");
  };

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <TaskProgress tasks={tasks} />

      <Card style={{ padding: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
          <div>
            <SectionLabel>Mes actions à réaliser</SectionLabel>
            <p style={{ color: E.gray, fontSize: 12, margin: "2px 0 0", lineHeight: 1.5 }}>
              Ces actions sont définies par votre coach pour vous aider à progresser entre deux séances.
            </p>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[
              ["active", "À faire", activeTasks.length],
              ["done", "Faites", doneTasks.length],
              ["all", "Tout", tasks.length],
            ].map(([k, l, count]) => (
              <button key={k} onClick={() => setFilter(k)} style={{
                padding: "6px 11px", borderRadius: 20, border: "none", cursor: "pointer",
                background: filter === k ? E.teal : E.offWhite,
                color: filter === k ? E.white : E.gray,
                fontSize: 11, fontWeight: 800, fontFamily: "'Outfit', sans-serif",
              }}>{l} ({count})</button>
            ))}
          </div>
        </div>

        {tasks.length === 0 ? (
          <EmptyState icon="📝" title="Aucune action pour l'instant" desc="Votre coach pourra bientôt vous assigner des actions concrètes à réaliser." />
        ) : shown.length === 0 ? (
          <EmptyState icon="🏆" title="Tout est terminé !" desc="Bravo, vous avez terminé toutes vos actions." />
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {shown.map(t => {
              const clientDone = isClientDone(t);
              const coachDone = isCoachDone(t);
              const fullyDone = isFullyDone(t);
              const waiting = isWaitingCoach(t);
              return (
                <div key={t.id} style={{
                  background: fullyDone ? E.tealLight : waiting ? E.orangeLight : E.offWhite,
                  borderRadius: 14, padding: "14px 16px",
                  border: `1.5px solid ${fullyDone ? E.teal : waiting ? E.orange : E.grayLight}33`,
                  opacity: fullyDone ? 0.85 : 1,
                }}>
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    {/* Bouton validation client */}
                    <button
                      onClick={() => !clientDone && updateTaskStatus(t)}
                      disabled={clientDone}
                      style={{
                        width: 28, height: 28, borderRadius: "50%", flexShrink: 0, marginTop: 2,
                        border: `2px solid ${clientDone ? E.teal : E.grayLight}`,
                        background: clientDone ? E.teal : "white",
                        cursor: clientDone ? "default" : "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 13, color: "white",
                      }}>
                      {clientDone ? "✓" : ""}
                    </button>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: t.priority === "high" ? E.red : t.priority === "normal" ? E.teal : E.gray, background: "white", borderRadius: 20, padding: "2px 8px" }}>
                          {t.priority === "high" ? "🔴 Prioritaire" : t.priority === "normal" ? "🚩 Normale" : "🏳️ Basse"}
                        </span>
                        {t.due_date && (
                          <span style={{ fontSize: 11, color: new Date(t.due_date) < new Date() && !fullyDone ? E.red : E.gray, background: "white", borderRadius: 20, padding: "2px 8px" }}>
                            📅 Échéance {new Date(t.due_date + "T00:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                          </span>
                        )}
                        {t.created_at && (
                          <span style={{ fontSize: 11, color: E.gray, background: "white", borderRadius: 20, padding: "2px 8px" }}>
                            🗓 Donnée le {new Date(t.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                          </span>
                        )}
                      </div>
                      <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 14, color: E.navy, textDecoration: fullyDone ? "line-through" : "none" }}>
                        {t.title}
                      </div>
                      {t.description && <div style={{ fontSize: 12, color: E.gray, marginTop: 4, lineHeight: 1.5 }}>{t.description}</div>}

                      {/* Indicateurs double validation */}
                      <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: clientDone ? E.teal : E.gray }}>
                          <span style={{ width: 16, height: 16, borderRadius: "50%", background: clientDone ? E.teal : E.grayLight, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "white" }}>{clientDone ? "✓" : ""}</span>
                          Moi
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: coachDone ? E.teal : E.gray }}>
                          <span style={{ width: 16, height: 16, borderRadius: "50%", background: coachDone ? E.teal : E.grayLight, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "white" }}>{coachDone ? "✓" : ""}</span>
                          Coach
                        </div>
                        {clientDone && !coachDone && (
                          <span style={{ fontSize: 11, color: E.orangeDark, fontStyle: "italic" }}>⏳ En attente du coach</span>
                        )}
                        {fullyDone && <span style={{ fontSize: 11, color: E.teal, fontWeight: 700 }}>✨ Terminée !</span>}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}


// ─── COACH DASHBOARD ──────────────────────
function BlockSlotForm({ coachId, onDone }) {
  const [form, setForm] = useState({ date: "", start_time: "09:00", end_time: "10:00", reason: "" });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.date) { toast("Sélectionnez une date", "error"); return; }
    if (form.start_time >= form.end_time) { toast("L'heure de fin doit être après l'heure de début", "error"); return; }
    try {
      const result = await sb.q("blocked_slots").insert({ coach_id: coachId, blocked_date: form.date, start_time: form.start_time, end_time: form.end_time, reason: form.reason });
      if (result?.message || result?.error) {
        toast(`Erreur : ${result.message || result.error}`, "error");
        return;
      }
      toast("Créneau bloqué ✓", "success");
      setForm({ date: "", start_time: "09:00", end_time: "10:00", reason: "" });
      onDone();
    } catch(e) {
      toast("Erreur lors du blocage", "error");
    }
  };

  return (
    <div style={{ display: "grid", gap: 10 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        {[
          ["Date", <input key="date" type="date" value={form.date} onChange={e => set("date", e.target.value)} style={{ width: "100%", background: E.offWhite, border: `1.5px solid ${E.grayLight}`, borderRadius: E.radiusSm, padding: "8px 6px", color: E.text, fontSize: 12, outline: "none" }} />],
          ["De", <input key="start" type="time" value={form.start_time} onChange={e => set("start_time", e.target.value)} style={{ width: "100%", background: E.offWhite, border: `1.5px solid ${E.grayLight}`, borderRadius: E.radiusSm, padding: "8px 6px", color: E.text, fontSize: 12, outline: "none" }} />],
          ["À", <input key="end" type="time" value={form.end_time} onChange={e => set("end_time", e.target.value)} style={{ width: "100%", background: E.offWhite, border: `1.5px solid ${E.grayLight}`, borderRadius: E.radiusSm, padding: "8px 6px", color: E.text, fontSize: 12, outline: "none" }} />],
        ].map(([label, el]) => (
          <div key={label}>
            <div style={{ color: E.gray, fontSize: 10, fontWeight: 600, marginBottom: 4, fontFamily: "'Outfit', sans-serif" }}>{label}</div>
            {el}
          </div>
        ))}
      </div>
      <input value={form.reason} onChange={e => set("reason", e.target.value)}
        placeholder="Raison (optionnel) — ex: Réunion, Médecin…"
        style={{ width: "100%", boxSizing: "border-box", background: E.offWhite, border: `1.5px solid ${E.grayLight}`, borderRadius: E.radiusSm, padding: "8px 12px", color: E.text, fontSize: 12, outline: "none", fontFamily: "'Outfit', sans-serif" }}
      />
      <Btn small onClick={submit}>🚫 Bloquer ce créneau</Btn>
    </div>
  );
}

// ─── SYSTÈME DE NIVEAUX COACH ─────────────
const COACH_LEVELS = [
  {
    key: "decouverte",
    label: "Coach Découverte",
    icon: "🌱",
    color: "#6b7a90",
    bg: "#f4f6f9",
    description: "Vous démarrez votre aventure Elivio",
    perks: [
      { icon: "👁️", text: "Profil visible sur la plateforme" },
      { icon: "📊", text: "Accès au tableau de bord" },
      { icon: "📅", text: "Système de réservation en ligne" },
    ],
    criteria: { reviews: 0, responseRate: 0, completeness: 0, clients: 0, actions: 0 },
  },
  {
    key: "certifie",
    label: "Coach Certifié",
    icon: "✅",
    color: "#4bbfb5",
    bg: "#e8f7f6",
    description: "Votre sérieux est reconnu par Elivio",
    perks: [
      { icon: "🏅", text: "Badge Certifié sur votre fiche publique" },
      { icon: "🔍", text: "Apparaît dans les résultats de matching" },
      { icon: "📧", text: "Mentionné dans la newsletter Elivio" },
      { icon: "📈", text: "Statistiques de vues profil débloquées" },
    ],
    criteria: { reviews: 3, responseRate: 70, completeness: 75, clients: 3, actions: 5 },
  },
  {
    key: "expert",
    label: "Coach Expert",
    icon: "⭐",
    color: "#f59e0b",
    bg: "#fef3c7",
    description: "Votre expertise est reconnue par vos clients",
    perks: [
      { icon: "🥇", text: "Badge Expert visible — crédibilité renforcée" },
      { icon: "🚀", text: "Mise en avant dans les résultats de recherche" },
      { icon: "📣", text: "Partagé sur les réseaux sociaux d'Elivio" },
      { icon: "🎯", text: "Accès aux statistiques avancées de conversion" },
      { icon: "💬", text: "Témoignage client mis en avant sur la plateforme" },
    ],
    criteria: { reviews: 10, responseRate: 80, completeness: 90, clients: 10, actions: 20 },
  },
  {
    key: "reference",
    label: "Coach Référence",
    icon: "🏆",
    color: "#8b5cf6",
    bg: "#f3f0ff",
    description: "Vous faites partie de l'élite Elivio",
    perks: [
      { icon: "💎", text: "Badge Référence exclusif sur votre profil" },
      { icon: "📌", text: "Profil épinglé en tête des résultats" },
      { icon: "🎙️", text: "Invité à témoigner sur le blog Elivio" },
      { icon: "🤝", text: "Accès prioritaire aux partenariats Elivio" },
      { icon: "📊", text: "Dashboard analytique complet" },
      { icon: "🔔", text: "Alertes clients personnalisées en avant-première" },
    ],
    criteria: { reviews: 25, responseRate: 90, completeness: 100, clients: 25, actions: 50 },
  },
  {
    key: "ambassadeur",
    label: "Coach Ambassadeur",
    icon: "🎖️",
    color: "#0d1b3e",
    bg: "linear-gradient(135deg, #0d1b3e, #1a2f5e)",
    description: "Le niveau ultime — vous incarnez Elivio",
    perks: [
      { icon: "👑", text: "Badge Ambassadeur — le plus rare de la plateforme" },
      { icon: "🥇", text: "Profil en position #1 dans toutes les recherches" },
      { icon: "📸", text: "Interview vidéo mise en avant sur LinkedIn Elivio" },
      { icon: "✍️", text: "Article dédié sur le blog Elivio" },
      { icon: "🎤", text: "Speaker invité aux événements Elivio" },
      { icon: "🛠️", text: "Co-création des nouvelles fonctionnalités" },
      { icon: "💡", text: "Accès bêta à toutes les nouveautés en avant-première" },
    ],
    criteria: { reviews: 50, responseRate: 95, completeness: 100, clients: 50, actions: 100 },
  },
];

function getCoachLevel({ reviewCount, responseRate, completeness, clientCount, actionsCount }) {
  let current = COACH_LEVELS[0];
  for (const level of COACH_LEVELS) {
    const c = level.criteria;
    if (reviewCount >= c.reviews && responseRate >= c.responseRate && completeness >= c.completeness && clientCount >= c.clients && actionsCount >= c.actions) {
      current = level;
    }
  }
  return current;
}

// ─── COACH LEVEL ROW ──────────────────────
function CoachLevelRow({ level: l, achieved, isCurrent, defaultOpen }) {
  const [expanded, setExpanded] = useState(defaultOpen);

  return (
    <div>
      <div onClick={() => setExpanded(e => !e)} style={{
        display: "flex", alignItems: "center", gap: 12, cursor: "pointer",
        background: achieved ? (l.bg.includes("gradient") ? E.navy : l.bg) : E.offWhite,
        borderRadius: expanded ? "12px 12px 0 0" : 12,
        padding: "12px 14px",
        border: isCurrent ? `2px solid ${l.color}` : "2px solid transparent",
        opacity: achieved ? 1 : 0.55,
        transition: "all 0.2s",
      }}>
        <span style={{ fontSize: 22 }}>{achieved ? l.icon : "🔒"}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 13, color: achieved ? (l.bg.includes("gradient") ? "white" : E.navy) : E.gray }}>{l.label}</div>
          <div style={{ fontSize: 11, color: achieved ? (l.bg.includes("gradient") ? "rgba(255,255,255,0.6)" : E.gray) : E.gray }}>
            {l.criteria.clients === 0 ? "Niveau de départ" : `${l.criteria.clients} clients · ${l.criteria.reviews} avis · ${l.criteria.actions} actions`}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {isCurrent && <span style={{ fontSize: 11, fontWeight: 700, color: l.color, background: `${l.color}15`, borderRadius: 20, padding: "2px 10px" }}>Actuel</span>}
          {achieved && !isCurrent && <span style={{ color: E.teal, fontSize: 14 }}>✓</span>}
          <span style={{ fontSize: 11, color: E.gray, display: "inline-block", transform: expanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>▼</span>
        </div>
      </div>

      {expanded && (
        <div style={{
          background: achieved ? (l.bg.includes("gradient") ? "#1a2f5e" : `${l.bg}dd`) : "#f9fafb",
          borderRadius: "0 0 12px 12px", padding: "14px 16px",
          border: isCurrent ? `2px solid ${l.color}` : `1px solid ${E.grayLight}`,
          borderTop: "none",
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: achieved ? (l.bg.includes("gradient") ? "rgba(255,255,255,0.5)" : E.gray) : E.gray, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>
            {achieved ? "🎁 Récompenses débloquées" : "🔒 Conditions pour débloquer"}
          </div>
          {achieved ? (
            <div style={{ display: "grid", gap: 8 }}>
              {l.perks.map(p => (
                <div key={p.text} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <span style={{ fontSize: 16, flexShrink: 0 }}>{p.icon}</span>
                  <span style={{ fontSize: 13, color: l.bg.includes("gradient") ? "rgba(255,255,255,0.85)" : E.navy, lineHeight: 1.5 }}>{p.text}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ display: "grid", gap: 6 }}>
              {[
                l.criteria.reviews > 0 && `⭐ ${l.criteria.reviews} avis minimum`,
                l.criteria.responseRate > 0 && `📨 ${l.criteria.responseRate}% taux de réponse`,
                l.criteria.completeness > 0 && `👤 Profil complété à ${l.criteria.completeness}%`,
                l.criteria.clients > 0 && `🏆 ${l.criteria.clients} clients accompagnés`,
                l.criteria.actions > 0 && `✅ ${l.criteria.actions} actions réalisées par vos clients`,
              ].filter(Boolean).map(c => (
                <div key={c} style={{ fontSize: 12, color: E.gray, display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ color: E.teal }}>→</span> {c}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CoachLevelCard({ stats }) {
  const s = stats || {};
  const reviewCount = Number(s.reviewCount || 0);
  const responseRate = Number(s.responseRate || 0);
  const completeness = Number(s.completeness || 0);
  const clientCount = Number(s.clientCount || 0);
  const actionsCount = Number(s.actionsCount || 0);
  const profileClicksTotal = Number(s.profileClicksTotal || 0);
  const bookingCount = Number(s.bookingCount || 0);
  const avgRating = s.avgRating || "0";

  let current = COACH_LEVELS[0];
  try {
    current = getCoachLevel({ reviewCount, responseRate, completeness, clientCount, actionsCount }) || COACH_LEVELS[0];
  } catch (e) {
    current = COACH_LEVELS[0];
  }

  const currentIdx = Math.max(0, COACH_LEVELS.findIndex(l => l.key === current.key));
  const next = COACH_LEVELS[currentIdx + 1] || null;
  const isGradient = String(current.bg || "").includes("gradient");

  const missingCriteria = next ? [
    next.criteria.reviews > reviewCount && `${next.criteria.reviews - reviewCount} avis supplémentaires`,
    next.criteria.responseRate > responseRate && `${next.criteria.responseRate}% taux de réponse`,
    next.criteria.completeness > completeness && `Profil à ${next.criteria.completeness}%`,
    next.criteria.clients > clientCount && `${next.criteria.clients - clientCount} clients accompagnés`,
    next.criteria.actions > actionsCount && `${next.criteria.actions - actionsCount} actions clients supplémentaires`,
  ].filter(Boolean) : [];

  const impactRows = [
    ["⭐", "Avis reçus", reviewCount, `Note moyenne ${avgRating}/5`],
    ["👁️", "Vues profil", profileClicksTotal, "clics sur votre fiche"],
    ["📅", "Réservations", bookingCount, "demandes reçues"],
    ["🎯", "Taux de réponse", `${responseRate}%`, "demandes traitées"],
    ["✅", "Actions réalisées", actionsCount, "par vos clients"],
    ["🏆", "Clients accompagnés", clientCount, "séances terminées"],
  ];

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ background: current.bg || E.offWhite, borderRadius: 20, padding: "24px 22px", border: isGradient ? "none" : `2px solid ${(current.color || E.teal)}33`, position: "relative", overflow: "hidden" }}>
        {isGradient && <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", background: "rgba(75,191,181,0.15)" }} />}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, position: "relative" }}>
          <div>
            <div style={{ fontSize: 36, marginBottom: 6 }}>{current.icon}</div>
            <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 22, color: isGradient ? "white" : current.color }}>
              {current.label}
            </div>
            <div style={{ fontSize: 13, color: isGradient ? "rgba(255,255,255,0.65)" : E.gray, marginTop: 4 }}>
              {current.description}
            </div>
          </div>
          <div style={{ background: isGradient ? "rgba(255,255,255,0.15)" : `${current.color}15`, borderRadius: 12, padding: "8px 14px", textAlign: "center" }}>
            <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 22, color: isGradient ? "white" : current.color }}>{currentIdx + 1}</div>
            <div style={{ fontSize: 10, color: isGradient ? "rgba(255,255,255,0.6)" : E.gray }}>/{COACH_LEVELS.length}</div>
          </div>
        </div>

        <div style={{ display: "grid", gap: 8, marginBottom: next && missingCriteria.length > 0 ? 16 : 0 }}>
          {(current.perks || []).map((p, idx) => (
            <div key={`${current.key}-perk-${idx}`} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 13, color: isGradient ? "rgba(255,255,255,0.85)" : E.navy }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>{p.icon}</span>
              <span>{p.text}</span>
            </div>
          ))}
        </div>

        {next && missingCriteria.length > 0 && (
          <div style={{ background: isGradient ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)", borderRadius: 12, padding: "12px 14px" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: isGradient ? "rgba(255,255,255,0.8)" : E.gray, marginBottom: 8 }}>
              Pour devenir {next.icon} {next.label} :
            </div>
            {missingCriteria.map((c, idx) => (
              <div key={`missing-${idx}`} style={{ fontSize: 12, color: isGradient ? "rgba(255,255,255,0.65)" : E.gray, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ color: isGradient ? "#4bbfb5" : current.color }}>→</span> {c}
              </div>
            ))}
          </div>
        )}
      </div>

      <Card style={{ padding: "20px 22px" }}>
        <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 16, color: E.navy, marginBottom: 16 }}>
          📊 Vos points d'impact
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          {impactRows.map(([icon, label, value, sub]) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 12, background: E.offWhite, borderRadius: 12, padding: "12px 14px" }}>
              <span style={{ fontSize: 20, flexShrink: 0 }}>{icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 13, color: E.navy }}>{label}</div>
                <div style={{ fontSize: 12, color: E.gray }}>{sub}</div>
              </div>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 20, color: E.navy }}>{value}</div>
            </div>
          ))}
        </div>
      </Card>

      <Card style={{ padding: "20px 22px" }}>
        <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 15, color: E.navy, marginBottom: 4 }}>
          🗺️ Parcours de progression
        </div>
        <div style={{ color: E.gray, fontSize: 12, marginBottom: 14 }}>
          Cliquez sur un niveau pour voir les récompenses débloquées.
        </div>

        <div style={{ display: "grid", gap: 10 }}>
          {COACH_LEVELS.map((level, index) => {
            const achieved = index <= currentIdx;
            const isCurrent = level.key === current.key;
            const levelBg = achieved ? (level.bg || E.offWhite) : E.offWhite;
            const levelGradient = String(levelBg).includes("gradient");

            return (
              <details key={level.key} open={isCurrent} style={{
                background: levelBg,
                borderRadius: 16,
                border: isCurrent ? `2px solid ${level.color}` : `1px solid ${achieved ? `${level.color}35` : E.grayLight}`,
                opacity: achieved ? 1 : 0.72,
                overflow: "hidden",
              }}>
                <summary style={{
                  listStyle: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "14px 16px",
                  cursor: "pointer",
                  fontFamily: "'Outfit', sans-serif",
                }}>
                  <span style={{ fontSize: 24, flexShrink: 0 }}>{achieved ? level.icon : "🔒"}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: 14, color: levelGradient && achieved ? E.white : achieved ? E.navy : E.gray }}>
                      {level.label}
                      {isCurrent && (
                        <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 900, color: level.color, background: levelGradient ? "rgba(255,255,255,0.9)" : `${level.color}18`, borderRadius: 20, padding: "2px 8px" }}>
                          Actuel
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: levelGradient && achieved ? "rgba(255,255,255,0.68)" : E.gray, marginTop: 3 }}>
                      {level.criteria.clients === 0 ? "Niveau de départ" : `${level.criteria.clients} clients · ${level.criteria.reviews} avis · ${level.criteria.actions} actions`}
                    </div>
                  </div>
                  <span style={{ fontSize: 16, color: levelGradient && achieved ? E.white : level.color }}>⌄</span>
                </summary>

                <div style={{ padding: "0 16px 16px" }}>
                  <div style={{
                    background: levelGradient && achieved ? "rgba(255,255,255,0.10)" : E.white,
                    borderRadius: 14,
                    padding: "14px",
                    border: levelGradient && achieved ? "1px solid rgba(255,255,255,0.12)" : `1px solid ${level.color}22`,
                  }}>
                    <div style={{
                      fontSize: 12,
                      fontWeight: 900,
                      color: levelGradient && achieved ? E.white : level.color,
                      marginBottom: 10,
                      fontFamily: "'Outfit', sans-serif",
                    }}>
                      🎁 Récompenses débloquées
                    </div>

                    <div style={{ display: "grid", gap: 8 }}>
                      {(level.perks || []).map((p, idx) => (
                        <div key={`${level.key}-${idx}`} style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 10,
                          color: levelGradient && achieved ? "rgba(255,255,255,0.86)" : achieved ? E.navy : E.gray,
                          fontSize: 12,
                          lineHeight: 1.45,
                        }}>
                          <span style={{ fontSize: 16, flexShrink: 0 }}>{achieved ? p.icon : "🔒"}</span>
                          <span>{p.text}</span>
                        </div>
                      ))}
                    </div>

                    {!achieved && (
                      <div style={{ marginTop: 12, background: E.offWhite, borderRadius: 12, padding: "10px 12px", color: E.gray, fontSize: 11, lineHeight: 1.5 }}>
                        Ce niveau sera débloqué quand les critères seront atteints.
                      </div>
                    )}
                  </div>
                </div>
              </details>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

// ─── COACH STATS GRID ─────────────────────
function CoachStatsGrid({ coachId, stats }) {
  const { pendingCount, confirmedCount, monthRevenue, occupancyRate,
          doneCount, avgRating, totalRevenue, profileClicksTotal,
          conversionRate, reviewsLength } = stats;

  const ALL_STATS = {
    pending:    { icon: "📅", label: "En attente",       get: () => [pendingCount,       pendingCount > 0 ? E.orangeLight : E.offWhite, pendingCount > 0 ? E.orangeDark : E.navy] },
    confirmed:  { icon: "✅", label: "Confirmés",         get: () => [confirmedCount,      E.offWhite, E.navy] },
    month:      { icon: "💶", label: "Ce mois",           get: () => [`${monthRevenue}€`,  E.tealLight, E.teal] },
    occupancy:  { icon: "📊", label: "Taux occupation",   get: () => [`${occupancyRate}%`, occupancyRate >= 70 ? E.tealLight : E.offWhite, occupancyRate >= 70 ? E.teal : E.navy] },
    done:       { icon: "🏁", label: "Séances réalisées", get: () => [doneCount,           E.offWhite, E.navy] },
    rating:     { icon: "⭐", label: "Note moyenne",      get: () => [avgRating,           E.offWhite, E.navy] },
    revenue:    { icon: "💰", label: "Revenus totaux",    get: () => [`${totalRevenue}€`,  E.tealLight, E.teal] },
    clicks:     { icon: "👁️", label: "Vues profil",      get: () => [profileClicksTotal,  E.offWhite, E.navy] },
    conversion: { icon: "🎯", label: "Conversion",        get: () => [`${conversionRate}%`,E.offWhite, E.navy] },
    reviews:    { icon: "💬", label: "Avis reçus",        get: () => [reviewsLength,       E.offWhite, E.navy] },
  };

  const PREFS_KEY = `elivio_stats_${coachId}`;
  const [selected, setSelected] = useState(() => {
    try { return JSON.parse(localStorage.getItem(PREFS_KEY)) || ["pending","confirmed","month","occupancy"]; }
    catch { return ["pending","confirmed","month","occupancy"]; }
  });
  const [editing, setEditing] = useState(false);

  const toggleStat = (key) => {
    setSelected(prev => {
      let next;
      if (prev.includes(key)) {
        if (prev.length <= 1) return prev;
        next = prev.filter(k => k !== key);
      } else {
        next = prev.length >= 4 ? [...prev.slice(1), key] : [...prev, key];
      }
      localStorage.setItem(PREFS_KEY, JSON.stringify(next));
      return next;
    });
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: E.gray, textTransform: "uppercase", letterSpacing: 1, fontFamily: "'Outfit', sans-serif" }}>Mes statistiques</span>
        <button onClick={() => setEditing(e => !e)} style={{ background: editing ? E.teal : E.offWhite, color: editing ? E.white : E.gray, border: "none", borderRadius: 20, padding: "4px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}>
          {editing ? "✓ Valider" : "✏️ Personnaliser"}
        </button>
      </div>
      {editing && (
        <div style={{ background: E.offWhite, borderRadius: 14, padding: "14px 16px", marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: E.gray, marginBottom: 10 }}>Choisissez jusqu'à 4 statistiques :</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {Object.entries(ALL_STATS).map(([key, s]) => (
              <button key={key} onClick={() => toggleStat(key)} style={{
                padding: "6px 12px", borderRadius: 20, cursor: "pointer", fontSize: 12, fontWeight: 600,
                background: selected.includes(key) ? E.teal : E.white,
                border: `1.5px solid ${selected.includes(key) ? E.teal : E.grayLight}`,
                color: selected.includes(key) ? E.white : E.gray,
                fontFamily: "'Outfit', sans-serif", transition: "all 0.15s",
              }}>{s.icon} {s.label}</button>
            ))}
          </div>
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {selected.map(key => {
          const s = ALL_STATS[key];
          if (!s) return null;
          const [val, bg, color] = s.get();
          return (
            <Card key={key} style={{ padding: "16px 18px", background: bg }}>
              <div style={{ fontSize: 22, marginBottom: 8 }}>{s.icon}</div>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 22, color }}>{val}</div>
              <div style={{ color: E.gray, fontSize: 12, marginTop: 2 }}>{s.label}</div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function CoachDashboard({ user, onTabChange, initialTab }) {
  const [tab, setTabRaw] = useState(initialTab || "home");
  const setTab = (t) => { setTabRaw(t); onTabChange?.(t); };
  const [bookings, setBookings] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [coachData, setCoachData] = useState(null);
  const [profileClicks, setProfileClicks] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [availabilities, setAvailabilities] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editProfile, setEditProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [newSlot, setNewSlot] = useState({ day_of_week: 1, start_time: "09:00", end_time: "17:00" });
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useClickOutside(menuRef, () => setMenuOpen(false));
  const [weekOffset, setWeekOffset] = useState(0);
  const [bookingFilter, setBookingFilter] = useState("all");
  const [selectedBookingDetail, setSelectedBookingDetail] = useState(null);
  const DAYS = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const coaches = await sb.q("coaches").eq("user_id", user.id).limit(1).get();
      const coach = Array.isArray(coaches) && coaches.length > 0 ? coaches[0] : null;
      if (coach) {
        setCoachData(coach);
        setProfileForm(coach);
        const [bks, tks, clicks, rvs, avs, notifs, blk] = await Promise.all([
          sb.q("bookings").eq("coach_id", coach.id).order("scheduled_at", false).get(),
          sb.q("tasks").eq("coach_id", coach.id).order("created_at", false).get().catch(() => []),
          sb.q("coach_profile_clicks").eq("coach_id", coach.id).order("created_at", false).get().catch(() => []),
          sb.q("reviews").eq("coach_id", coach.id).order("created_at", false).get(),
          sb.q("availabilities").eq("coach_id", coach.id).order("day_of_week").get(),
          sb.q("notifications").eq("user_id", user.id).order("created_at", false).limit(10).get(),
          sb.q("blocked_slots").eq("coach_id", coach.id).order("blocked_date").get(),
        ]);
        setBookings(Array.isArray(bks) ? bks : []);
        setTasks(Array.isArray(tks) ? tks : []);
        setProfileClicks(Array.isArray(clicks) ? clicks : []);
        setReviews(Array.isArray(rvs) ? rvs : []);
        setAvailabilities(Array.isArray(avs) ? avs : []);
        setNotifications(Array.isArray(notifs) ? notifs : []);
        setBlockedSlots(Array.isArray(blk) ? blk : []);
      }
    } catch(e) {
      console.error("Erreur chargement coach dashboard", e);
    }
    setLoading(false);
  }, [user.id]);

  useEffect(() => { load(); }, [load]);

  const markNotifRead = async (id) => {
    await sb.q("notifications").eq("id", id).update({ read: true });
    setNotifications(ns => ns.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const updateBooking = async (id, status) => {
    // Met à jour le state local immédiatement pour feedback instantané
    setBookings(prev => prev.map(b => b.id === id ? { ...b, status } : b));
    await sb.q("bookings").eq("id", id).update({ status });
    try {
      const booking = bookings.find(b => b.id === id);
      if (booking) {
        const dt = new Date(booking.scheduled_at);
        const dateStr = dt.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
        const timeStr = dt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
        const clientProfile = await sb.q("profiles").eq("id", booking.client_id).single().get();
        const clientEmail = clientProfile?.email || "";
        if (status === "confirmed" && clientEmail) {
          await sendNotification("rdv_confirme", clientEmail, { coach_name: coachData.name, date: dateStr, time: timeStr, rate: coachData.rate });
          await createInAppNotification(booking.client_id, "rdv_confirme", "RDV confirmé ✅", `${coachData.name} a confirmé votre séance du ${dateStr} à ${timeStr}.`, "client-dashboard");
        } else if (status === "cancelled" && clientEmail) {
          await sendNotification("rdv_annule_client", clientEmail, { date: dateStr, time: timeStr });
          await createInAppNotification(booking.client_id, "rdv_annule", "RDV annulé ❌", `La séance du ${dateStr} à ${timeStr} a été annulée.`, "client-dashboard");
        } else if (status === "done" && clientEmail) {
          await sendNotification("demande_avis", clientEmail, { coach_name: coachData.name, date: dateStr, booking_id: booking.id });
          await createInAppNotification(booking.client_id, "demande_avis", "Laissez un avis ⭐", `Comment s'est passée votre séance avec ${coachData.name} ?`, "client-dashboard");
        }
      }
    } catch(e) { console.error("Notif booking erreur", e); }
    load();
  };

  const saveProfile = async () => {
    setSaving(true);
    await sb.q("coaches").eq("id", coachData.id).update({
      name: profileForm.name, bio: profileForm.bio, rate: profileForm.rate,
      types: profileForm.types, specialties: profileForm.specialties,
      availability_label: profileForm.availability_label,
      certification: profileForm.certification, linkedin_url: profileForm.linkedin_url,
    });
    setSaving(false); setEditProfile(false); load();
  };

  const addSlot = async () => {
    if (!coachData?.id) return;
    await sb.q("availabilities").insert({ ...newSlot, coach_id: coachData.id });
    load();
  };

  const removeSlot = async (id) => {
    if (!window.confirm("Supprimer cette disponibilité ?")) return;
    await sb.q("availabilities").eq("id", id).del();
    load();
  };

  const statusColor = { pending: E.orange, confirmed: E.teal, cancelled: E.red, done: E.navy };
  const statusLabel = { pending: "En attente", confirmed: "Confirmé", cancelled: "Annulé", done: "Terminé" };

  // ── Tous les useMemo AVANT les returns conditionnels ──
  const pendingCount = useMemo(() => bookings.filter(b => b.status === "pending").length, [bookings]);
  const confirmedCount = useMemo(() => bookings.filter(b => b.status === "confirmed").length, [bookings]);
  const doneCount = useMemo(() => bookings.filter(b => b.status === "done").length, [bookings]);
  const unreadCount = useMemo(() => notifications.filter(n => !n.read).length, [notifications]);
  const avgRating = useMemo(() => reviews.length > 0 ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : "—", [reviews]);
  const monthRevenue = useMemo(() => {
    const now = new Date();
    return bookings.filter(b => {
      const dt = new Date(b.scheduled_at);
      return b.status === "done" && dt.getMonth() === now.getMonth() && dt.getFullYear() === now.getFullYear();
    }).length * (coachData?.rate || 0);
  }, [bookings, coachData?.rate]);
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Bonjour" : hour < 18 ? "Bon après-midi" : "Bonsoir";
  const occupancyRate = useMemo(() => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay() + 1);
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 7);
    const slotsThisWeek = availabilities.reduce((acc, av) => acc + (parseInt(av.end_time?.slice(0,2)||"17") - parseInt(av.start_time?.slice(0,2)||"9")), 0);
    const bookedThisWeek = bookings.filter(b => {
      const dt = new Date(b.scheduled_at);
      return dt >= startOfWeek && dt < endOfWeek && ["confirmed","done"].includes(b.status);
    }).length;
    return slotsThisWeek > 0 ? Math.round((bookedThisWeek / slotsThisWeek) * 100) : 0;
  }, [bookings, availabilities]);
  const criteria = useMemo(() => [
    { label: "Photo", done: coachData?.photo_status === "approved" },
    { label: "Bio", done: (coachData?.bio?.length || 0) > 80 },
    { label: "Types", done: (coachData?.types?.length || 0) >= 1 },
    { label: "Spécialités", done: (coachData?.specialties?.length || 0) >= 2 },
    { label: "Tarif", done: (coachData?.rate || 0) > 0 },
    { label: "Disponibilités", done: availabilities.length > 0 },
    { label: "Certification", done: !!coachData?.certification },
    { label: "LinkedIn", done: !!coachData?.linkedin_url },
  ], [coachData, availabilities]);
  const completenessScore = useMemo(() => Math.round((criteria.filter(c => c.done).length / criteria.length) * 100), [criteria]);
  const completenessLevel = useMemo(() => completenessScore === 100 ? { label: "Elite ✦", color: "#f59e0b" }
    : completenessScore >= 75 ? { label: "Avancé", color: E.teal }
    : completenessScore >= 50 ? { label: "Intermédiaire", color: E.blue }
    : { label: "Débutant", color: E.gray }, [completenessScore]);
  const upcomingBookings = useMemo(() => bookings
    .filter(b => ["pending", "confirmed"].includes(b.status) && new Date(b.scheduled_at) > new Date())
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
    .slice(0, 3), [bookings]);
  const nowStats = new Date();
  const startOfMonthStats = new Date(nowStats.getFullYear(), nowStats.getMonth(), 1);
  const profileClicksMonth = useMemo(() => profileClicks.filter(c => new Date(c.created_at) >= startOfMonthStats).length, [profileClicks]);
  const profileClicksTotal = profileClicks.length;
  const profileClickersUnique = useMemo(() => new Set(profileClicks.map(c => c.viewer_id).filter(Boolean)).size, [profileClicks]);
  const conversionRate = useMemo(() => profileClicksTotal > 0 ? Math.round((bookings.length / profileClicksTotal) * 100) : 0, [bookings, profileClicksTotal]);
  const totalRevenue = useMemo(() => bookings.filter(b => b.status === "done").length * (coachData?.rate || 0), [bookings, coachData?.rate]);
  const doneTasks = tasks.filter(t => t.status === "done").length;
  const actionsCount = doneTasks;
  const activeTasks = tasks.filter(t => t.status !== "done").length;
  const responseRate = bookings.length > 0 ? Math.round(((bookings.filter(b => b.status !== "pending").length) / bookings.length) * 100) : 0;
  const clickSparkline = Array.from({ length: 6 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (5 - i) * 7);
    const start = new Date(d); start.setDate(d.getDate() - d.getDay() + 1); start.setHours(0,0,0,0);
    const end = new Date(start); end.setDate(start.getDate() + 7);
    return profileClicks.filter(c => {
      const cd = new Date(c.created_at);
      return cd >= start && cd < end;
    }).length;
  });

  if (loading) return <LoadingPage />;
  if (!coachData) return <div style={{ padding: 40, color: E.gray, textAlign: "center" }}>Profil coach introuvable.</div>;

  if (coachData.status === "pending" || coachData.status === "rejected") {
    return (
      <div style={{ padding: "60px 0", textAlign: "center", animation: "fadeUp 0.4s ease" }}>
        <div style={{ width: 72, height: 72, borderRadius: "50%", background: coachData.status === "pending" ? E.orangeLight : E.redLight, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 32 }}>
          {coachData.status === "pending" ? "⏳" : "❌"}
        </div>
        <h2 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 22, color: E.navy, marginBottom: 10 }}>
          {coachData.status === "pending" ? "Profil en cours de validation" : "Profil non validé"}
        </h2>
        <p style={{ color: E.gray, fontSize: 14, maxWidth: 380, margin: "0 auto" }}>
          {coachData.status === "pending"
            ? "Notre équipe examine votre candidature. Vous recevrez une réponse sous 48h."
            : `Raison : ${coachData.rejection_reason || "Non précisée"}.`}
        </p>
      </div>
    );
  }

  const MENU_ITEMS = [
    ["home", "🏠", "Accueil"],
    ["bookings", "📅", "Réservations", pendingCount],
    ["tasks", "✅", "Actions", tasks.filter(t => t.status !== "done").length],
    ["niveau", "🏆", "Mon niveau"],
    ["stats", "📊", "Statistiques"],
    ["agenda", "🗓", "Agenda"],
    ["profile", "👤", "Mon profil"],
  ];
  const currentMenu = MENU_ITEMS.find(m => m[0] === tab);

  return (
    <div style={{ padding: "24px 0 40px", animation: "fadeUp 0.4s ease" }}>

      {/* Header avec dropdown */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 20, color: E.navy }}>
            {currentMenu?.[1]} {currentMenu?.[2]}
          </div>
          <div style={{ color: E.gray, fontSize: 13 }}>{greeting} {coachData.name?.split(" ")[0]} 👋</div>
          {/* Badge niveau */}
          {(() => {
            const lvl = getCoachLevel({ reviewCount: reviews.length, responseRate, completeness: completenessScore, clientCount: doneCount, actionsCount: tasks.filter(t => t.status === 'done').length });
            return (
              <button onClick={() => setTab("niveau")} style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 5, background: lvl.bg.includes("gradient") ? E.navy : lvl.bg, border: `1px solid ${lvl.color}44`, borderRadius: 20, padding: "3px 10px", cursor: "pointer" }}>
                <span style={{ fontSize: 13 }}>{lvl.icon}</span>
                <span style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 11, color: lvl.bg.includes("gradient") ? "white" : lvl.color }}>{lvl.label}</span>
              </button>
            );
          })()}
        </div>

        <div ref={menuRef} style={{ position: "relative" }}>
          <button onClick={() => setMenuOpen(o => !o)} aria-label="Menu navigation" style={{
            display: "flex", alignItems: "center", gap: 8,
            background: NAVY, color: E.white, border: "none",
            borderRadius: 12, padding: "10px 16px", cursor: "pointer",
            fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 13,
            boxShadow: E.shadowMd, position: "relative",
          }}>
            ☰ Menu
            {(pendingCount + unreadCount) > 0 && (
              <span style={{ position: "absolute", top: -6, right: -6, background: E.red, color: E.white, fontSize: 10, fontWeight: 800, borderRadius: "50%", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {pendingCount + unreadCount}
              </span>
            )}
          </button>

          {menuOpen && (
            <div style={{
              position: "absolute", right: 0, top: "calc(100% + 8px)",
              background: E.white, borderRadius: 16, padding: 8,
              boxShadow: E.shadowMd,
              zIndex: E.zDropdown, minWidth: 200, animation: "fadeUp 0.15s ease",
            }}>
              {MENU_ITEMS.map(([k, icon, label, badge]) => (
                <button key={k} onClick={() => { setTab(k); setMenuOpen(false); }} style={{
                  width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                  gap: 10, padding: "12px 14px",
                  background: tab === k ? E.tealLight : "transparent",
                  border: "none", borderRadius: 10, cursor: "pointer",
                  fontFamily: "'Outfit', sans-serif", fontWeight: tab === k ? 700 : 500,
                  fontSize: 14, color: tab === k ? E.teal : E.navy,
                }}>
                  <span>{icon} {label}</span>
                  {badge > 0 && (
                    <span style={{ background: E.red, color: E.white, fontSize: 10, fontWeight: 800, borderRadius: 20, padding: "2px 8px" }}>{badge}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── ACCUEIL ── */}
      {tab === "home" && (
        <div style={{ animation: "fadeTab 0.2s ease",  display: "grid", gap: 16 }}>

          {/* Alerte disponibilités manquantes */}
          {availabilities.length === 0 && (
            <div style={{ background: E.orangeLight, border: "1.5px solid #f59e0b", borderRadius: 14, padding: "14px 18px", display: "flex", gap: 12, alignItems: "flex-start" }}>
              <span style={{ fontSize: 22, flexShrink: 0 }}>⚠️</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 14, color: E.orangeDark, marginBottom: 4 }}>
                  Vous n'avez pas renseigné vos disponibilités
                </div>
                <p style={{ color: E.orangeDark, fontSize: 13, lineHeight: 1.6, margin: "0 0 10px" }}>
                  Les clients ne peuvent pas réserver tant que vous n'avez pas ajouté vos créneaux.
                </p>
                <button onClick={() => setTab("agenda")} style={{ background: E.orange, color: E.white, border: "none", borderRadius: 8, padding: "7px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}>
                  Ajouter mes disponibilités →
                </button>
              </div>
            </div>
          )}

          {/* Carte complétude profil */}
          <Card style={{ padding: "18px 20px", border: completenessScore === 100 ? `1.5px solid ${E.orange}44` : `1px solid ${E.grayLight}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 14, color: E.navy }}>
                Profil <span style={{ fontSize: 11, fontWeight: 700, background: `${completenessLevel.color}18`, color: completenessLevel.color, borderRadius: 20, padding: "2px 10px", marginLeft: 6 }}>{completenessLevel.label}</span>
              </div>
              <span style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 20, color: completenessLevel.color }}>{completenessScore}%</span>
            </div>
            <div style={{ height: 8, background: E.grayLight, borderRadius: 4, marginBottom: 12, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${completenessScore}%`, background: `linear-gradient(90deg, ${E.teal}, ${completenessLevel.color})`, borderRadius: 4, transition: "width 0.5s ease" }} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, marginBottom: completenessScore < 100 ? 12 : 0 }}>
              {criteria.map(c => (
                <div key={c.label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 13, color: c.done ? E.teal : E.grayLight }}>{c.done ? "✓" : "○"}</span>
                  <span style={{ fontSize: 12, color: c.done ? E.text : E.gray, fontFamily: "'Outfit', sans-serif" }}>{c.label}</span>
                </div>
              ))}
            </div>
            {completenessScore < 100 && (
              <button onClick={() => setTab("profile")} style={{ background: TEAL, color: E.white, border: "none", borderRadius: 8, padding: "10px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit', sans-serif", width: "100%" }}>
                Compléter mon profil →
              </button>
            )}
            {completenessScore === 100 && (
              <div style={{ textAlign: "center", color: E.teal, fontSize: 13, fontWeight: 700 }}>✨ Profil complet !</div>
            )}
          </Card>

          {/* Stats clés personnalisables */}
          <CoachStatsGrid coachId={coachData.id} stats={{
            pendingCount, confirmedCount, monthRevenue, occupancyRate,
            doneCount, avgRating, totalRevenue, profileClicksTotal,
            conversionRate, reviewsLength: reviews.length
          }} />

          {/* Prochains RDV */}
          <Card style={{ padding: "18px 20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 14, color: E.navy }}>📅 Prochains RDV</div>
              <button onClick={() => setTab("bookings")} style={{ background: "transparent", border: "none", color: E.teal, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}>Voir tout →</button>
            </div>
            {upcomingBookings.length === 0
              ? <div style={{ color: E.gray, fontSize: 13, textAlign: "center", padding: "16px 0" }}>Aucun RDV à venir.</div>
              : <div style={{ display: "grid", gap: 8 }}>
                  {upcomingBookings.map(b => {
                    const dt = new Date(b.scheduled_at);
                    return (
                      <div key={b.id} style={{ display: "flex", alignItems: "center", gap: 12, background: E.offWhite, borderRadius: 10, padding: "10px 14px" }}>
                        <div style={{ textAlign: "center", minWidth: 40 }}>
                          <div style={{ fontSize: 11, color: E.gray, fontFamily: "'Outfit', sans-serif" }}>{dt.toLocaleDateString("fr-FR", { weekday: "short" })}</div>
                          <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 18, color: E.navy, lineHeight: 1 }}>{dt.getDate()}</div>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: 13, color: E.navy }}>{dt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>
                          <ClientName clientId={b.client_id} />
                          <span style={{ fontSize: 11, fontWeight: 700, color: statusColor[b.status], background: `${statusColor[b.status]}18`, borderRadius: 20, padding: "2px 8px" }}>{statusLabel[b.status]}</span>
                        </div>
                        {b.status === "pending" && (
                          <div style={{ display: "flex", gap: 8 }}>
                            <button onClick={() => updateBooking(b.id, "confirmed")} style={{ background: TEAL, color: E.white, border: "none", borderRadius: 8, padding: "5px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>✓</button>
                            <button onClick={() => updateBooking(b.id, "cancelled")} style={{ background: E.redLight, color: E.red, border: "none", borderRadius: 8, padding: "5px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>✕</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
            }
          </Card>

          {/* Notifications in-app */}
          {notifications.length > 0 && (
            <Card style={{ padding: "18px 20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 14, color: E.navy }}>
                  🔔 Notifications {unreadCount > 0 && <span style={{ background: E.red, color: E.white, fontSize: 10, fontWeight: 800, borderRadius: 20, padding: "2px 8px", marginLeft: 6 }}>{unreadCount}</span>}
                </div>
              </div>
              <div style={{ display: "grid", gap: 8 }}>
                {notifications.map(n => (
                  <div key={n.id} onClick={() => markNotifRead(n.id)} style={{
                    display: "flex", alignItems: "flex-start", gap: 10,
                    background: n.read ? E.offWhite : E.tealLight,
                    borderRadius: 10, padding: "10px 14px", cursor: "pointer",
                    transition: "background 0.2s",
                  }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: n.read ? "transparent" : E.teal, flexShrink: 0, marginTop: 5 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 13, color: E.navy }}>{n.title}</div>
                      <div style={{ fontSize: 12, color: E.gray, marginTop: 2 }}>{n.message}</div>
                      <div style={{ fontSize: 11, color: E.gray, marginTop: 4 }}>{new Date(n.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Derniers avis */}
          {reviews.length > 0 && (
            <Card style={{ padding: "18px 20px" }}>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 14, color: E.navy, marginBottom: 14 }}>⭐ Derniers avis</div>
              <div style={{ display: "grid", gap: 8 }}>
                {reviews.slice(0, 2).map(r => (
                  <div key={r.id} style={{ background: E.offWhite, borderRadius: 10, padding: "10px 14px" }}>
                    <Stars rating={r.rating} />
                    {r.comment && <p style={{ color: E.textSub, fontSize: 13, margin: "6px 0 0", lineHeight: 1.5 }}>{r.comment}</p>}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}


      {/* ── ACTIONS CLIENTS ── */}
      {tab === "tasks" && (
        <CoachTasksPanel
          coachData={coachData}
          bookings={bookings}
          tasks={tasks}
          setTasks={setTasks}
          onRefresh={load}
        />
      )}

      {/* ── RÉSERVATIONS ── */}
      {tab === "bookings" && (() => {
        const orderedBookings = [...bookings].sort((a, b) => {
          const priority = { pending: 0, confirmed: 1, done: 2, cancelled: 3 };
          const pa = priority[a.status] ?? 9;
          const pb = priority[b.status] ?? 9;
          if (pa !== pb) return pa - pb;
          return new Date(a.scheduled_at) - new Date(b.scheduled_at);
        });

        const filtered = bookingFilter === "all"
          ? orderedBookings
          : orderedBookings.filter(b => b.status === bookingFilter);

        const upcoming = orderedBookings.filter(b => ["pending", "confirmed"].includes(b.status) && new Date(b.scheduled_at) >= new Date());
        const history = filtered.filter(b => ["done", "cancelled"].includes(b.status) || new Date(b.scheduled_at) < new Date());
        const activeFiltered = filtered.filter(b => !["done", "cancelled"].includes(b.status) && new Date(b.scheduled_at) >= new Date());

        const groupLabel = (b) => {
          const d = new Date(b.scheduled_at);
          const today = new Date(); today.setHours(0,0,0,0);
          const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
          const week = new Date(today); week.setDate(today.getDate() + 7);
          const bd = new Date(d); bd.setHours(0,0,0,0);
          if (bd.getTime() === today.getTime()) return "Aujourd’hui";
          if (bd.getTime() === tomorrow.getTime()) return "Demain";
          if (bd < week && bd > today) return "Cette semaine";
          return "Historique";
        };

        const renderGroup = (title, items) => {
          if (!items.length) return null;
          return (
            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "8px 0 2px" }}>
                <span style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 13, color: E.navy }}>{title}</span>
                <span style={{ fontSize: 11, color: E.gray, background: E.offWhite, borderRadius: 20, padding: "2px 8px" }}>{items.length}</span>
              </div>
              {items.map(b => (
                <CoachBookingCard
                  key={b.id}
                  b={b}
                  statusColor={statusColor}
                  statusLabel={statusLabel}
                  updateBooking={updateBooking}
                  onAddAction={() => setTab("tasks")}
                />
              ))}
            </div>
          );
        };

        const todayItems = activeFiltered.filter(b => groupLabel(b) === "Aujourd’hui");
        const tomorrowItems = activeFiltered.filter(b => groupLabel(b) === "Demain");
        const weekItems = activeFiltered.filter(b => groupLabel(b) === "Cette semaine");
        const otherActiveItems = activeFiltered.filter(b => !["Aujourd’hui", "Demain", "Cette semaine"].includes(groupLabel(b)));
        const historyItems = filtered.filter(b => !activeFiltered.includes(b));

        return (
          <div style={{ animation: "fadeTab 0.2s ease", display: "grid", gap: 12 }}>
            {/* Résumé utile */}
            <Card style={{ padding: "16px 18px", background: pendingCount > 0 ? E.orangeLight : E.tealLight, border: `1.5px solid ${pendingCount > 0 ? E.orange : E.teal}33` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: 14, background: E.white, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>
                  {pendingCount > 0 ? "⚡" : "✅"}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 15, color: E.navy }}>
                    {pendingCount > 0 ? `${pendingCount} demande${pendingCount > 1 ? "s" : ""} à traiter` : "Tout est à jour"}
                  </div>
                  <div style={{ color: E.gray, fontSize: 12, marginTop: 2 }}>
                    {upcoming.length} RDV à venir · {doneCount} séance{doneCount > 1 ? "s" : ""} terminée{doneCount > 1 ? "s" : ""}
                  </div>
                </div>
              </div>
            </Card>

            {/* Filtres compacts */}
            <div style={{ display: "flex", gap: 8, overflowX: "auto", WebkitOverflowScrolling: "touch", paddingBottom: 4 }}>
              {[
                ["all", "Tous", bookings.length],
                ["pending", "⚡ À traiter", pendingCount],
                ["confirmed", "✅ Confirmés", confirmedCount],
                ["done", "📋 Terminés", doneCount],
                ["cancelled", "❌ Annulés", bookings.filter(b => b.status === "cancelled").length],
              ].map(([key, label, count]) => (
                <button key={key} onClick={() => setBookingFilter(key)} style={{
                  flex: "0 0 auto",
                  padding: "8px 13px",
                  borderRadius: 22,
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 900,
                  background: bookingFilter === key ? E.navy : E.white,
                  border: `1.5px solid ${bookingFilter === key ? E.navy : E.grayLight}`,
                  color: bookingFilter === key ? E.white : E.gray,
                  fontFamily: "'Outfit', sans-serif",
                  boxShadow: bookingFilter === key ? "none" : E.shadow,
                }}>
                  {label} <span style={{ opacity: 0.72 }}>({count})</span>
                </button>
              ))}
            </div>

            {filtered.length === 0 ? (
              <EmptyState icon="📅" title="Aucune réservation" desc="Aucun RDV dans cette catégorie." />
            ) : (
              <div style={{ display: "grid", gap: 12 }}>
                {renderGroup("Aujourd’hui", todayItems)}
                {renderGroup("Demain", tomorrowItems)}
                {renderGroup("Cette semaine", weekItems)}
                {renderGroup("À venir", otherActiveItems)}
                {renderGroup("Historique", historyItems)}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── AGENDA ── */}
      {tab === "agenda" && (() => {
        const HOURS = Array.from({ length: 14 }, (_, i) => i + 7);
        const today = new Date();
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay() + 1 + (weekOffset * 7));
        const weekDays = Array.from({ length: 7 }, (_, i) => {
          const d = new Date(startOfWeek);
          d.setDate(startOfWeek.getDate() + i);
          return d;
        });
        const isToday = (d) => d.toDateString() === today.toDateString();
        const getBookingsForDayHour = (date, hour) => bookings.filter(b => {
          const dt = new Date(b.scheduled_at);
          return dt.toDateString() === date.toDateString() && dt.getHours() === hour;
        });
        const getAvailForDay = (dayIndex) => availabilities.some(a => a.day_of_week === (dayIndex === 6 ? 0 : dayIndex + 1));
        const getBlockedForDayHour = (date, hour) => blockedSlots.filter(b => {
          if (!b.blocked_date) return false;
          const bd = new Date(b.blocked_date + "T00:00:00");
          const startH = parseInt(b.start_time?.slice(0,2) || "0");
          const endH = parseInt(b.end_time?.slice(0,2) || "0");
          return bd.toDateString() === date.toDateString() && startH <= hour && endH > hour;
        });

        return (
          <div style={{ display: "grid", gap: 16 }}>
            <Card style={{ overflow: "hidden" }}>
              {/* Navigation semaine */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px 0" }}>
                <button onClick={() => setWeekOffset(w => w - 1)} style={{ background: E.offWhite, border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", color: E.navy, fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>←</button>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 14, color: E.navy }}>
                    {weekOffset === 0 ? "Cette semaine" : weekOffset === 1 ? "Semaine prochaine" : weekOffset === -1 ? "Semaine dernière" : `Semaine du ${startOfWeek.toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}`}
                  </div>
                  <div style={{ fontSize: 11, color: E.gray, fontFamily: "'Outfit', sans-serif" }}>
                    {startOfWeek.toLocaleDateString("fr-FR", { day: "numeric", month: "long" })} — {weekDays[6].toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}
                  </div>
                </div>
                <button onClick={() => setWeekOffset(w => w + 1)} style={{ background: E.offWhite, border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", color: E.navy, fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>→</button>
              </div>
              {weekOffset !== 0 && (
                <div style={{ textAlign: "center", padding: "6px 0" }}>
                  <button onClick={() => setWeekOffset(0)} style={{ background: "transparent", border: "none", color: E.teal, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}>Aujourd'hui</button>
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "36px repeat(7, 1fr)", borderBottom: `1px solid ${E.grayLight}`, marginTop: 10 }}>
                <div />
                {weekDays.map((d, i) => (
                  <div key={i} style={{ padding: "8px 4px", textAlign: "center", borderLeft: `1px solid ${E.grayLight}`, background: isToday(d) ? E.tealLight : "transparent" }}>
                    <div style={{ fontSize: 10, color: isToday(d) ? E.teal : E.gray, fontFamily: "'Outfit', sans-serif", fontWeight: 700 }}>{["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"][i]}</div>
                    <div style={{ fontSize: 14, fontWeight: 800, fontFamily: "'Outfit', sans-serif", width: 26, height: 26, borderRadius: "50%", background: isToday(d) ? E.teal : "transparent", color: isToday(d) ? E.white : E.navy, display: "flex", alignItems: "center", justifyContent: "center", margin: "2px auto 0" }}>{d.getDate()}</div>
                    {getAvailForDay(i) && <div style={{ width: 5, height: 5, borderRadius: "50%", background: TEAL, margin: "3px auto 0", boxShadow: `0 0 4px ${TEAL}` }} />}
                  </div>
                ))}
              </div>
              <div style={{ maxHeight: 340, overflowY: "auto" }}>
                {HOURS.map(hour => (
                  <div key={hour} style={{ display: "grid", gridTemplateColumns: "36px repeat(7, 1fr)", minHeight: 48, borderBottom: `1px solid ${E.grayLight}` }}>
                    <div style={{ padding: "4px", fontSize: 10, color: E.gray, fontWeight: 600, background: E.offWhite, display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: 6 }}>{hour}h</div>
                    {weekDays.map((d, i) => {
                      const rdvs = getBookingsForDayHour(d, hour);
                      const avail = getAvailForDay(i);
                      const blocked = getBlockedForDayHour(d, hour);
                      const isBlocked = blocked.length > 0;
                      return (
                        <div key={i} style={{ borderLeft: `1px solid ${E.grayLight}`, background: isBlocked ? "#f1f5f9" : avail && rdvs.length === 0 ? `${TEAL}0a` : E.white, padding: 2, position: "relative" }}>
                          {/* Barre disponibilité */}
                          {avail && rdvs.length === 0 && !isBlocked && (
                            <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: `${TEAL}55`, borderRadius: "0 2px 2px 0" }} />
                          )}
                          {/* Créneau bloqué */}
                          {isBlocked && (
                            <div style={{ position: "absolute", inset: 0, background: "repeating-linear-gradient(45deg, #e2e8f0, #e2e8f0 2px, #f1f5f9 2px, #f1f5f9 8px)", opacity: 0.8, borderRadius: 2 }} />
                          )}
                          {rdvs.map(b => (
                            <div key={b.id}
                              onClick={() => setSelectedBookingDetail(selectedBookingDetail?.id === b.id ? null : b)}
                              style={{
                                background: statusColor[b.status] || TEAL,
                                borderRadius: 4, padding: "3px 4px",
                                fontSize: 8, fontWeight: 700, color: E.white,
                                lineHeight: 1.3, cursor: "pointer",
                                boxShadow: `0 2px 6px ${statusColor[b.status]}44`,
                                position: "relative", zIndex: 1,
                              }}>
                              {new Date(b.scheduled_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>

              {/* Légende */}
              <div style={{ display: "flex", gap: 12, padding: "10px 12px", borderTop: `1px solid ${E.grayLight}`, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <div style={{ width: 3, height: 14, background: `${TEAL}55`, borderRadius: 2 }} />
                  <span style={{ fontSize: 11, color: E.gray, fontFamily: "'Outfit', sans-serif" }}>Disponible</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <div style={{ width: 14, height: 10, background: "repeating-linear-gradient(45deg, #e2e8f0, #e2e8f0 2px, #f1f5f9 2px, #f1f5f9 6px)", borderRadius: 2 }} />
                  <span style={{ fontSize: 11, color: E.gray, fontFamily: "'Outfit', sans-serif" }}>Bloqué</span>
                </div>
                {Object.entries(statusColor).map(([s, c]) => (
                  <div key={s} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 2, background: c }} />
                    <span style={{ fontSize: 11, color: E.gray, fontFamily: "'Outfit', sans-serif" }}>{statusLabel[s]}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Détail RDV cliqué */}
            {selectedBookingDetail && (
              <Card style={{ padding: "16px 18px", border: `2px solid ${statusColor[selectedBookingDetail.status]}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 14, color: E.navy, marginBottom: 4 }}>
                      📅 {new Date(selectedBookingDetail.scheduled_at).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} à {new Date(selectedBookingDetail.scheduled_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                    </div>
                    <ClientName clientId={selectedBookingDetail.client_id} />
                    <span style={{ fontSize: 11, fontWeight: 700, color: statusColor[selectedBookingDetail.status], background: `${statusColor[selectedBookingDetail.status]}15`, borderRadius: 20, padding: "3px 10px" }}>
                      {statusLabel[selectedBookingDetail.status]}
                    </span>
                    {selectedBookingDetail.notes && <p style={{ fontSize: 12, color: E.gray, marginTop: 8, fontStyle: "italic" }}>💬 {selectedBookingDetail.notes}</p>}
                  </div>
                  <button onClick={() => setSelectedBookingDetail(null)} style={{ background: E.offWhite, border: "none", borderRadius: "50%", width: 40, height: 40, cursor: "pointer", color: E.gray, fontSize: 14 }}>✕</button>
                </div>
                {selectedBookingDetail.status === "pending" && (
                  <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <Btn small onClick={() => { updateBooking(selectedBookingDetail.id, "confirmed"); setSelectedBookingDetail(null); }}>✓ Confirmer</Btn>
                    <Btn small variant="danger" onClick={() => { updateBooking(selectedBookingDetail.id, "cancelled"); setSelectedBookingDetail(null); }}>✕ Refuser</Btn>
                  </div>
                )}
                {selectedBookingDetail.status === "confirmed" && (
                  <Btn small variant="ghost" style={{ marginTop: 12 }} onClick={() => { updateBooking(selectedBookingDetail.id, "done"); setSelectedBookingDetail(null); }}>Marquer terminé</Btn>
                )}
              </Card>
            )}

            <Card style={{ padding: "18px 20px" }}>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 14, color: E.navy, marginBottom: 14 }}>🕐 Mes disponibilités</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
                {[
                  ["Jour", <select key="jour" value={newSlot.day_of_week} onChange={e => setNewSlot(s => ({ ...s, day_of_week: parseInt(e.target.value) }))} style={{ width: "100%", background: E.offWhite, border: `1.5px solid ${E.grayLight}`, borderRadius: E.radiusSm, padding: "8px 6px", color: E.text, fontSize: 12, outline: "none", fontFamily: "'Outfit', sans-serif" }}>
                    {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                  </select>],
                  ["Début", <input key="debut" type="time" value={newSlot.start_time} onChange={e => setNewSlot(s => ({ ...s, start_time: e.target.value }))} style={{ width: "100%", background: E.offWhite, border: `1.5px solid ${E.grayLight}`, borderRadius: E.radiusSm, padding: "8px 6px", color: E.text, fontSize: 12, outline: "none" }} />],
                  ["Fin", <input key="fin" type="time" value={newSlot.end_time} onChange={e => setNewSlot(s => ({ ...s, end_time: e.target.value }))} style={{ width: "100%", background: E.offWhite, border: `1.5px solid ${E.grayLight}`, borderRadius: E.radiusSm, padding: "8px 6px", color: E.text, fontSize: 12, outline: "none" }} />],
                ].map(([label, el]) => (
                  <div key={label}>
                    <div style={{ color: E.gray, fontSize: 10, fontWeight: 600, marginBottom: 4, fontFamily: "'Outfit', sans-serif" }}>{label}</div>
                    {el}
                  </div>
                ))}
              </div>
              <Btn small onClick={addSlot}>+ Ajouter</Btn>
              {availabilities.length > 0 && (
                <div style={{ display: "grid", gap: 8, marginTop: 14 }}>
                  {availabilities.map(a => (
                    <div key={a.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: E.offWhite, borderRadius: 10, padding: "10px 12px" }}>
                      <span style={{ color: E.navy, fontSize: 13, fontFamily: "'Outfit', sans-serif", fontWeight: 700 }}>{DAYS[a.day_of_week]} · {a.start_time.slice(0, 5)} → {a.end_time.slice(0, 5)}</span>
                      <button onClick={() => removeSlot(a.id)} style={{ background: E.redLight, border: "none", borderRadius: 8, padding: "8px 12px", minWidth: 36, minHeight: 36, color: E.red, fontSize: 14, cursor: "pointer", fontWeight: 700 }} aria-label="Supprimer">×</button>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card style={{ padding: "18px 20px" }}>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 14, color: E.navy, marginBottom: 14 }}>🚫 Bloquer un créneau</div>
              <p style={{ color: E.gray, fontSize: 13, marginBottom: 14, lineHeight: 1.6 }}>
                Bloquez une plage horaire ponctuelle sans modifier vos disponibilités récurrentes.
              </p>
              <BlockSlotForm coachId={coachData.id} onDone={load} />
              {blockedSlots.filter(b => new Date(b.blocked_date) >= new Date()).length > 0 && (
                <div style={{ marginTop: 16, borderTop: `1px solid ${E.grayLight}`, paddingTop: 14 }}>
                  <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, fontWeight: 700, color: E.gray, marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>Créneaux bloqués à venir</div>
                  <div style={{ display: "grid", gap: 8 }}>
                    {blockedSlots.filter(b => new Date(b.blocked_date) >= new Date()).map(b => (
                      <div key={b.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#f1f5f9", borderRadius: 10, padding: "10px 12px" }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: E.navy, fontFamily: "'Outfit', sans-serif" }}>
                            {new Date(b.blocked_date + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })} · {b.start_time?.slice(0,5)} → {b.end_time?.slice(0,5)}
                          </div>
                          {b.reason && <div style={{ fontSize: 11, color: E.gray, marginTop: 2 }}>{b.reason}</div>}
                        </div>
                        <button onClick={async () => {
                          if (!window.confirm("Débloquer ce créneau ?")) return;
                          await sb.q("blocked_slots").eq("id", b.id).del();
                          load();
                          toast("Créneau débloqué", "info");
                        }}
                          style={{ background: E.redLight, border: "none", borderRadius: 8, padding: "8px 12px", minWidth: 36, minHeight: 36, color: E.red, fontSize: 14, cursor: "pointer", fontWeight: 700 }} aria-label="Supprimer">×</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </div>
        );
      })()}

      {/* ── MON PROFIL ── */}
      {tab === "profile" && (
        <div style={{ animation: "fadeTab 0.2s ease",  display: "grid", gap: 16 }}>
          <Card style={{ padding: "20px 24px" }}>
            <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
              <div style={{ position: "relative", flexShrink: 0 }}>
                <Avatar initials={coachData.avatar} color={coachData.color || E.teal} size={68}
                  photoUrl={coachData.photo_status === "approved" ? coachData.photo_url : null} />
                <label style={{ position: "absolute", bottom: 0, right: 0, width: 22, height: 22, borderRadius: "50%", background: TEAL, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: E.white }}>
                  📷
                  <input type="file" accept="image/*" style={{ display: "none" }} onChange={async e => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 2 * 1024 * 1024) { toast("Photo trop lourde (max 2MB)", "error"); return; }
                    const ext = file.name.split(".").pop();
                    const path = `${user.id}/avatar.${ext}`;
                    const formData = new FormData();
                    formData.append("", file);
                    const res = await fetch(`${SUPABASE_URL}/storage/v1/object/avatars/${path}`, {
                      method: "POST",
                      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${sb.token}` },
                      body: formData,
                    });
                    if (res.ok) {
                      const photoUrl = `${SUPABASE_URL}/storage/v1/object/public/avatars/${path}`;
                      await sb.q("coaches").eq("id", coachData.id).update({ photo_url: photoUrl, photo_status: "pending" });
                      try {
                        const adminProfile = await sb.q("profiles").eq("role", "admin").single().get();
                        if (adminProfile?.email) await sendNotification("nouvelle_photo", adminProfile.email, { coach_name: coachData.name });
                      } catch(e) {}
                      load();
                      toast("Photo envoyée — validation en cours 📷", "success");
                    }
                  }} />
                </label>
              </div>

              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 18, color: E.navy }}>{coachData.name}</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
                  {(coachData.types || []).map(t => <Tag key={t} label={t} color={coachData.color || E.teal} />)}
                </div>
                <div style={{ color: E.gray, fontSize: 13, marginTop: 5 }}>{coachData.rate}€/séance · {doneCount} séances · {reviews.length} avis</div>
                {coachData.photo_url && coachData.photo_status !== "approved" && (
                  <span style={{ fontSize: 11, fontWeight: 600, background: coachData.photo_status === "pending" ? E.orangeLight : E.redLight, color: coachData.photo_status === "pending" ? E.orangeDark : "#dc2626", borderRadius: 20, padding: "2px 10px", marginTop: 8, display: "inline-block" }}>
                    {coachData.photo_status === "pending" ? "📷 Photo en attente" : "📷 Photo refusée"}
                  </span>
                )}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Btn small variant={editProfile ? "danger" : "ghost"} onClick={() => setEditProfile(!editProfile)}>
                  {editProfile ? "✕" : "✏️"}
                </Btn>
                <Btn small onClick={() => {
                  const url = window.location.href;
                  if (navigator.share) navigator.share({ title: `Coach ${coachData.name} — Elivio`, url: `https://elivio.fr/coach/${(coachData.name||"").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"").replace(/\s+/g,"-").replace(/[^a-z0-9-]/g,"")}` });
                  else { navigator.clipboard?.writeText(`https://elivio.fr/coach/${(coachData.name||"").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"").replace(/\s+/g,"-").replace(/[^a-z0-9-]/g,"")}`); toast("Lien copié ! 🔗", "success"); }
                }}>🔗</Btn>
              </div>
            </div>

            {/* Formulaire qui se déroule dans la carte */}
            {editProfile && (
              <div style={{ marginTop: 20, paddingTop: 20, borderTop: `1px solid ${E.grayLight}`, animation: "fadeUp 0.3s ease" }}>
                <div style={{ display: "grid", gap: 12 }}>
                  <Input label="Nom" value={profileForm.name || ""} onChange={v => setProfileForm(f => ({ ...f, name: v }))} />
                  <Input label="Bio" value={profileForm.bio || ""} onChange={v => setProfileForm(f => ({ ...f, bio: v }))} rows={3} />
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <Input label="Tarif (€)" type="number" value={profileForm.rate || 0} onChange={v => setProfileForm(f => ({ ...f, rate: parseInt(v) || 0 }))} />
                    <Input label="Disponibilité" value={profileForm.availability_label || ""} onChange={v => setProfileForm(f => ({ ...f, availability_label: v }))} />
                  </div>
                  <Input label="Certification" value={profileForm.certification || ""} onChange={v => setProfileForm(f => ({ ...f, certification: v }))} placeholder="ICF, RNCP…" />
                  <Input label="LinkedIn" value={profileForm.linkedin_url || ""} onChange={v => setProfileForm(f => ({ ...f, linkedin_url: v }))} placeholder="https://linkedin.com/in/…" />
                  <div>
                    <div style={{ color: E.gray, fontSize: 12, fontWeight: 600, marginBottom: 8, fontFamily: "'Outfit', sans-serif" }}>Types de coaching</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {COACH_TYPES.map(t => (
                        <button key={t} onClick={() => setProfileForm(f => ({ ...f, types: (f.types||[]).includes(t) ? (f.types||[]).filter(x=>x!==t) : [...(f.types||[]),t] }))} style={{ padding: "6px 12px", borderRadius: 20, cursor: "pointer", fontSize: 12, fontWeight: 600, background: (profileForm.types||[]).includes(t) ? E.teal : E.offWhite, border: `1.5px solid ${(profileForm.types||[]).includes(t) ? E.teal : E.grayLight}`, color: (profileForm.types||[]).includes(t) ? E.white : E.gray, fontFamily: "'Outfit', sans-serif" }}>{t}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: E.gray, fontSize: 12, fontWeight: 600, marginBottom: 8, fontFamily: "'Outfit', sans-serif" }}>Spécialités</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {ALL_SPECIALTIES.map(s => (
                        <button key={s} onClick={() => setProfileForm(f => ({ ...f, specialties: (f.specialties||[]).includes(s) ? (f.specialties||[]).filter(x=>x!==s) : [...(f.specialties||[]),s] }))} style={{ padding: "4px 10px", borderRadius: 20, cursor: "pointer", fontSize: 11, background: (profileForm.specialties||[]).includes(s) ? E.tealLight : E.offWhite, border: `1px solid ${(profileForm.specialties||[]).includes(s) ? E.teal : E.grayLight}`, color: (profileForm.specialties||[]).includes(s) ? E.teal : E.gray, fontFamily: "'Outfit', sans-serif" }}>{s}</button>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Btn variant="ghost" onClick={() => setEditProfile(false)}>Annuler</Btn>
                    <Btn onClick={saveProfile} disabled={saving}>{saving ? <><Spinner size={14} color="white" /> Sauvegarde…</> : "Sauvegarder ✓"}</Btn>
                  </div>
                </div>
              </div>
            )}
          </Card>

          {reviews.length > 0 && (
            <Card style={{ padding: "18px 20px" }}>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 14, color: E.navy, marginBottom: 14 }}>⭐ Mes avis ({reviews.length})</div>
              <div style={{ display: "flex", alignItems: "center", gap: 16, background: E.offWhite, borderRadius: 12, padding: "14px 16px", marginBottom: 12 }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 36, color: E.navy, lineHeight: 1 }}>{avgRating}</div>
                  <Stars rating={parseFloat(avgRating) || 0} />
                </div>
                <div>
                  <div style={{ color: E.navy, fontWeight: 700, fontSize: 14, fontFamily: "'Outfit', sans-serif" }}>{reviews.length} avis</div>
                  <div style={{ color: E.gray, fontSize: 12 }}>{doneCount} séances réalisées</div>
                </div>
              </div>
              <div style={{ display: "grid", gap: 8 }}>
                {reviews.slice(0, 5).map(r => (
                  <div key={r.id} style={{ background: E.offWhite, borderRadius: 10, padding: "12px 14px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <Stars rating={r.rating} />
                      <span style={{ color: E.gray, fontSize: 11 }}>{new Date(r.created_at).toLocaleDateString("fr-FR")}</span>
                    </div>
                    {r.comment && <p style={{ color: E.textSub, fontSize: 13, margin: 0, lineHeight: 1.5 }}>{r.comment}</p>}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* ── MON NIVEAU ── */}
      {tab === "niveau" && (() => {
        const coachStats = {
          reviewCount: reviews.length,
          avgRating: avgRating !== "—" ? avgRating : "0",
          responseRate,
          completeness: completenessScore,
          clientCount: doneCount,
          bookingCount: bookings.length,
          actionsCount: tasks.filter(t => t.status === "done").length,
          profileClicksTotal,
          doneCount,
          activeTasks: tasks.filter(t => t.status !== "done").length,
        };
        return (
          <div style={{ animation: "fadeTab 0.2s ease" }}>
            <CoachLevelCard stats={coachStats} />
          </div>
        );
      })()}

      {/* ── STATISTIQUES ── */}
      {tab === "stats" && (
        <div style={{ display: "grid", gap: 16, animation: "fadeTab 0.2s ease" }}>
          <Card style={{ padding: "20px 22px" }}>
            <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 16, color: E.navy, marginBottom: 16 }}>📊 Vos points d'impact</div>
            <div style={{ display: "grid", gap: 10 }}>
              {[
                ["⭐", "Avis reçus", reviews.length, `Note moyenne : ${avgRating}/5`],
                ["👁️", "Vues profil", profileClicksTotal, "clics sur votre fiche publique"],
                ["📅", "Réservations totales", bookings.length, "demandes reçues"],
                ["✅", "Séances réalisées", doneCount, "séances terminées"],
                ["🎯", "Taux de conversion", `${conversionRate}%`, "vues → réservation"],
                ["📨", "Taux de réponse", `${responseRate}%`, "demandes traitées"],
                ["💶", "Revenus totaux", `${totalRevenue}€`, "générés sur Elivio"],
                ["💶", "Revenus ce mois", `${monthRevenue}€`, "mois en cours"],
              ].map(([icon, label, value, sub]) => (
                <div key={label} style={{ display: "flex", alignItems: "center", gap: 12, background: E.offWhite, borderRadius: 12, padding: "12px 14px" }}>
                  <span style={{ fontSize: 20, flexShrink: 0 }}>{icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 13, color: E.navy }}>{label}</div>
                    <div style={{ fontSize: 12, color: E.gray }}>{sub}</div>
                  </div>
                  <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 20, color: E.navy }}>{value}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

    </div>
  );
}

// ─── CLIENT NAME ──────────────────────────
function ClientName({ clientId }) {
  const [name, setName] = useState(null);
  useEffect(() => {
    if (!clientId) return;
    sb.q("profiles").eq("id", clientId).single().get().then(p => {
      if (p && !p.error) setName(p.full_name || "Client");
    });
  }, [clientId]);
  return name ? <div style={{ fontSize: 12, color: E.gray, fontFamily: "'Outfit', sans-serif", marginBottom: 2 }}>👤 {name}</div> : null;
}

function CoachBookingCard({ b, statusColor, statusLabel, updateBooking, onAddAction }) {
  const dt = new Date(b.scheduled_at);
  const [showDetail, setShowDetail] = useState(false);
  const [clientProfile, setClientProfile] = useState(null);

  const loadClient = useCallback(async () => {
    const p = await sb.q("profiles").eq("id", b.client_id).single().get();
    if (p && !p.error) setClientProfile(p);
  }, [b.client_id]);

  useEffect(() => { loadClient(); }, [loadClient]);

  const toggleDetail = async () => {
    if (!clientProfile) await loadClient();
    setShowDetail(s => !s);
  };

  const clientName = clientProfile?.full_name || clientProfile?.email || "Client";
  const initials = clientName.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) || "CL";
  const isUpcoming = ["pending", "confirmed"].includes(b.status) && dt > new Date();

  const statusMeta = {
    pending: { icon: "⚡", label: "Demande à traiter", bg: E.orangeLight, color: E.orangeDark },
    confirmed: { icon: "✅", label: "Séance prévue", bg: E.tealLight, color: E.teal },
    done: { icon: "📋", label: "Séance terminée", bg: E.offWhite, color: E.navy },
    cancelled: { icon: "❌", label: "Annulé", bg: "#fee2e2", color: E.red },
  }[b.status] || { icon: "📅", label: statusLabel[b.status] || b.status, bg: E.offWhite, color: E.gray };

  return (
    <Card style={{ padding: "16px 18px", marginBottom: 10, border: b.status === "pending" ? `1.5px solid ${E.orange}45` : `1px solid ${E.grayLight}` }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
        <div style={{ textAlign: "center", minWidth: 58, background: isUpcoming ? E.tealLight : E.offWhite, borderRadius: 14, padding: "10px 8px" }}>
          <div style={{ fontSize: 10, color: E.gray, fontFamily: "'Outfit', sans-serif", textTransform: "capitalize" }}>
            {dt.toLocaleDateString("fr-FR", { weekday: "short" })}
          </div>
          <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 24, color: E.navy, lineHeight: 1 }}>
            {dt.getDate()}
          </div>
          <div style={{ fontSize: 11, color: E.gray, marginTop: 2 }}>
            {dt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <div style={{ width: 34, height: 34, borderRadius: "50%", background: E.teal, color: E.white, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, fontFamily: "'Outfit', sans-serif", flexShrink: 0 }}>
              {initials}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 15, color: E.navy, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {clientName}
              </div>
              <div style={{ color: E.gray, fontSize: 12 }}>
                {dt.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })} · {dt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: b.notes ? 8 : 10 }}>
            <span style={{ fontSize: 11, fontWeight: 900, color: statusMeta.color, background: statusMeta.bg, borderRadius: 20, padding: "4px 10px", fontFamily: "'Outfit', sans-serif" }}>
              {statusMeta.icon} {statusMeta.label}
            </span>
            {b.status === "done" && (
              <span style={{ fontSize: 11, color: E.teal, background: E.tealLight, borderRadius: 20, padding: "4px 10px", fontWeight: 800 }}>
                Suivi possible
              </span>
            )}
          </div>

          {b.notes && (
            <div style={{ color: E.gray, fontSize: 12, lineHeight: 1.45, background: E.offWhite, borderRadius: 10, padding: "8px 10px", marginBottom: 10 }}>
              💬 {b.notes}
            </div>
          )}

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {b.status === "pending" && <>
              <Btn small onClick={() => updateBooking(b.id, "confirmed")}>✓ Confirmer</Btn>
              <Btn small variant="danger" onClick={() => {
                if (window.confirm("Refuser cette demande de RDV ?")) updateBooking(b.id, "cancelled");
              }}>✕ Refuser</Btn>
            </>}
            {b.status === "confirmed" && <>
              <Btn small variant="ghost" onClick={() => {
                const dt = new Date(b.scheduled_at);
                const dateStr = dt.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
                const timeStr = dt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
                if (window.confirm(`Confirmer la fin de la séance du ${dateStr} à ${timeStr} ?\n\nLe client recevra une demande d'avis.`)) {
                  updateBooking(b.id, "done");
                }
              }}>✅ Marquer terminée</Btn>
              <Btn small variant="danger" onClick={() => {
                if (window.confirm("Annuler cette séance confirmée ?\n\nLe client sera notifié.")) updateBooking(b.id, "cancelled");
              }}>✕ Annuler</Btn>
            </>}
            {b.status === "done" && <>
              <Btn small variant="ghost" onClick={onAddAction}>+ Action</Btn>
              <Btn small variant="ghost" onClick={() => {
                if (window.confirm("Remettre cette séance en \"Confirmée\" ?\n\n(Utilisez cette option si vous avez cliqué par erreur.)")) updateBooking(b.id, "confirmed");
              }} style={{ color: E.gray, fontSize: 11 }}>↩ Annuler</Btn>
            </>}
            {b.status === "cancelled" && (
              <Btn small variant="ghost" onClick={() => {
                if (window.confirm("Remettre cette réservation en attente ?")) updateBooking(b.id, "pending");
              }} style={{ color: E.gray, fontSize: 11 }}>↩ Rétablir</Btn>
            )}
            <button onClick={toggleDetail} style={{ background: E.offWhite, border: `1px solid ${E.grayLight}`, borderRadius: 10, padding: "7px 11px", fontSize: 12, fontWeight: 800, cursor: "pointer", color: E.navy, fontFamily: "'Outfit', sans-serif" }}>
              {showDetail ? "▲ Masquer" : "👤 Détails"}
            </button>
          </div>
        </div>
      </div>

      {showDetail && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${E.grayLight}`, animation: "fadeUp 0.2s ease" }}>
          {clientProfile ? (
            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                <div style={{ width: 38, height: 38, borderRadius: "50%", background: E.teal, color: E.white, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 13 }}>
                  {initials}
                </div>
                <div>
                  <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 14, color: E.navy }}>{clientName}</div>
                  <div style={{ fontSize: 12, color: E.gray }}>{clientProfile.email}</div>
                </div>
              </div>
              {[
                ["🎯", "Objectif", clientProfile.objectif_pro],
                ["📍", "Situation", clientProfile.situation_actuelle],
                ["⏱", "Délai souhaité", clientProfile.delai_souhaite],
              ].filter(([,, v]) => v).map(([icon, label, val]) => (
                <div key={label} style={{ display: "flex", gap: 8, background: E.offWhite, borderRadius: 10, padding: "9px 12px" }}>
                  <span style={{ fontSize: 14, flexShrink: 0 }}>{icon}</span>
                  <div>
                    <div style={{ fontSize: 10, color: E.gray, fontFamily: "'Outfit', sans-serif" }}>{label}</div>
                    <div style={{ fontSize: 13, color: E.navy, fontFamily: "'Outfit', sans-serif", fontWeight: 700 }}>{val}</div>
                  </div>
                </div>
              ))}
              {!clientProfile.objectif_pro && !clientProfile.situation_actuelle && (
                <div style={{ color: E.gray, fontSize: 12, fontStyle: "italic", textAlign: "center", padding: "8px 0" }}>
                  Ce client n'a pas encore complété son profil.
                </div>
              )}
            </div>
          ) : (
            <div style={{ color: E.teal, fontSize: 13 }}>Chargement…</div>
          )}
        </div>
      )}
    </Card>
  );
}

function ClientOnboarding({ user, onDone }) {
  const [step, setStep] = useState(0);

  const steps = [
    {
      icon: "👋",
      title: `Bienvenue sur Elivio, ${user.name?.split(" ")[0] || ""}!`,
      subtitle: "Votre parcours vers une transition professionnelle réussie commence ici.",
      desc: "Elivio vous met en relation avec les meilleurs coaches selon votre profil et vos objectifs.",
      cta: "Commencer →",
    },
    {
      icon: "🎯",
      title: "Comment ça marche ?",
      subtitle: "3 étapes simples pour trouver votre coach idéal.",
      steps: [
        ["🧭", "Répondez à 4 questions", "Notre algorithme analyse vos besoins"],
        ["✨", "Découvrez vos matches", "Les coaches les plus compatibles avec vous"],
        ["📅", "Réservez une séance", "Directement depuis l'appli"],
      ],
      cta: "Je comprends →",
    },
    {
      icon: "🚀",
      title: "Prêt à trouver votre coach ?",
      subtitle: "Le questionnaire prend moins de 2 minutes.",
      desc: "Vos réponses nous permettent de vous proposer les coaches les plus adaptés à votre situation.",
      cta: "Démarrer le matching →",
    },
  ];

  const current = steps[step];

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(13,27,62,0.6)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 400, padding: 20 }}>
      <div style={{ background: E.white, borderRadius: 24, padding: "36px 28px", maxWidth: 420, width: "100%", boxShadow: E.shadowMd, textAlign: "center" }}>

        {/* Progress dots */}
        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 28 }}>
          {steps.map((_, i) => (
            <div key={i} style={{ width: i === step ? 20 : 6, height: 6, borderRadius: 3, background: i === step ? E.teal : E.grayLight, transition: "all 0.3s" }} />
          ))}
        </div>

        {/* Icon */}
        <div style={{ width: 72, height: 72, background: E.tealLight, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 32 }}>
          {current.icon}
        </div>

        <h2 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 22, color: E.navy, marginBottom: 8, lineHeight: 1.3 }}>
          {current.title}
        </h2>
        <p style={{ color: E.gray, fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
          {current.subtitle}
        </p>

        {/* Étapes (step 1) */}
        {current.steps && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24, textAlign: "left" }}>
            {current.steps.map(([icon, title, desc]) => (
              <div key={title} style={{ display: "flex", alignItems: "flex-start", gap: 12, background: E.offWhite, borderRadius: 12, padding: "12px 14px" }}>
                <span style={{ fontSize: 20, flexShrink: 0 }}>{icon}</span>
                <div>
                  <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 13, color: E.navy, marginBottom: 2 }}>{title}</div>
                  <div style={{ fontSize: 12, color: E.gray }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Description (step 0 et 2) */}
        {current.desc && (
          <div style={{ background: E.tealLight, borderRadius: 12, padding: "12px 16px", marginBottom: 24, textAlign: "left" }}>
            <p style={{ color: E.navy, fontSize: 13, lineHeight: 1.6, margin: 0 }}>{current.desc}</p>
          </div>
        )}

        <Btn full onClick={() => {
          if (step < steps.length - 1) setStep(s => s + 1);
          else onDone();
        }}>
          {current.cta}
        </Btn>

        {step > 0 && (
          <button onClick={() => setStep(s => s - 1)} style={{ background: "transparent", border: "none", color: E.gray, fontSize: 13, cursor: "pointer", marginTop: 12, fontFamily: "'Outfit', sans-serif" }}>
            ← Retour
          </button>
        )}

        <button onClick={onDone} style={{ display: "block", background: "transparent", border: "none", color: E.gray, fontSize: 12, cursor: "pointer", margin: "10px auto 0", fontFamily: "'Outfit', sans-serif" }}>
          Passer
        </button>
      </div>
    </div>
  );
}

// ─── CLIENT DASHBOARD ─────────────────────
// ─── LOYALTY & REFERRAL HELPERS ───────────
const LOYALTY_LEVELS = [
  { key: "bronze", label: "Bronze", icon: "🥉", min: 0,  max: 4,  color: "#cd7f32", bg: "#fdf3e8", discount: 0,   perks: ["Accès à tous les coaches", "Historique des séances"] },
  { key: "silver", label: "Argent", icon: "🥈", min: 5,  max: 9,  color: "#9e9e9e", bg: "#f5f5f5", discount: 5,   perks: ["5% de réduction sur chaque séance", "Priorité sur les créneaux populaires"] },
  { key: "gold",   label: "Gold",   icon: "🥇", min: 10, max: 999, color: "#f59e0b", bg: "#fef3c7", discount: 10,  perks: ["10% de réduction sur chaque séance", "Accès prioritaire aux nouveaux coaches", "Badge Gold visible par les coaches"] },
];

function getLoyaltyLevel(doneCount) {
  return LOYALTY_LEVELS.find(l => doneCount >= l.min && doneCount <= l.max) || LOYALTY_LEVELS[0];
}

function generateReferralCode(userId) {
  return "ELV" + userId.slice(0, 6).toUpperCase();
}

function LoyaltyCard({ doneCount }) {
  const current = getLoyaltyLevel(doneCount);
  const next = LOYALTY_LEVELS[LOYALTY_LEVELS.indexOf(current) + 1];
  const progress = next ? Math.round(((doneCount - current.min) / (next.min - current.min)) * 100) : 100;

  return (
    <Card style={{ padding: "20px 22px", border: `2px solid ${current.color}33`, background: current.bg }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div>
          <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 18, color: E.navy }}>
            {current.icon} Niveau {current.label}
          </div>
          <div style={{ color: E.gray, fontSize: 13, marginTop: 2 }}>{doneCount} séance{doneCount > 1 ? "s" : ""} réalisée{doneCount > 1 ? "s" : ""}</div>
        </div>
        {current.discount > 0 && (
          <div style={{ background: current.color, color: "white", borderRadius: 20, padding: "4px 12px", fontSize: 13, fontWeight: 800, fontFamily: "'Outfit', sans-serif" }}>
            -{current.discount}%
          </div>
        )}
      </div>

      {/* Barre de progression vers le niveau suivant */}
      {next && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: E.gray }}>Progression vers {next.icon} {next.label}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: current.color }}>{doneCount}/{next.min} séances</span>
          </div>
          <div style={{ height: 8, background: "rgba(0,0,0,0.08)", borderRadius: 4, overflow: "hidden" }}>
            <div style={{ width: `${progress}%`, height: "100%", background: current.color, borderRadius: 4, transition: "width 0.6s ease" }} />
          </div>
          <div style={{ fontSize: 11, color: E.gray, marginTop: 4 }}>
            Encore {next.min - doneCount} séance{next.min - doneCount > 1 ? "s" : ""} pour atteindre {next.label}
          </div>
        </div>
      )}
      {!next && (
        <div style={{ background: `${current.color}20`, borderRadius: 10, padding: "8px 12px", marginBottom: 14 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: current.color }}>🏆 Niveau maximum atteint !</span>
        </div>
      )}

      {/* Avantages */}
      <div style={{ display: "grid", gap: 6 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: E.gray, textTransform: "uppercase", letterSpacing: 1, marginBottom: 2 }}>Vos avantages</div>
        {current.perks.map(p => (
          <div key={p} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: E.navy }}>
            <span style={{ color: current.color, fontWeight: 800 }}>✓</span> {p}
          </div>
        ))}
      </div>
    </Card>
  );
}

function ReferralCard({ user, referrals, onShare }) {
  const code = generateReferralCode(user.id);
  const referralUrl = `https://elivio.fr?ref=${code}`;
  const completed = referrals.filter(r => r.status === "completed").length;
  const pending = referrals.filter(r => r.status === "pending").length;

  return (
    <Card style={{ padding: "20px 22px" }}>
      <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 16, color: E.navy, marginBottom: 4 }}>
        🎁 Parrainez vos amis
      </div>
      <p style={{ color: E.gray, fontSize: 13, lineHeight: 1.6, marginBottom: 16 }}>
        Invitez un ami sur Elivio. Quand il réserve sa première séance, <strong>vous recevez chacun une réduction</strong>.
      </p>

      {/* Lien de parrainage */}
      <div style={{ background: E.offWhite, borderRadius: 12, padding: "12px 14px", marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ flex: 1, overflow: "hidden" }}>
          <div style={{ fontSize: 11, color: E.gray, marginBottom: 2 }}>Votre lien unique</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: E.navy, fontFamily: "'Outfit', sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{referralUrl}</div>
        </div>
        <button onClick={onShare} style={{ background: E.teal, color: "white", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit', sans-serif", flexShrink: 0 }}>
          Partager
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div style={{ background: E.tealLight, borderRadius: 10, padding: "12px 14px", textAlign: "center" }}>
          <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 24, color: E.teal }}>{completed}</div>
          <div style={{ fontSize: 12, color: E.gray }}>Filleuls actifs</div>
        </div>
        <div style={{ background: E.offWhite, borderRadius: 10, padding: "12px 14px", textAlign: "center" }}>
          <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 24, color: E.navy }}>{pending}</div>
          <div style={{ fontSize: 12, color: E.gray }}>En attente</div>
        </div>
      </div>

      {completed > 0 && (
        <div style={{ marginTop: 12, background: E.tealLight, borderRadius: 10, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 18 }}>🎉</span>
          <span style={{ fontSize: 13, color: E.teal, fontWeight: 700 }}>
            {completed} ami{completed > 1 ? "s" : ""} parrainé{completed > 1 ? "s" : ""} — réduction appliquée sur votre prochain RDV !
          </span>
        </div>
      )}
    </Card>
  );
}

function ClientDashboard({ user, coaches, onNavigate, onTabChange, pendingReviewBookingId, initialTab }) {
  const [tab, setTabRaw] = useState(initialTab || "home");
  const setTab = (t) => { setTabRaw(t); onTabChange?.(t); };
  const [bookings, setBookings] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [myReviews, setMyReviews] = useState([]);
  const [profile, setProfile] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null);
  const [editProfile, setEditProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useClickOutside(menuRef, () => setMenuOpen(false));

  const load = useCallback(async () => {
    setLoading(true);
    const [bks, tks, rvs, prof, refs] = await Promise.all([
      sb.q("bookings").eq("client_id", user.id).order("scheduled_at", false).get(),
      sb.q("tasks").eq("client_id", user.id).order("created_at", false).get(),
      sb.q("reviews").eq("client_id", user.id).get(),
      sb.q("profiles").eq("id", user.id).single().get(),
      sb.q("referrals").eq("referrer_id", user.id).get().catch(() => []),
    ]);
    setBookings(Array.isArray(bks) ? bks : []);
    setTasks(Array.isArray(tks) ? tks : []);
    setMyReviews(Array.isArray(rvs) ? rvs : []);
    if (prof && !prof.error) { setProfile(prof); setProfileForm(prof); }
    setReferrals(Array.isArray(refs) ? refs : []);
    setLoading(false);
  }, [user.id]);

  useEffect(() => { load(); }, [load]);

  // Ouvre automatiquement la modal d'avis si lien email
  useEffect(() => {
    if (pendingReviewBookingId && bookings.length > 0 && !reviewing) {
      const booking = bookings.find(b => b.id === pendingReviewBookingId);
      if (booking) {
        setReviewing(booking);
        setTab("bookings");
        const url = new URL(window.location.href);
        url.searchParams.delete("action");
        url.searchParams.delete("booking");
        window.history.replaceState({}, "", url.toString());
      }
    }
  }, [pendingReviewBookingId, bookings]);

  const cancel = async (id) => {
    if (!window.confirm("Annuler ce rendez-vous ? Cette action est irréversible.")) return;
    const booking = bookings.find(b => b.id === id);
    if (booking) {
      const hoursUntil = (new Date(booking.scheduled_at) - new Date()) / (1000 * 60 * 60);
      if (hoursUntil < 24) { toast("Annulation impossible — moins de 24h avant la séance", "error"); return; }
    }
    await sb.q("bookings").eq("id", id).update({ status: "cancelled" });
    try {
      if (booking) {
        const dt = new Date(booking.scheduled_at);
        const coach = coaches.find(c => c.id === booking.coach_id);
        if (coach) {
          const coachFull = await sb.q("coaches").eq("id", coach.id || coach.coach_id).single().get();
          const coachUserId = coachFull?.user_id || coach.user_id;
          const coachProfile = coachUserId ? await sb.q("profiles").eq("id", coachUserId).single().get() : null;
          if (coachProfile?.email) {
            await sendNotification("rdv_annule_coach", coachProfile.email, {
              client_name: user.name,
              date: dt.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }),
              time: dt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }),
            });
          }
          if (coachUserId) {
            await createInAppNotification(coachUserId, "rdv_annule_coach", "Réservation annulée",
              `${user.name} a annulé sa réservation du ${dt.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}.`, "coach-dashboard");
          }
        }
      }
    } catch(e) {}
    load();
  };

  const saveProfile = async () => {
    setSaving(true);
    await sb.q("profiles").eq("id", user.id).update({
      full_name: profileForm.full_name,
      objectif_pro: profileForm.objectif_pro,
      situation_actuelle: profileForm.situation_actuelle,
      delai_souhaite: profileForm.delai_souhaite,
      nb_seances_objectif: parseInt(profileForm.nb_seances_objectif) || 10,
    });
    setSaving(false); setEditProfile(false); load();
    toast("Profil mis à jour ✓", "success");
  };

  const statusColor = { pending: E.orange, confirmed: E.teal, cancelled: E.red, done: E.navy };
  const statusLabel = { pending: "En attente", confirmed: "Confirmé", cancelled: "Annulé", done: "Terminé" };

  // Stats calculées
  const doneCount = useMemo(() => bookings.filter(b => b.status === "done").length, [bookings]);
  const confirmedCount = useMemo(() => bookings.filter(b => b.status === "confirmed").length, [bookings]);
  const objectifSeances = profile?.nb_seances_objectif || 10;
  const progression = useMemo(() => Math.min(100, Math.round((doneCount / objectifSeances) * 100)), [doneCount, objectifSeances]);

  // Score motivation (Duolingo-style)
  const motivationScore = useMemo(() => {
    let score = 0;
    score += Math.min(40, doneCount * 8);
    score += Math.min(20, myReviews.length * 10);
    if (profile?.objectif_pro) score += 10;
    if (profile?.situation_actuelle) score += 10;
    if (profile?.delai_souhaite) score += 5;
    const recentBooking = bookings.find(b => (new Date() - new Date(b.scheduled_at)) < 14 * 24 * 60 * 60 * 1000);
    if (recentBooking) score += 15;
    return Math.min(100, score);
  }, [doneCount, myReviews, profile, bookings]);

  const motivationLabel = useMemo(() => motivationScore >= 80 ? { text: "🔥 En feu !", color: E.orange }
    : motivationScore >= 60 ? { text: "💪 Motivé", color: E.teal }
    : motivationScore >= 40 ? { text: "📈 En progression", color: E.blue }
    : { text: "🌱 Démarrage", color: E.gray }, [motivationScore]);

  // Streak
  const streak = useMemo(() => {
    const doneDates = bookings.filter(b => b.status === "done").map(b => new Date(b.scheduled_at));
    if (doneDates.length === 0) return 0;
    doneDates.sort((a, b) => b - a);
    let s = 1;
    for (let i = 1; i < doneDates.length; i++) {
      const diff = (doneDates[i-1] - doneDates[i]) / (1000 * 60 * 60 * 24 * 7);
      if (diff <= 2) s++; else break;
    }
    return s;
  }, [bookings]);

  // Prochain RDV
  const nextBooking = useMemo(() => bookings
    .filter(b => ["pending","confirmed"].includes(b.status) && new Date(b.scheduled_at) > new Date())
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))[0], [bookings]);

  const MENU_ITEMS = [
    ["home", "🏠", "Mon espace"],
    ["bookings", "📅", "Mes RDV", bookings.filter(b => b.status === "pending").length],
    ["tasks", "✅", "Mes actions", tasks.filter(t => t.status !== "done").length],
    ["loyalty", "🏆", "Fidélité & Parrainage"],
    ["profile", "👤", "Mon profil"],
  ];
  const currentMenu = MENU_ITEMS.find(m => m[0] === tab);

  return (
    <div style={{ padding: "24px 0 40px", animation: "fadeUp 0.4s ease" }}>

      {/* Header dropdown */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 20, color: E.navy }}>
            {currentMenu?.[1]} {currentMenu?.[2]}
          </div>
          <div style={{ color: E.gray, fontSize: 13 }}>Bonjour {user.name?.split(" ")[0]} 👋</div>
        </div>
        <div ref={menuRef} style={{ position: "relative" }}>
          <button onClick={() => setMenuOpen(o => !o)} aria-label="Menu navigation" style={{
            display: "flex", alignItems: "center", gap: 8,
            background: E.navy, color: E.white, border: "none",
            borderRadius: 12, padding: "10px 16px", cursor: "pointer",
            fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 13,
            boxShadow: E.shadowMd,
          }}>
            ☰ Menu
          </button>
          {menuOpen && (
            <div style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", background: E.white, borderRadius: 16, padding: 8, boxShadow: E.shadowMd, zIndex: E.zDropdown, minWidth: 200, animation: "fadeUp 0.15s ease" }}>
              {MENU_ITEMS.map(([k, icon, label, badge]) => (
                <button key={k} onClick={() => { setTab(k); setMenuOpen(false); }} style={{
                  width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                  gap: 10, padding: "12px 14px", background: tab === k ? E.tealLight : "transparent",
                  border: "none", borderRadius: 10, cursor: "pointer",
                  fontFamily: "'Outfit', sans-serif", fontWeight: tab === k ? 700 : 500,
                  fontSize: 14, color: tab === k ? E.teal : E.navy,
                }}>
                  <span>{icon} {label}</span>
                  {badge > 0 && <span style={{ background: E.red, color: E.white, fontSize: 10, fontWeight: 800, borderRadius: 20, padding: "2px 8px" }}>{badge}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── ACCUEIL ── */}
      {tab === "home" && (
        <div style={{ animation: "fadeTab 0.2s ease",  display: "grid", gap: 16 }}>

          {/* Hero card */}
          <div style={{ background: `linear-gradient(135deg, ${E.navy}, ${E.navyMid})`, borderRadius: 24, padding: "24px 22px", color: "white" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
              <div>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 22, marginBottom: 4 }}>
                  Mon parcours 🚀
                </div>
                <div style={{ color: "rgba(255,255,255,0.65)", fontSize: 13 }}>
                  {profile?.objectif_pro || "Définissez votre objectif →"}
                </div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 28, color: motivationLabel.color }}>{motivationScore}%</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>Motivation</div>
                <div style={{ fontSize: 13 }}>{motivationLabel.text}</div>
              </div>
            </div>

            {/* Progression */}
            <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 14, padding: "14px 16px", marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 13 }}>🎯 Progression du parcours</span>
                <span style={{ fontWeight: 800, fontSize: 13, color: E.teal }}>{progression}%</span>
              </div>
              <div style={{ height: 8, borderRadius: 4, background: "rgba(255,255,255,0.15)", overflow: "hidden", marginBottom: 8 }}>
                <div style={{ width: `${progression}%`, height: "100%", background: "linear-gradient(90deg, #4bbfb5, #8be3da)", borderRadius: 4, transition: "width 0.8s ease" }} />
              </div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>
                {doneCount} séance{doneCount > 1 ? "s" : ""} réalisée{doneCount > 1 ? "s" : ""} sur {objectifSeances} objectif
                {profile?.delai_souhaite && ` · Délai : ${profile.delai_souhaite}`}
              </div>
            </div>

            {/* Stats rapides */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              {[
                ["📅", "Séances", doneCount],
                ["🔥", "Streak", `${streak} sem.`],
                ["⭐", "Avis", myReviews.length],
              ].map(([icon, label, val]) => (
                <div key={label} style={{ background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: "10px 8px", textAlign: "center" }}>
                  <div style={{ fontSize: 18, marginBottom: 4 }}>{icon}</div>
                  <div style={{ fontWeight: 800, fontSize: 16 }}>{val}</div>
                  <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)" }}>{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions à faire */}
          {tasks.filter(t => t.status !== "done").length > 0 && (
            <Card style={{ padding: "18px 20px", cursor: "pointer", border: `1.5px solid ${E.teal}25` }} onClick={() => setTab("tasks")} hover>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ width: 48, height: 48, borderRadius: 14, background: E.tealLight, color: E.teal, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>✅</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 14, color: E.navy }}>Actions à réaliser</div>
                  <div style={{ color: E.gray, fontSize: 12, marginTop: 2 }}>
                    {tasks.filter(t => t.status !== "done").length} action{tasks.filter(t => t.status !== "done").length > 1 ? "s" : ""} en attente dans votre parcours.
                  </div>
                </div>
                <span style={{ color: E.teal, fontWeight: 900 }}>→</span>
              </div>
            </Card>
          )}

          {/* Prochain RDV */}
          {nextBooking ? (
            <Card style={{ padding: "18px 20px" }}>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 14, color: E.navy, marginBottom: 12 }}>📅 Prochain RDV</div>
              {(() => {
                const coach = coaches.find(c => c.id === nextBooking.coach_id);
                const dt = new Date(nextBooking.scheduled_at);
                const daysUntil = Math.ceil((dt - new Date()) / (1000 * 60 * 60 * 24));
                return (
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    {coach && <Avatar initials={coach.avatar} color={coach.color || E.teal} size={52} photoUrl={coach.photo_status === "approved" ? coach.photo_url : null} />}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 14, color: E.navy }}>{coach?.name || "Coach"}</div>
                      <div style={{ color: E.gray, fontSize: 13, marginTop: 2 }}>{dt.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} à {dt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: statusColor[nextBooking.status], background: `${statusColor[nextBooking.status]}18`, borderRadius: 20, padding: "2px 10px", marginTop: 4, display: "inline-block" }}>{statusLabel[nextBooking.status]}</span>
                    </div>
                    <div style={{ textAlign: "center", background: daysUntil <= 2 ? E.orangeLight : E.tealLight, borderRadius: 12, padding: "8px 12px" }}>
                      <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 22, color: daysUntil <= 2 ? E.orangeDark : E.teal }}>{daysUntil}</div>
                      <div style={{ fontSize: 10, color: E.gray }}>jour{daysUntil > 1 ? "s" : ""}</div>
                    </div>
                  </div>
                );
              })()}
            </Card>
          ) : (
            <Card style={{ padding: "18px 20px" }}>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 14, color: E.navy, marginBottom: 8 }}>📅 Aucun RDV à venir</div>
              <p style={{ color: E.gray, fontSize: 13, marginBottom: 12 }}>Réservez une séance avec l'un de nos coaches pour avancer vers votre objectif.</p>
              <Btn small onClick={() => onNavigate("browse")}>Trouver un coach →</Btn>
            </Card>
          )}

          {/* Notifications */}
          <NotificationPanel userId={user.id} compact onNavigate={onNavigate} />

          {/* Encouragement si profil incomplet */}
          {(!profile?.objectif_pro || !profile?.situation_actuelle) && (
            <Card style={{ padding: "18px 20px", border: `1.5px solid ${E.teal}33`, background: E.tealLight }}>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 14, color: E.navy, marginBottom: 8 }}>
                💡 Complétez votre profil pour un meilleur matching
              </div>
              <p style={{ color: E.gray, fontSize: 13, marginBottom: 12, lineHeight: 1.6 }}>
                Indiquez votre objectif et votre situation actuelle pour que notre IA vous propose les coaches les plus adaptés.
              </p>
              <Btn small onClick={() => setTab("profile")}>Compléter mon profil →</Btn>
            </Card>
          )}

          {/* Niveau fidélité */}
          {(() => {
            const lvl = getLoyaltyLevel(doneCount);
            const next = LOYALTY_LEVELS[LOYALTY_LEVELS.indexOf(lvl) + 1];
            return (
              <div onClick={() => setTab("loyalty")} style={{ display: "flex", alignItems: "center", gap: 12, background: lvl.bg, borderRadius: 14, padding: "14px 18px", cursor: "pointer", border: `1.5px solid ${lvl.color}33` }}>
                <span style={{ fontSize: 28 }}>{lvl.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 14, color: E.navy }}>Niveau {lvl.label}</div>
                  <div style={{ fontSize: 12, color: E.gray, marginTop: 2 }}>
                    {lvl.discount > 0 ? `-${lvl.discount}% sur vos séances` : next ? `Plus que ${next.min - doneCount} séance${next.min - doneCount > 1 ? "s" : ""} pour passer Argent` : "Accès standard"}
                  </div>
                </div>
                <span style={{ fontSize: 12, color: lvl.color, fontWeight: 700 }}>Voir →</span>
              </div>
            );
          })()}

          {/* Refaire le matching */}
          <Card style={{ padding: "18px 20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: E.tealLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>✨</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 14, color: E.navy }}>Refaire votre matching IA</div>
                <div style={{ color: E.gray, fontSize: 12, marginTop: 2 }}>Votre situation a évolué ? Trouvez de nouveaux coaches adaptés.</div>
              </div>
              <Btn small onClick={() => { onNavigate("quiz"); }}>Relancer →</Btn>
            </div>
          </Card>

          {/* Badges gamification */}
          <Card style={{ padding: "18px 20px" }}>
            <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 14, color: E.navy, marginBottom: 14 }}>🏆 Mes badges</div>
            <div style={{ display: "grid", gap: 10 }}>
              {[
                { icon: "🚀", label: "Lancé", done: bookings.length > 0, desc: "Faire votre première réservation", how: bookings.length > 0 ? null : "Réservez une séance avec un coach" },
                { icon: "⭐", label: "Contributeur", done: myReviews.length > 0, desc: "Laisser votre premier avis", how: myReviews.length > 0 ? null : "Laissez un avis après une séance terminée" },
                { icon: "🔥", label: "Assidu", done: streak >= 3, desc: "3 semaines consécutives avec une séance", how: streak >= 3 ? null : `Encore ${3 - streak} semaine${3 - streak > 1 ? "s" : ""} consécutive${3 - streak > 1 ? "s" : ""}` },
                { icon: "🎯", label: "Mi-parcours", done: progression >= 50, desc: "50% de l'objectif de séances atteint", how: progression >= 50 ? null : `${Math.ceil(objectifSeances / 2) - doneCount} séance${Math.ceil(objectifSeances / 2) - doneCount > 1 ? "s" : ""} de plus` },
                { icon: "🏅", label: "Accompli", done: progression >= 100, desc: `${objectifSeances} séances réalisées`, how: progression >= 100 ? null : `${objectifSeances - doneCount} séance${objectifSeances - doneCount > 1 ? "s" : ""} restante${objectifSeances - doneCount > 1 ? "s" : ""}` },
              ].map(b => (
                <div key={b.label} style={{ display: "flex", alignItems: "center", gap: 12, background: b.done ? E.tealLight : E.offWhite, borderRadius: 12, padding: "12px 14px", opacity: b.done ? 1 : 0.8, transition: "all 0.2s" }}>
                  <div style={{ width: 44, height: 44, borderRadius: "50%", background: b.done ? E.teal : E.grayLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0, border: b.done ? `2px solid ${E.teal}` : `2px solid ${E.grayLight}` }}>
                    {b.done ? b.icon : "🔒"}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 13, color: b.done ? E.navy : E.gray }}>
                      {b.icon} {b.label} {b.done && <span style={{ color: E.teal, fontSize: 11 }}>✓ Débloqué</span>}
                    </div>
                    <div style={{ fontSize: 12, color: E.gray, marginTop: 2 }}>{b.desc}</div>
                    {!b.done && b.how && (
                      <div style={{ fontSize: 11, color: E.teal, fontWeight: 600, marginTop: 3 }}>→ {b.how}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}


      {/* ── MES ACTIONS ── */}
      {tab === "tasks" && (
        <ClientTasksPanel
          user={user}
          tasks={tasks}
          setTasks={setTasks}
          coaches={coaches}
        />
      )}

      {/* ── MES RDV ── */}
      {tab === "bookings" && (
        <div style={{ animation: "fadeTab 0.2s ease",  display: "grid", gap: 10 }}>
          {loading ? [1,2,3].map(i => <SkeletonCard key={i} />) : (
            bookings.length === 0
              ? <EmptyState icon="📅" title="Aucune réservation" desc="Vous n'avez pas encore réservé de séance. Trouvez votre coach idéal et commencez votre parcours !" cta="Trouver un coach" onCta={() => onNavigate("browse")} />
              : bookings.map(b => {
                  const coach = coaches.find(c => c.id === b.coach_id);
                  const alreadyReviewed = myReviews.some(r => r.booking_id === b.id || r.coach_id === b.coach_id);
                  return (
                    <Card key={b.id} style={{ padding: "16px 18px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                        {coach && <Avatar initials={coach.avatar} color={coach.color || E.teal} size={44} photoUrl={coach.photo_status === "approved" ? coach.photo_url : null} />}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: 14, color: E.navy, fontFamily: "'Outfit', sans-serif" }}>{coach?.name || "Coach"}</div>
                          <div style={{ color: E.gray, fontSize: 12, marginTop: 2 }}>
                            {new Date(b.scheduled_at).toLocaleString("fr-FR", { weekday: "short", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 700, color: statusColor[b.status], background: `${statusColor[b.status]}15`, borderRadius: 20, padding: "2px 10px", marginTop: 4, display: "inline-block" }}>{statusLabel[b.status]}</span>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
                          {b.status === "pending" && <Btn small variant="danger" onClick={() => cancel(b.id)}>Annuler</Btn>}
                          {b.status === "done" && (alreadyReviewed
                            ? <span style={{ fontSize: 11, color: E.teal, fontWeight: 700, background: E.tealLight, borderRadius: 20, padding: "3px 10px" }}>⭐ Avis laissé</span>
                            : <Btn small variant="ghost" onClick={() => setReviewing(b)}>Laisser un avis</Btn>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })
          )}
        </div>
      )}

      {/* ── MON PROFIL ── */}
      {tab === "profile" && (
        <div style={{ animation: "fadeTab 0.2s ease",  display: "grid", gap: 16 }}>
          <Card style={{ padding: "20px 24px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
              <div>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 18, color: E.navy }}>{user.name}</div>
                <div style={{ color: E.gray, fontSize: 13, marginTop: 2 }}>{user.email}</div>
              </div>
              <Btn small variant={editProfile ? "danger" : "ghost"} onClick={() => setEditProfile(!editProfile)}>
                {editProfile ? "✕ Annuler" : "✏️ Modifier"}
              </Btn>
            </div>

            {/* Infos actuelles */}
            {!editProfile && (
              <div style={{ display: "grid", gap: 10 }}>
                {[
                  ["🎯", "Objectif professionnel", profile?.objectif_pro],
                  ["📍", "Situation actuelle", profile?.situation_actuelle],
                  ["⏱", "Délai souhaité", profile?.delai_souhaite],
                  ["📅", "Objectif de séances", profile?.nb_seances_objectif ? `${profile.nb_seances_objectif} séances` : null],
                ].map(([icon, label, val]) => (
                  <div key={label} style={{ background: E.offWhite, borderRadius: 10, padding: "12px 14px" }}>
                    <div style={{ fontSize: 11, color: E.gray, marginBottom: 3, fontFamily: "'Outfit', sans-serif" }}>{icon} {label}</div>
                    <div style={{ fontSize: 14, color: val ? E.navy : E.gray, fontWeight: val ? 600 : 400, fontFamily: "'Outfit', sans-serif", fontStyle: val ? "normal" : "italic" }}>
                      {val || "Non renseigné"}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Formulaire */}
            {editProfile && (
              <div style={{ animation: "fadeUp 0.3s ease", display: "grid", gap: 12 }}>
                <div>
                  <label style={{ color: E.gray, fontSize: 12, fontWeight: 600, marginBottom: 8, display: "block" }}>🎯 Objectif professionnel</label>
                  <input value={profileForm.objectif_pro || ""} onChange={e => setProfileForm(f => ({ ...f, objectif_pro: e.target.value }))}
                    placeholder="ex: Reconversion vers le digital, créer mon entreprise…"
                    style={{ width: "100%", boxSizing: "border-box", background: E.offWhite, border: `1.5px solid ${profileForm.objectif_pro ? E.teal : E.grayLight}`, borderRadius: E.radiusSm, padding: "10px 14px", color: E.text, fontSize: 14, outline: "none", fontFamily: "'Outfit', sans-serif" }}
                  />
                </div>
                <div>
                  <label style={{ color: E.gray, fontSize: 12, fontWeight: 600, marginBottom: 8, display: "block" }}>📍 Situation actuelle</label>
                  <select value={profileForm.situation_actuelle || ""} onChange={e => setProfileForm(f => ({ ...f, situation_actuelle: e.target.value }))}
                    style={{ width: "100%", background: E.offWhite, border: `1.5px solid ${E.grayLight}`, borderRadius: E.radiusSm, padding: "10px 14px", color: E.text, fontSize: 14, outline: "none", fontFamily: "'Outfit', sans-serif" }}>
                    <option value="">Sélectionnez…</option>
                    {["En poste", "En recherche d'emploi", "Étudiant", "Entrepreneur", "En arrêt", "En reconversion", "Autre"].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ color: E.gray, fontSize: 12, fontWeight: 600, marginBottom: 8, display: "block" }}>⏱ Délai souhaité</label>
                  <select value={profileForm.delai_souhaite || ""} onChange={e => setProfileForm(f => ({ ...f, delai_souhaite: e.target.value }))}
                    style={{ width: "100%", background: E.offWhite, border: `1.5px solid ${E.grayLight}`, borderRadius: E.radiusSm, padding: "10px 14px", color: E.text, fontSize: 14, outline: "none", fontFamily: "'Outfit', sans-serif" }}>
                    <option value="">Sélectionnez…</option>
                    {["1 mois", "3 mois", "6 mois", "1 an", "Plus de 1 an", "Flexible"].map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ color: E.gray, fontSize: 12, fontWeight: 600, marginBottom: 8, display: "block" }}>📅 Objectif nombre de séances</label>
                  <input type="number" min="1" max="50" value={profileForm.nb_seances_objectif || 10} onChange={e => setProfileForm(f => ({ ...f, nb_seances_objectif: e.target.value }))} placeholder="ex: 10"
                    style={{ width: "100%", boxSizing: "border-box", background: E.offWhite, border: `1.5px solid ${E.grayLight}`, borderRadius: E.radiusSm, padding: "10px 14px", color: E.text, fontSize: 14, outline: "none", fontFamily: "'Outfit', sans-serif" }}
                  />
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Btn variant="ghost" onClick={() => setEditProfile(false)}>Annuler</Btn>
                  <Btn onClick={saveProfile} disabled={saving}>{saving ? <><Spinner size={14} color="white" /> Sauvegarde…</> : "Sauvegarder ✓"}</Btn>
                </div>
              </div>
            )}
          </Card>

          {/* Score de motivation */}
          <Card style={{ padding: "18px 20px" }}>
            <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 14, color: E.navy, marginBottom: 14 }}>
              🔥 Score de motivation
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 14 }}>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 44, color: motivationLabel.color, lineHeight: 1 }}>{motivationScore}</div>
              <div>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 16, color: E.navy }}>{motivationLabel.text}</div>
                <div style={{ color: E.gray, fontSize: 12 }}>Sur 100 points</div>
              </div>
            </div>
            <div style={{ height: 8, background: E.grayLight, borderRadius: 4, marginBottom: 14, overflow: "hidden" }}>
              <div style={{ width: `${motivationScore}%`, height: "100%", background: `linear-gradient(90deg, ${E.teal}, ${motivationLabel.color})`, borderRadius: 4, transition: "width 0.8s ease" }} />
            </div>
            <div style={{ display: "grid", gap: 8 }}>
              {[
                ["📅", "Séances réalisées", Math.min(40, doneCount * 8), 40],
                ["⭐", "Avis laissés", Math.min(20, myReviews.length * 10), 20],
                ["👤", "Profil complété", (profile?.objectif_pro ? 10 : 0) + (profile?.situation_actuelle ? 10 : 0) + (profile?.delai_souhaite ? 5 : 0), 25],
                ["🔥", "Activité récente", bookings.find(b => (new Date() - new Date(b.scheduled_at)) < 14*24*60*60*1000) ? 15 : 0, 15],
              ].map(([icon, label, pts, max]) => (
                <div key={label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 14, flexShrink: 0 }}>{icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                      <span style={{ fontSize: 12, color: E.navy, fontFamily: "'Outfit', sans-serif" }}>{label}</span>
                      <span style={{ fontSize: 11, color: E.gray }}>{pts}/{max}</span>
                    </div>
                    <div style={{ height: 4, background: E.grayLight, borderRadius: 2, overflow: "hidden" }}>
                      <div style={{ width: `${(pts/max)*100}%`, height: "100%", background: E.teal, borderRadius: 2 }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* ── FIDÉLITÉ & PARRAINAGE ── */}
      {tab === "loyalty" && (
        <div style={{ display: "grid", gap: 16, animation: "fadeTab 0.2s ease" }}>

          {/* Niveau de fidélité */}
          <LoyaltyCard doneCount={doneCount} />

          {/* Tous les niveaux */}
          <Card style={{ padding: "20px 22px" }}>
            <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 15, color: E.navy, marginBottom: 14 }}>
              📊 Tous les niveaux
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {LOYALTY_LEVELS.map(l => {
                const isCurrentLevel = getLoyaltyLevel(doneCount).key === l.key;
                return (
                  <div key={l.key} style={{ display: "flex", alignItems: "center", gap: 12, background: isCurrentLevel ? l.bg : E.offWhite, borderRadius: 12, padding: "12px 14px", border: isCurrentLevel ? `1.5px solid ${l.color}44` : "1.5px solid transparent" }}>
                    <span style={{ fontSize: 24 }}>{l.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 14, color: E.navy }}>{l.label}</div>
                      <div style={{ fontSize: 12, color: E.gray }}>{l.min === 0 ? "Dès l'inscription" : `À partir de ${l.min} séances`}</div>
                    </div>
                    {l.discount > 0
                      ? <span style={{ background: l.color, color: "white", borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 800 }}>-{l.discount}%</span>
                      : <span style={{ color: E.gray, fontSize: 12 }}>Standard</span>
                    }
                    {isCurrentLevel && <span style={{ color: l.color, fontSize: 11, fontWeight: 700 }}>← Vous</span>}
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Parrainage */}
          <ReferralCard
            user={user}
            referrals={referrals}
            onShare={() => {
              const code = generateReferralCode(user.id);
              const url = `https://elivio.fr?ref=${code}`;
              if (navigator.share) {
                navigator.share({ title: "Rejoins-moi sur Elivio !", text: "Inscris-toi avec mon lien et profite d'une réduction sur ta première séance de coaching.", url });
              } else {
                navigator.clipboard?.writeText(url);
                toast("Lien copié ! 🔗", "success");
              }
            }}
          />
        </div>
      )}

      {reviewing && <ReviewModal booking={reviewing} user={user} onClose={() => setReviewing(null)} onDone={load} />}
    </div>
  );
}

// ─── PAGE PUBLIQUE COACH ──────────────────
function CoachPublicPage({ slug, onBook, onConnect }) {
  const [coach, setCoach] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [availabilities, setAvailabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState("about");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      // Cherche le coach par slug (nom normalisé)
      const coaches = await sb.q("coach_stats").eq("status", "active").get();
      const all = Array.isArray(coaches) ? coaches : [];
      const found = all.find(c => {
        const s = (c.name || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
        return s === slug;
      });
      if (!found) { setNotFound(true); setLoading(false); return; }
      setCoach({ ...found, id: found.coach_id });
      const [rvs, avs] = await Promise.all([
        sb.q("reviews").eq("coach_id", found.coach_id).order("created_at", false).get(),
        sb.q("availabilities").eq("coach_id", found.coach_id).get(),
      ]);
      setReviews(Array.isArray(rvs) ? rvs : []);
      setAvailabilities(Array.isArray(avs) ? avs : []);
      setLoading(false);

      // SEO meta tags
      document.title = `${found.name} — Coach ${(found.types||[]).join(", ")} | Elivio`;
      const setMeta = (prop, val) => {
        let el = document.querySelector(`meta[property="${prop}"]`) || document.querySelector(`meta[name="${prop}"]`);
        if (!el) { el = document.createElement("meta"); el.setAttribute(prop.startsWith("og:") ? "property" : "name", prop); document.head.appendChild(el); }
        el.setAttribute("content", val);
      };
      setMeta("og:title", `${found.name} — Coach sur Elivio`);
      setMeta("og:description", found.bio || `Coach ${(found.types||[]).join(", ")} — ${found.rate}€/séance`);
      if (found.photo_url && found.photo_status === "approved") setMeta("og:image", found.photo_url);
      setMeta("og:url", window.location.href);
    };
    load();
  }, [slug]);

  if (loading) return <LoadingPage />;
  if (notFound) return (
    <div style={{ padding: "80px 20px", textAlign: "center" }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
      <h2 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, color: E.navy, marginBottom: 8 }}>Coach introuvable</h2>
      <p style={{ color: E.gray, fontSize: 14 }}>Ce profil n'existe pas ou n'est plus disponible.</p>
    </div>
  );

  const avgRating = reviews.length > 0 ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : null;
  const slotsCount = availabilities.reduce((acc, av) => acc + (parseInt(av.end_time?.slice(0,2)||"17") - parseInt(av.start_time?.slice(0,2)||"9")), 0);
  const DAYS = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];

  return (
    <div style={{ maxWidth: 600, margin: "0 auto", paddingBottom: 100 }}>

      {/* Hero */}
      <div style={{ background: `linear-gradient(160deg, ${E.navy} 0%, ${E.navyMid} 100%)`, padding: "40px 24px 32px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -40, right: -40, width: 200, height: 200, borderRadius: "50%", background: `${E.teal}15` }} />
        <div style={{ display: "flex", gap: 18, alignItems: "flex-start", position: "relative" }}>
          <Avatar initials={coach.avatar} color={coach.color || E.teal} size={80}
            photoUrl={coach.photo_status === "approved" ? coach.photo_url : null} />
          <div style={{ flex: 1 }}>
            <h1 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 24, color: E.white, margin: "0 0 6px" }}>{coach.name}</h1>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
              {(coach.types||[]).map(t => (
                <span key={t} style={{ background: `${E.teal}30`, color: E.teal, borderRadius: 20, padding: "3px 12px", fontSize: 12, fontWeight: 700 }}>{t}</span>
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {avgRating && <><Stars rating={parseFloat(avgRating)} /><span style={{ color: "rgba(255,255,255,0.7)", fontSize: 13 }}>{avgRating} ({reviews.length} avis)</span></>}
              {!avgRating && <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>Nouveau coach</span>}
            </div>
          </div>
        </div>

        {/* Stats rapides */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 24 }}>
          {[
            ["💶", `${coach.rate}€`, "par séance"],
            ["📅", slotsCount > 0 ? `${slotsCount}h` : "—", "disponibles/sem"],
            ["🎓", coach.years_experience ? `${coach.years_experience} ans` : "—", "d'expérience"],
          ].map(([icon, val, label]) => (
            <div key={label} style={{ background: "rgba(255,255,255,0.08)", borderRadius: 14, padding: "12px 8px", textAlign: "center" }}>
              <div style={{ fontSize: 20, marginBottom: 4 }}>{icon}</div>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 18, color: E.white }}>{val}</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", borderBottom: `2px solid ${E.grayLight}`, background: E.white, position: "sticky", top: 60, zIndex: E.zSticky }}>
        {[["about","À propos"], ["availability","Disponibilités"], ["reviews",`Avis (${reviews.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            flex: 1, padding: "13px 8px", background: "transparent", border: "none", cursor: "pointer",
            fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 13,
            color: tab === k ? E.teal : E.gray,
            borderBottom: `2px solid ${tab === k ? E.teal : "transparent"}`,
            marginBottom: -2,
          }}>{l}</button>
        ))}
      </div>

      <div style={{ padding: "24px 20px" }}>

        {/* À propos */}
        {tab === "about" && (
          <div style={{ display: "grid", gap: 20 }}>
            {coach.bio && (
              <div>
                <SectionLabel>Présentation</SectionLabel>
                <p style={{ color: E.textSub, fontSize: 14, lineHeight: 1.75, margin: 0 }}>{coach.bio}</p>
              </div>
            )}
            {(coach.specialties||[]).length > 0 && (
              <div>
                <SectionLabel>Spécialités</SectionLabel>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {(coach.specialties||[]).map(s => (
                    <span key={s} style={{ background: E.tealLight, color: E.teal, borderRadius: 20, padding: "5px 14px", fontSize: 13, fontWeight: 600 }}>{s}</span>
                  ))}
                </div>
              </div>
            )}
            {coach.certification && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, background: E.offWhite, borderRadius: 12, padding: "12px 16px" }}>
                <span style={{ fontSize: 20 }}>🎓</span>
                <div>
                  <div style={{ fontSize: 11, color: E.gray }}>Certification</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: E.navy }}>{coach.certification}</div>
                </div>
              </div>
            )}
            {coach.linkedin_url && (
              <a href={coach.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", gap: 10, background: "#EFF6FF", borderRadius: 12, padding: "12px 16px", textDecoration: "none" }}>
                <span style={{ fontSize: 20 }}>🔗</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: "#1D4ED8" }}>Voir le profil LinkedIn</span>
              </a>
            )}
          </div>
        )}

        {/* Disponibilités */}
        {tab === "availability" && (
          <div style={{ display: "grid", gap: 12 }}>
            {availabilities.length === 0
              ? <EmptyState icon="📅" title="Pas de créneaux" desc="Ce coach n'a pas encore renseigné ses disponibilités." />
              : availabilities.map(a => (
                <div key={a.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: E.offWhite, borderRadius: 12, padding: "14px 18px" }}>
                  <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 15, color: E.navy }}>{DAYS[a.day_of_week]}</div>
                  <div style={{ color: E.teal, fontWeight: 700, fontSize: 14 }}>{a.start_time?.slice(0,5)} → {a.end_time?.slice(0,5)}</div>
                </div>
              ))
            }
          </div>
        )}

        {/* Avis */}
        {tab === "reviews" && (
          <div style={{ display: "grid", gap: 12 }}>
            {reviews.length === 0
              ? <EmptyState icon="⭐" title="Pas encore d'avis" desc="Soyez le premier à laisser un avis après votre séance !" />
              : <>
                  {avgRating && (
                    <div style={{ display: "flex", alignItems: "center", gap: 16, background: E.offWhite, borderRadius: 14, padding: "18px 20px", marginBottom: 8 }}>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 40, color: E.navy, lineHeight: 1 }}>{avgRating}</div>
                        <Stars rating={parseFloat(avgRating)} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 15, color: E.navy, fontFamily: "'Outfit', sans-serif" }}>{reviews.length} avis</div>
                        <div style={{ color: E.gray, fontSize: 13 }}>de clients vérifiés</div>
                      </div>
                    </div>
                  )}
                  {reviews.map(r => (
                    <Card key={r.id} style={{ padding: "16px 18px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                        <Stars rating={r.rating} />
                        <span style={{ color: E.gray, fontSize: 12 }}>{new Date(r.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span>
                      </div>
                      {r.comment && <p style={{ color: E.textSub, fontSize: 14, lineHeight: 1.6, margin: 0 }}>{r.comment}</p>}
                    </Card>
                  ))}
                </>
            }
          </div>
        )}
      </div>

      {/* CTA fixe en bas */}
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: E.white, borderTop: `1px solid ${E.grayLight}`, padding: "16px 20px", paddingBottom: "calc(16px + env(safe-area-inset-bottom))", zIndex: E.zSticky, boxShadow: "0 -4px 20px rgba(13,27,62,0.08)" }}>
        <div style={{ maxWidth: 600, margin: "0 auto", display: "flex", gap: 12, alignItems: "center" }}>
          <div>
            <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 20, color: E.navy }}>{coach.rate}€</div>
            <div style={{ color: E.gray, fontSize: 12 }}>par séance</div>
          </div>
          <Btn full onClick={() => onBook(coach)}>
            Réserver une séance →
          </Btn>
        </div>
      </div>
    </div>
  );
}

function AdminPanel({ user, onNavigate }) {
  const [tab, setTab] = useState("profiles");
  const [pending, setPending] = useState([]);
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reasons, setReasons] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const [profiles, photos] = await Promise.all([
      sb.q("coaches").neq("status", "active").order("created_at", false).get(),
      sb.q("coaches").eq("photo_status", "pending").get(),
    ]);
    setPending(Array.isArray(profiles) ? profiles : []);
    setPendingPhotos(Array.isArray(photos) ? photos : []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const validate = async (id, status, reason) => {
    await sb.q("coaches").eq("id", id).update({ status, ...(reason ? { rejection_reason: reason } : {}) });
    try {
      const coach = pending.find(c => c.id === id);
      if (coach?.user_id) {
        const coachProfile = await sb.q("profiles").eq("id", coach.user_id).single().get();
        const coachEmail = coachProfile?.email;
        if (coachEmail) {
          await sendNotification(status === "active" ? "coach_valide" : "coach_refuse", coachEmail, {
            coach_name: coach.name,
            reason: reason || "",
          });
        }
        await createInAppNotification(
          coach.user_id,
          status === "active" ? "coach_valide" : "coach_refuse",
          status === "active" ? "Profil coach validé ✅" : "Profil coach à modifier 📋",
          status === "active" ? "Votre profil est maintenant visible sur Elivio." : `Votre dossier nécessite des modifications. ${reason || ""}`,
          "coach-dashboard"
        );
        toast(coachEmail ? "Email envoyé au coach ✓" : "Notif in-app envoyée (email non trouvé)", "success");
      }
    } catch(e) {
      console.error("Notif validation coach erreur", e);
      toast("Validation enregistrée", "success");
    }
    load();
  };

  const validatePhoto = async (id, approved) => {
    await sb.q("coaches").eq("id", id).update({ photo_status: approved ? "approved" : "rejected" });
    try {
      const coach = pendingPhotos.find(c => c.id === id);
      if (coach?.user_id) {
        const coachProfile = await sb.q("profiles").eq("id", coach.user_id).single().get();
        const coachEmail = coachProfile?.email;
        if (coachEmail) {
          await sendNotification(approved ? "photo_validee" : "photo_refusee", coachEmail, {
            coach_name: coach.name,
          });
        }
        await createInAppNotification(
          coach.user_id,
          approved ? "photo_validee" : "photo_refusee",
          approved ? "Photo approuvée ✅" : "Photo refusée ❌",
          approved ? "Votre photo de profil est maintenant visible." : "Votre photo a été refusée. Vous pouvez en envoyer une nouvelle.",
          "coach-dashboard"
        );
        toast(coachEmail ? "Email envoyé au coach ✓" : "Notif in-app envoyée (email non trouvé)", "success");
      }
    } catch(e) {
      console.error("Notif photo coach erreur", e);
      toast("Décision enregistrée", "success");
    }
    load();
  };

  return (
    <div style={{ padding: "32px 0", animation: "fadeUp 0.4s ease" }}>
      <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 24, fontWeight: 800, color: E.navy, marginBottom: 20 }}>
        Panel Admin
      </h2>

      <NotificationPanel userId={user.id} compact onNavigate={onNavigate} />

      {/* Tabs */}
      <div style={{ display: "flex", gap: 0, marginBottom: 24, borderBottom: `2px solid ${E.grayLight}` }}>
        {[
          ["profiles", `📋 Dossiers (${pending.length})`],
          ["photos", `📷 Photos (${pendingPhotos.length})`],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding: "10px 18px", background: "transparent", border: "none", cursor: "pointer",
            fontSize: 13, fontWeight: 700, fontFamily: "'Outfit', sans-serif",
            color: tab === k ? E.teal : E.gray,
            borderBottom: `2px solid ${tab === k ? E.teal : "transparent"}`,
            marginBottom: -2, transition: "all 0.15s",
          }}>{l}</button>
        ))}
      </div>

      {loading ? <div style={{ color: E.teal }}>Chargement…</div> : (
        <>
          {/* TAB: Dossiers coaches */}
          {tab === "profiles" && (
            pending.length === 0
              ? <Card style={{ padding: 32, textAlign: "center" }}>
                  <div style={{ color: E.gray, fontFamily: "'Outfit', sans-serif" }}>Aucun dossier en attente. ✓</div>
                </Card>
              : (
                <div style={{ display: "grid", gap: 14 }}>
                  {pending.map(c => (
                    <Card key={c.id} style={{ padding: 20 }}>
                      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 12 }}>
                        <Avatar initials={c.avatar || "CO"} color={c.color || E.teal} size={48}
                          photoUrl={c.photo_status === "approved" ? c.photo_url : null} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 16, color: E.navy }}>{c.name}</div>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
                            {(c.types || []).map(t => <Tag key={t} label={t} color={c.color || E.teal} />)}
                          </div>
                          <div style={{ color: E.gray, fontSize: 13, marginTop: 4 }}>
                            {c.rate}€/séance · {c.years_experience} ans{c.certification && ` · ${c.certification}`}
                          </div>
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 12px", borderRadius: 20, fontFamily: "'Outfit', sans-serif", background: c.status === "pending" ? E.orangeLight : E.redLight, color: c.status === "pending" ? E.orangeDark : "#dc2626" }}>
                          {c.status === "pending" ? "En attente" : "Rejeté"}
                        </span>
                      </div>
                      <p style={{ color: E.textSub, fontSize: 13, lineHeight: 1.6, marginBottom: 12, fontFamily: "'Outfit', sans-serif" }}>{c.bio}</p>

                      {/* Spécialités */}
                      {(c.specialties || []).length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: E.gray, marginBottom: 8, fontFamily: "'Outfit', sans-serif", textTransform: "uppercase", letterSpacing: 1 }}>Spécialités</div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                            {(c.specialties || []).map(s => (
                              <span key={s} style={{ fontSize: 11, color: E.gray, background: E.offWhite, borderRadius: 20, padding: "3px 10px", fontFamily: "'Outfit', sans-serif" }}>{s}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Disponibilité */}
                      {c.availability_label && (
                        <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: E.gray, fontFamily: "'Outfit', sans-serif", textTransform: "uppercase", letterSpacing: 1 }}>Disponibilité :</span>
                          <span style={{ fontSize: 12, color: E.navy, fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>{c.availability_label}</span>
                        </div>
                      )}

                      {c.linkedin_url && <a href={c.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ color: E.teal, fontSize: 12, display: "block", marginBottom: 12 }}>🔗 LinkedIn</a>}
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <Btn small onClick={() => validate(c.id, "active")}>✓ Valider</Btn>
                        <input value={reasons[c.id] || ""} onChange={e => setReasons(r => ({ ...r, [c.id]: e.target.value }))}
                          placeholder="Raison du refus…"
                          style={{ flex: 1, minWidth: 160, background: E.offWhite, border: `1px solid ${E.grayLight}`, borderRadius: E.radiusSm, padding: "6px 12px", color: E.text, fontSize: 12, fontFamily: "'Outfit', sans-serif", outline: "none" }}
                        />
                        <Btn small variant="danger" onClick={() => validate(c.id, "rejected", reasons[c.id])}>✕ Rejeter</Btn>
                      </div>
                    </Card>
                  ))}
                </div>
              )
          )}

          {/* TAB: Photos */}
          {tab === "photos" && (
            pendingPhotos.length === 0
              ? <Card style={{ padding: 32, textAlign: "center" }}>
                  <div style={{ color: E.gray, fontFamily: "'Outfit', sans-serif" }}>Aucune photo en attente. ✓</div>
                </Card>
              : (
                <div style={{ display: "grid", gap: 14 }}>
                  {pendingPhotos.map(c => (
                    <Card key={c.id} style={{ padding: 20 }}>
                      <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 16 }}>
                        <Avatar initials={c.avatar || "CO"} color={c.color || E.teal} size={44} />
                        <div>
                          <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 14, color: E.navy }}>{c.name}</div>
                          <div style={{ color: E.gray, fontSize: 12 }}>Photo en attente de validation</div>
                        </div>
                      </div>

                      {/* Aperçu photo */}
                      {c.photo_url && (
                        <div style={{ marginBottom: 16 }}>
                          <img loading="lazy"
                            src={c.photo_url}
                            alt={c.name}
                            style={{ width: "100%", maxHeight: 280, objectFit: "cover", borderRadius: 12, border: `1px solid ${E.grayLight}` }}
                          />
                        </div>
                      )}

                      <div style={{ display: "flex", gap: 8 }}>
                        <Btn full onClick={() => validatePhoto(c.id, true)}>✓ Approuver la photo</Btn>
                        <Btn variant="danger" full onClick={() => validatePhoto(c.id, false)}>✕ Refuser</Btn>
                      </div>
                    </Card>
                  ))}
                </div>
              )
          )}
        </>
      )}
    </div>
  );
}

// ─── CHATBOX IA ───────────────────────────
function ChatBox({ onSuggestCoaches }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Bonjour 👋 Je suis l'assistant Elivio. Parlez-moi de votre situation professionnelle, je suis là pour vous écouter et vous orienter." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useState(null);

  const scrollToBottom = () => {
    setTimeout(() => {
      const el = document.getElementById("elivio-chat-bottom");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 50);
  };

  const sendText = (text) => {
    if (!text.trim() || loading) return;
    const newMessages = [...messages, { role: "user", text }];
    setMessages(newMessages);
    setInput("");
    setLoading(true);
    scrollToBottom();

    const getSmartReply = (userText) => {
      const t = userText.toLowerCase();
      if (t.includes("bilan") && t.includes("compétence")) return "Un bilan de compétences est une excellente démarche pour mieux vous connaître et définir votre projet professionnel. Nos coaches spécialisés en reconversion peuvent vous accompagner pas à pas dans cette réflexion. Qu'est-ce qui vous a amené à envisager cette démarche aujourd'hui ?";
      if (t.includes("reconversion") || t.includes("changer de métier")) return "Une reconversion professionnelle, c'est souvent à la fois excitant et intimidant. Nos coaches ont accompagné des centaines de personnes dans cette transition. Quel secteur ou type d'activité vous attire en ce moment ?";
      if (t.includes("bloqué") || t.includes("bloque") || t.includes("perdu") || t.includes("voie")) return "Se sentir bloqué, c'est souvent le signe qu'un changement est nécessaire. Nos coaches sont justement là pour vous aider à clarifier votre situation et retrouver de l'élan. Depuis combien de temps ressentez-vous cela ?";
      if (t.includes("stress") || t.includes("burn") || t.includes("épuisé") || t.includes("fatigué")) return "Le stress et l'épuisement professionnel sont des signaux importants à ne pas ignorer. Un coach spécialisé en bien-être peut vous aider à retrouver l'équilibre. Souhaitez-vous découvrir nos coaches bien-être ?";
      if (t.includes("confiance") || t.includes("peur") || t.includes("doute")) return "Le manque de confiance est l'un des freins les plus courants dans la vie professionnelle — et l'un des plus travaillables avec le bon accompagnement. Qu'est-ce qui vous fait douter de vous en ce moment ?";
      if (t.includes("business") || t.includes("entrepren") || t.includes("créer")) return "Se lancer dans l'entrepreneuriat demande à la fois de la vision et de la méthode. Nos coaches business peuvent vous aider à structurer votre projet. Vous en êtes à quelle étape ?";
      if (t.includes("coach") || t.includes("trouver") || t.includes("cherche")) return "Je peux vous aider à trouver le coach idéal ! Vous pouvez utiliser notre questionnaire de matching en 4 questions, ou explorer directement nos coaches. Que préférez-vous ?";
      return "Merci de me partager ça. Pour mieux vous orienter vers le bon accompagnement, pouvez-vous me dire ce qui vous pèse le plus dans votre situation professionnelle en ce moment ?";
    };

    fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "claude-sonnet-4-20250514",
        max_tokens: 1000,
        system: "Tu es l'assistant bienveillant d'Elivio, une plateforme de coaching pour la transition professionnelle. Tu aides les utilisateurs à exprimer leur situation, leurs blocages, leurs aspirations. Tu es à l'écoute, empathique, jamais intrusif. Tu poses des questions ouvertes pour mieux comprendre. Si la personne semble prête, tu peux lui suggérer de découvrir nos coaches. Tu ne poses jamais plus d'une question à la fois. Réponds toujours en français, de façon chaleureuse et concise (3-4 phrases max).",
        messages: newMessages.map(m => ({ role: m.role === "assistant" ? "assistant" : "user", content: m.text })),
      }),
    })
    .then(r => r.json())
    .then(data => {
      const reply = data.content?.map(b => b.text || "").join("") || getSmartReply(text);
      setMessages(prev => [...prev, { role: "assistant", text: reply }]);
    })
    .catch(() => {
      setMessages(prev => [...prev, { role: "assistant", text: getSmartReply(text) }]);
    })
    .finally(() => { setLoading(false); scrollToBottom(); });
  };

  const send = () => sendText(input.trim());

  const handleKey = e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <>
      {/* Bouton flottant */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          position: "fixed", bottom: 24, right: 24, zIndex: E.zDropdown,
          width: 56, height: 56, borderRadius: "50%",
          background: open ? E.navy : E.teal,
          border: "none", cursor: "pointer",
          boxShadow: `0 4px 20px ${E.teal}66`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 22, transition: "all 0.2s ease",
          transform: open ? "rotate(0deg)" : "scale(1)",
        }}
      >
        {open ? <span style={{ color: E.white, fontSize: 18, fontWeight: 700 }}>✕</span> : <span>💬</span>}
      </button>

      {/* Fenêtre chat */}
      {open && (
        <div style={{
          position: "fixed", bottom: 92, right: 24, zIndex: 499,
          width: 340, maxWidth: "calc(100vw - 32px)",
          background: E.white, borderRadius: 20,
          boxShadow: "0 8px 48px rgba(13,27,62,0.18)",
          border: `1px solid ${E.grayLight}`,
          display: "flex", flexDirection: "column",
          overflow: "hidden", animation: "fadeUp 0.25s ease",
          maxHeight: "70vh",
        }}>
          {/* Header */}
          <div style={{ background: NAVY, padding: "16px 18px", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: E.tealLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🤝</div>
            <div>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 14, color: E.white }}>Assistant Elivio</div>
              <div style={{ fontSize: 11, color: `${E.teal}`, fontFamily: "'Outfit', sans-serif" }}>● En ligne</div>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: "auto", padding: "16px 14px", display: "flex", flexDirection: "column", gap: 10, minHeight: 200, maxHeight: 360 }}>
            {messages.map((m, i) => (
              <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
                <div style={{
                  maxWidth: "82%",
                  background: m.role === "user" ? E.teal : E.offWhite,
                  color: m.role === "user" ? E.white : E.text,
                  borderRadius: m.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                  padding: "10px 14px",
                  fontSize: 13,
                  lineHeight: 1.6,
                  fontFamily: "'Outfit', sans-serif",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                }}>{m.text}</div>
              </div>
            ))}
            {loading && (
              <div style={{ display: "flex", justifyContent: "flex-start" }}>
                <div style={{ background: E.offWhite, borderRadius: "16px 16px 16px 4px", padding: "10px 16px", display: "flex", gap: 4, alignItems: "center" }}>
                  {[0, 1, 2].map(i => (
                    <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: TEAL, animation: "pulse 1.2s ease infinite", animationDelay: `${i * 0.2}s` }} />
                  ))}
                </div>
              </div>
            )}
            <div id="elivio-chat-bottom" />
          </div>

          {/* Suggestions rapides */}
          {messages.length === 1 && !loading && (
            <div style={{ padding: "0 14px 10px", display: "flex", gap: 8, flexWrap: "wrap" }}>
              {["Je veux changer de métier", "Je me sens bloqué(e)", "Je cherche ma voie"].map(s => (
                <button key={s} onClick={() => sendText(s)} style={{
                  background: E.tealLight, border: `1px solid ${E.teal}33`,
                  borderRadius: 20, padding: "4px 10px", fontSize: 11,
                  color: E.teal, cursor: "pointer", fontFamily: "'Outfit', sans-serif", fontWeight: 600,
                }}>{s}</button>
              ))}
            </div>
          )}

          {/* Input */}
          <div style={{ padding: "10px 12px", borderTop: `1px solid ${E.grayLight}`, display: "flex", gap: 8, alignItems: "flex-end" }}>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Écrivez votre message…"
              rows={1}
              style={{
                flex: 1, background: E.offWhite, border: `1.5px solid ${E.grayLight}`,
                borderRadius: 12, padding: "9px 12px", color: E.text, fontSize: 13,
                fontFamily: "'Outfit', sans-serif", outline: "none", resize: "none",
                lineHeight: 1.5,
              }}
            />
            <button
              onClick={send}
              disabled={!input.trim() || loading}
              style={{
                width: 36, height: 36, borderRadius: "50%",
                background: input.trim() ? E.teal : E.grayLight,
                border: "none", cursor: input.trim() ? "pointer" : "default",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 14, flexShrink: 0, transition: "background 0.15s",
              }}
            >→</button>
          </div>
        </div>
      )}
    </>
  );
}

// ─── MAIN APP ─────────────────────────────
// ─── SPINNER ──────────────────────────────
function Spinner({ size = 20, color = TEAL }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{
        width: size, height: size, borderRadius: "50%",
        border: `2px solid ${color}22`,
        borderTop: `2px solid ${color}`,
        animation: "spin 0.7s linear infinite",
        flexShrink: 0,
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}

function LoadingPage() {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px", gap: 16 }}>
      <Spinner size={32} />
      <span style={{ color: E.gray, fontSize: 14, fontFamily: "'Outfit', sans-serif" }}>Chargement…</span>
    </div>
  );
}

// ─── USE WINDOW SIZE ──────────────────────
function useWindowSize() {
  const [size, setSize] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => {
    const handler = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return size;
}

// ─── USE CLICK OUTSIDE ────────────────────
function useClickOutside(ref, handler) {
  useEffect(() => {
    const listener = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      handler(e);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}

// ─── NETWORK ERROR BANNER ─────────────────
function NetworkStatus() {
  const [offline, setOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const goOffline = () => setOffline(true);
    const goOnline = () => setOffline(false);
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, []);
  if (!offline) return null;
  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: E.zToast,
      background: E.red, color: "white", textAlign: "center",
      padding: "8px 16px", fontSize: 13, fontWeight: 700,
      fontFamily: "'Outfit', sans-serif",
    }}>
      📡 Pas de connexion internet — certaines fonctionnalités sont indisponibles
    </div>
  );
}
function PullToRefresh({ children, onRefresh }) {
  const [pulling, setPulling] = useState(false);
  const [pullY, setPullY] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const THRESHOLD = 70;

  const onTouchStart = (e) => {
    if (window.scrollY === 0) startY.current = e.touches[0].clientY;
  };

  const onTouchMove = (e) => {
    if (!startY.current) return;
    const dy = e.touches[0].clientY - startY.current;
    if (dy > 0 && window.scrollY === 0) {
      setPulling(true);
      setPullY(Math.min(dy * 0.5, THRESHOLD + 20));
    }
  };

  const onTouchEnd = async () => {
    if (pullY >= THRESHOLD) {
      setRefreshing(true);
      setPullY(40);
      await onRefresh();
      setRefreshing(false);
    }
    setPulling(false);
    setPullY(0);
    startY.current = 0;
  };

  return (
    <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} style={{ position: "relative" }}>
      {/* Indicateur de tirage */}
      <div style={{
        position: "fixed", top: 60, left: "50%", transform: "translateX(-50%)",
        zIndex: E.zModal, transition: pulling ? "none" : "opacity 0.3s",
        opacity: pullY > 10 ? 1 : 0, pointerEvents: "none",
      }}>
        <div style={{
          background: NAVY, color: "white", borderRadius: 20, padding: "6px 14px",
          display: "flex", alignItems: "center", gap: 8,
          fontSize: 12, fontWeight: 700, fontFamily: "'Outfit', sans-serif",
          boxShadow: E.shadowMd,
        }}>
          <span style={{
            display: "inline-block",
            transform: `rotate(${refreshing ? 0 : Math.min(pullY / THRESHOLD * 180, 180)}deg)`,
            transition: refreshing ? "none" : "transform 0.1s",
            animation: refreshing ? "spin 0.8s linear infinite" : "none",
          }}>↓</span>
          {refreshing ? "Actualisation…" : pullY >= THRESHOLD ? "Relâchez !" : "Tirez pour actualiser"}
        </div>
      </div>
      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
      {children}
    </div>
  );
}

// ─── ERROR BOUNDARY ───────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(e) { return { error: e }; }
  componentDidCatch(e, info) { console.error("Crash:", e, info); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: "40px 24px", maxWidth: 480, margin: "0 auto", fontFamily: "'Outfit', sans-serif" }}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, color: "#0d1b3e", fontSize: 20, marginBottom: 8 }}>Une erreur est survenue</h2>
            <p style={{ color: "#6b7a90", fontSize: 14, lineHeight: 1.6 }}>L'application a rencontré un problème.</p>
          </div>
          <div style={{ background: "#fff8f8", border: "1px solid #fee2e2", borderRadius: 12, padding: "14px 16px", marginBottom: 20 }}>
            <div style={{ fontFamily: "monospace", fontSize: 12, color: "#ef4444", wordBreak: "break-all", lineHeight: 1.6 }}>
              {this.state.error?.message || String(this.state.error)}
            </div>
          </div>
          <button onClick={() => { this.setState({ error: null }); window.location.reload(); }}
            style={{ width: "100%", background: "#0d1b3e", color: "white", border: "none", borderRadius: 12, padding: "14px", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}>
            🔄 Recharger l'application
          </button>
          <button onClick={() => { localStorage.clear(); window.location.reload(); }}
            style={{ width: "100%", background: "transparent", color: "#6b7a90", border: "1px solid #e8ecf2", borderRadius: 12, padding: "14px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "'Outfit', sans-serif", marginTop: 10 }}>
            🗑 Vider le cache et recharger
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  const { w: screenWidth } = useWindowSize();
  const isWide = screenWidth >= 600;
  const [view, setViewRaw] = useState(() => {
    if (typeof window !== "undefined") {
      const hash = window.location.hash;
      const path = window.location.pathname;
      const search = window.location.search;
      const params = new URLSearchParams(search);
      if (
        path.includes("email-confirmed") ||
        hash.includes("type=signup") ||
        search.includes("type=signup") ||
        hash.includes("access_token") ||
        params.get("token_hash") ||
        params.get("type") === "signup" ||
        params.get("type") === "email_change"
      ) {
        return "email-confirmed";
      }
      if (path.startsWith("/coach/") && path.length > 7) return "coach-public";
      if (search.includes("action=review")) return "client-dashboard";
      // Liens depuis emails : ?view=coach-dashboard ou ?view=client-dashboard
      const viewParam = params.get("view");
      if (viewParam) return viewParam;
    }
    return "home";
  });

  // Tab initial depuis URL email (?tab=bookings etc.)
  const initialTab = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("tab") || "home"
    : "home";

  const coachSlug = typeof window !== "undefined" && window.location.pathname.startsWith("/coach/")
    ? window.location.pathname.slice(7)
    : null;

  const pendingReviewBookingId = typeof window !== "undefined"
    ? (new URLSearchParams(window.location.search).get("action") === "review"
        ? new URLSearchParams(window.location.search).get("booking")
        : null)
    : null;

  // Navigation avec historique
  const [history, setHistory] = useState([]);
  const setView = (v) => {
    setHistory(h => [...h.slice(-4), view]);
    setViewRaw(v);
    // Update meta title
    const titles = {
      home: "Elivio — Coaching & Transition Professionnelle",
      browse: "Nos coaches — Elivio",
      "client-dashboard": "Mes réservations — Elivio",
      "coach-dashboard": "Mon espace coach — Elivio",
      admin: "Panel Admin — Elivio",
      quiz: "Trouver mon coach — Elivio",
    };
    document.title = titles[v] || "Elivio";
    // Safari PWA scroll fix
    window.scrollTo({ top: 0, behavior: "smooth" });
    document.documentElement.scrollTo({ top: 0, behavior: "smooth" });
    document.body.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goBack = () => {
    if (history.length > 0) {
      const prev = history[history.length - 1];
      setHistory(h => h.slice(0, -1));
      setViewRaw(prev);
    }
  };

  const [coaches, setCoaches] = useState([]);
  const [loadingCoaches, setLoadingCoaches] = useState(true);
  const [quizStep, setQuizStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [matches, setMatches] = useState([]);
  const [aiSummary, setAiSummary] = useState("");
  const [loadingMatch, setLoadingMatch] = useState(false);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("Tous");
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [bookingCoach, setBookingCoach] = useState(null);
  const [coachTab, setCoachTab] = useState("home");
  const [clientTab, setClientTab] = useState("home");
  const [showAuth, setShowAuth] = useState(false);
  const [authDefaultMode, setAuthDefaultMode] = useState("signin");
  const [showClientOnboarding, setShowClientOnboarding] = useState(false);

  const openAuth = (mode = "signin") => { setAuthDefaultMode(mode); setShowAuth(true); };
  const [user, setUser] = useState(null);

  const allTypes = ["Tous", ...COACH_TYPES];
  const isCoach = user?.role === "coach";
  const isAdmin = user?.role === "admin";

  // Open Graph dynamique
  useEffect(() => {
    const setMeta = (prop, content) => {
      let el = document.querySelector(`meta[property="${prop}"]`) || document.querySelector(`meta[name="${prop}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(prop.startsWith("og:") ? "property" : "name", prop); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("og:title", document.title);
    setMeta("og:description", "Trouvez le coach idéal pour votre transition professionnelle grâce à notre algorithme de matching IA.");
    setMeta("og:image", "https://nmddkyutkwiedkxsjsys.supabase.co/storage/v1/object/public/assets/Elivio%20logo.png");
    setMeta("og:url", "https://elivio.fr");
    setMeta("og:type", "website");
    setMeta("description", "Trouvez le coach idéal pour votre transition professionnelle avec Elivio — matching IA, réservation en ligne, coaches certifiés.");
  }, [view]);

  // Splash screen

  // Restaure la session au chargement
  useEffect(() => {
    const savedUser = localStorage.getItem("elivio_user");
    if (savedUser && sb.token) {
      try {
        const u = JSON.parse(savedUser);
        setUser(u);
        // Redirige vers la bonne vue selon le rôle
        if (!["coach-public", "email-confirmed"].includes(view)) {
          if (u.role === "admin") setView("admin");
          else if (u.role === "coach") setView("coach-dashboard");
          else setView("client-dashboard");
        }
        // Vérifie que le token est toujours valide
        sb.q("profiles").eq("id", u.id).single().get().then(profile => {
          if (profile?.error || !profile) {
            sb.signOut();
            setUser(null);
            localStorage.removeItem("elivio_user");
            setView("home");
          }
        }).catch(() => {});
      } catch {
        localStorage.removeItem("elivio_user");
      }
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoadingCoaches(true);
      try {
        const data = await sb.q("coach_stats").eq("status", "active").get();
        setCoaches(Array.isArray(data) ? data.map(c => ({ ...c, id: c.coach_id, name: c.name || c.full_name || "" })) : []);
      } catch { setCoaches([]); }
      setLoadingCoaches(false);
    })();
  }, []);

  const handleAnswer = (qid, val) => {
    const newAns = { ...answers, [qid]: val };
    setAnswers(newAns);
    if (quizStep < questions.length - 1) setQuizStep(s => s + 1);
    else runMatching(newAns);
  };

  const runMatching = async (ans) => {
    setView("results"); setLoadingMatch(true); setAiSummary("");
    const scored = [...coaches].map(c => ({ ...c, score: scoreCoach(c, ans) })).sort((a, b) => b.score - a.score);
    setMatches(scored);
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 1000, messages: [{ role: "user", content: `Utilisateur cherche coach: objectif=${ans.goal}, défi=${ans.challenge}, style=${ans.style}, budget=${ans.budget}. 2 phrases chaleureuses en français.` }] }),
      });
      const d = await res.json();
      setAiSummary(d.content?.map(b => b.text || "").join("") || "");
    } catch { setAiSummary("Voici vos meilleures correspondances selon votre profil."); }
    setLoadingMatch(false);
  };

  const handleBook = (coach) => { if (!user) { setShowAuth(true); return; } setBookingCoach(coach); };

  const filtered = coaches.filter(c => {
    const s = search.toLowerCase();
    return (!s || c.name?.toLowerCase().includes(s) || (c.specialties || []).some(x => x.toLowerCase().includes(s)))
      && (filterType === "Tous" || (c.types || []).includes(filterType));
  });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: ${E.offWhite}; padding-bottom: env(safe-area-inset-bottom); }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: ${E.grayLight}; border-radius: 4px; }
        @keyframes fadeUp { from { opacity:0; transform:translateY(16px) } to { opacity:1; transform:translateY(0) } }
        @keyframes fadeTab { from { opacity:0; transform:translateX(8px) } to { opacity:1; transform:translateX(0) } }
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes dotBounce { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-8px)} }
        @keyframes splashPulse { 0%{opacity:0;transform:scale(0.8)} 50%{opacity:1;transform:scale(1.05)} 100%{opacity:1;transform:scale(1)} }
        * { -webkit-tap-highlight-color: transparent; }
        input, select, textarea, button { font-family: 'Outfit', sans-serif; }
        button:active { transform: scale(0.97); }
        a { -webkit-tap-highlight-color: transparent; }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.5} }
        input[type="date"]::-webkit-calendar-picker-indicator { opacity: 0.5; }
        input[type="time"]::-webkit-calendar-picker-indicator { opacity: 0.5; }
      `}</style>

      <PullToRefresh onRefresh={async () => {
        const data = await sb.q("coach_stats").eq("status", "active").get();
        if (Array.isArray(data)) {
          setCoaches(data.map(c => ({ ...c, id: c.coach_id, name: c.name || c.full_name || "" })));
        }
      }}>
      <div style={{ minHeight: "100vh", background: E.offWhite, fontFamily: "'Outfit', sans-serif", color: E.text }}>

        {/* ── NAV ── */}
        <div style={{ background: E.white, borderBottom: `1px solid ${E.grayLight}`, padding: "10px 16px", paddingTop: "calc(10px + env(safe-area-inset-top))", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, zIndex: E.zSticky, boxShadow: "E.shadowSm" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ cursor: "pointer", flexShrink: 0 }} onClick={() => setView("home")}>
              <ElivioLogo size={28} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "nowrap" }}>
            {!user && <Btn small variant="navy" onClick={() => setView("home")}>Accueil</Btn>}
            <Btn small variant="ghost" onClick={() => setView("browse")}>Nos coaches</Btn>
            {user ? (
              <>
                {isAdmin && <Btn small variant="ghost" onClick={() => setView("admin")}>Admin</Btn>}
                {isCoach
                  ? <Btn small variant="ghost" onClick={async () => {
                      const coachProfiles = await sb.q("coaches").eq("user_id", user.id).limit(1).get();
                      if (Array.isArray(coachProfiles) && coachProfiles.length > 0) setView("coach-dashboard");
                      else setView("coach-onboarding");
                    }}>Mon espace</Btn>
                  : <Btn small variant="ghost" onClick={() => setView("client-dashboard")}>Mon espace</Btn>
                }
                {isAdmin
                  ? <button onClick={() => { sb.signOut(); setUser(null); localStorage.removeItem("elivio_user"); setView("home"); toast("À bientôt ! 👋", "info"); }}
                      style={{ background: E.offWhite, border: `1px solid ${E.grayLight}`, borderRadius: 8, width: 32, height: 32, cursor: "pointer", color: E.gray, fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}
                      title="Déconnexion">
                      ⏻
                    </button>
                  : <Btn small variant="ghost" onClick={() => { sb.signOut(); setUser(null); localStorage.removeItem("elivio_user"); setView("home"); toast("À bientôt ! 👋", "info"); }}>Déconnexion</Btn>
                }
              </>
            ) : (
              <Btn small variant="navy" onClick={() => openAuth("signin")}>Connexion</Btn>
            )}
          </div>
        </div>

        {/* ── BREADCRUMB ── */}
        {!["home", "email-confirmed"].includes(view) && (
          <div style={{ background: E.offWhite, borderBottom: `1px solid ${E.grayLight}`, padding: "7px 20px", display: "flex", alignItems: "center", gap: 6, overflowX: "auto", whiteSpace: "nowrap" }}>
            <button onClick={() => setView("home")} style={{ background: "transparent", border: "none", color: E.gray, fontSize: 12, cursor: "pointer", fontFamily: "'Outfit', sans-serif", padding: 0, flexShrink: 0 }}>Accueil</button>

            {/* Niveau 2 — vue */}
            {view !== "home" && (() => {
              const viewLabels = {
                browse:           "Nos coaches",
                quiz:             "Matching IA",
                matches:          "Résultats",
                "client-dashboard":"Mon espace",
                "coach-dashboard": "Mon espace",
                "coach-onboarding":"Inscription coach",
                admin:            "Administration",
                "coach-public":   "Fiche coach",
              };
              const label = viewLabels[view];
              if (!label) return null;

              // Si on est dans un dashboard avec onglet → label cliquable
              const isDashboard = ["client-dashboard", "coach-dashboard"].includes(view);
              const tabLabels = {
                home: "Accueil", bookings: "Réservations", tasks: "Actions",
                agenda: "Agenda", profile: "Mon profil", stats: "Statistiques",
                niveau: "Mon niveau", loyalty: "Fidélité & Parrainage",
              };
              const currentTabLabel = isDashboard && coachTab && tabLabels[coachTab]
                ? tabLabels[coachTab]
                : isDashboard && clientTab && tabLabels[clientTab]
                  ? tabLabels[clientTab]
                  : null;

              return (
                <>
                  <span style={{ color: E.grayLight, fontSize: 12, flexShrink: 0 }}>›</span>
                  <button
                    onClick={() => isDashboard && currentTabLabel ? (view === "coach-dashboard" ? setCoachTab("home") : setClientTab("home")) : null}
                    style={{ background: "transparent", border: "none", color: currentTabLabel ? E.gray : E.navy, fontSize: 12, cursor: isDashboard && currentTabLabel ? "pointer" : "default", fontFamily: "'Outfit', sans-serif", padding: 0, fontWeight: currentTabLabel ? 400 : 600, flexShrink: 0 }}>
                    {label}
                  </button>

                  {/* Niveau 3 — onglet */}
                  {currentTabLabel && (
                    <>
                      <span style={{ color: E.grayLight, fontSize: 12, flexShrink: 0 }}>›</span>
                      <span style={{ color: E.navy, fontSize: 12, fontWeight: 600, fontFamily: "'Outfit', sans-serif", flexShrink: 0 }}>{currentTabLabel}</span>
                    </>
                  )}
                </>
              );
            })()}
          </div>
        )}

        <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 20px 80px" }}>

          {/* ── EMAIL CONFIRMÉ ── */}
          {view === "coach-public" && coachSlug && (
            <CoachPublicPage
              slug={coachSlug}
              onBook={(coach) => {
                if (!user) {
                  openAuth("signin");
                } else {
                  setBookingCoach(coach);
                }
              }}
              onConnect={() => openAuth("signin")}
            />
          )}

          {view === "email-confirmed" && (
            <div style={{ padding: "60px 0", textAlign: "center", animation: "fadeUp 0.5s ease" }}>
              {/* Échange le token PKCE automatiquement */}
              {(() => {
                const params = new URLSearchParams(window.location.search);
                const tokenHash = params.get("token_hash");
                const type = params.get("type");
                if (tokenHash && type) {
                  fetch(`${SUPABASE_URL}/auth/v1/verify`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "apikey": SUPABASE_ANON_KEY },
                    body: JSON.stringify({ token_hash: tokenHash, type })
                  }).then(r => r.json()).then(d => {
                    if (d.access_token) sb.setToken(d.access_token);
                  }).catch(() => {});
                }
                return null;
              })()}
              <div style={{ width: 72, height: 72, background: E.tealLight, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                  <circle cx="18" cy="18" r="18" fill={E.teal}/>
                  <path d="M10 18 L15.5 23.5 L26 13" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h1 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 26, color: E.navy, marginBottom: 10 }}>Email confirmé !</h1>
              <p style={{ color: E.gray, fontSize: 14, lineHeight: 1.7, maxWidth: 360, margin: "0 auto 32px" }}>
                Votre adresse email a bien été vérifiée. Bienvenue sur Elivio — vous faites maintenant partie de notre communauté.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 360, margin: "0 auto 32px" }}>
                {[["1", "Connectez-vous à votre espace personnel"], ["2", "Complétez votre profil"], ["3", "Commencez votre expérience Elivio"]].map(([n, t]) => (
                  <div key={n} style={{ display: "flex", alignItems: "center", gap: 12, background: E.offWhite, borderRadius: 12, padding: "12px 16px" }}>
                    <div style={{ width: 28, height: 28, background: TEAL, color: E.white, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{n}</div>
                    <span style={{ fontSize: 13, color: E.navy, fontWeight: 500, fontFamily: "'Outfit', sans-serif" }}>{t}</span>
                  </div>
                ))}
              </div>
              <Btn onClick={() => { setView("home"); setShowAuth(true); openAuth("signin"); }}>
                Accéder à mon espace →
              </Btn>
            </div>
          )}

          {/* ── HOME ── */}
          {view === "home" && (
            <div style={{ animation: "fadeUp 0.5s ease" }}>
              {/* Hero */}
              <div style={{ background: NAVY, borderRadius: `0 0 ${E.radius * 2}px ${E.radius * 2}px`, padding: "56px 32px 48px", marginBottom: 40, textAlign: "center", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: -40, right: -40, width: 200, height: 200, borderRadius: "50%", background: `${E.teal}15` }} />
                <div style={{ position: "absolute", bottom: -20, left: -20, width: 120, height: 120, borderRadius: "50%", background: `${E.teal}10` }} />
                <div style={{ position: "relative" }}>
                  <div style={{ display: "inline-block", background: `${E.teal}25`, border: `1px solid ${E.teal}50`, borderRadius: 20, padding: "4px 16px", fontSize: 11, color: E.teal, marginBottom: 20, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" }}>
                    Matching IA · Elivio
                  </div>
                  <h1 style={{ fontFamily: "'Outfit', sans-serif", fontSize: "clamp(30px,7vw,48px)", fontWeight: 900, lineHeight: 1.15, letterSpacing: -0.5, color: E.white, marginBottom: 16 }}>
                    Réinventez votre<br />
                    <span style={{ color: E.teal }}>avenir professionnel</span>
                  </h1>
                  <p style={{ color: "rgba(255,255,255,0.65)", fontSize: 16, lineHeight: 1.7, maxWidth: 400, margin: "0 auto 32px" }}>
                    Trouvez l'accompagnement idéal pour votre transition professionnelle grâce à notre algorithme de matching.
                  </p>
                  <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                    <button
                      onClick={() => { setView("quiz"); setQuizStep(0); setAnswers({}); }}
                      style={{ background: TEAL, color: E.white, border: "none", borderRadius: E.radiusSm, padding: "14px 28px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit', sans-serif", boxShadow: `0 4px 20px ${E.teal}55`, transition: "all 0.15s" }}
                    >Trouver mon accompagnement</button>
                    <button
                      onClick={() => setView("browse")}
                      style={{ background: "transparent", color: E.white, border: `2px solid rgba(255,255,255,0.3)`, borderRadius: E.radiusSm, padding: "14px 28px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "'Outfit', sans-serif" }}
                    >Explorer les coaches</button>
                  </div>
                </div>
              </div>

              {/* Values */}
              <div style={{ display: "grid", gap: 14, marginBottom: 32 }}>
                {[
                  ["🧭", "Adaptation", "Nous nous adaptons à votre parcours unique et à vos besoins spécifiques pour vous offrir un accompagnement sur mesure."],
                  ["👥", "Parcours atypiques", "Nous valorisons les parcours non conventionnels et aidons chacun à transformer son unicité en force."],
                  ["❤️", "Accompagnement humain", "Au cœur de notre approche : l'écoute, l'empathie et un soutien personnalisé pour vous guider vers votre réussite."],
                  ["📈", "Évolution", "Nous croyons en votre potentiel d'évolution et vous accompagnons dans chaque étape de votre transformation professionnelle."],
                ].map(([icon, title, desc]) => (
                  <Card key={title} style={{ padding: "20px 22px", display: "flex", gap: 16, alignItems: "flex-start" }}>
                    <div style={{ width: 52, height: 52, borderRadius: 14, background: E.tealLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>{icon}</div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 16, color: E.navy, marginBottom: 8, fontFamily: "'Outfit', sans-serif" }}>{title}</div>
                      <div style={{ color: E.textSub, fontSize: 13, lineHeight: 1.65 }}>{desc}</div>
                    </div>
                  </Card>
                ))}
              </div>


            </div>
          )}

          {/* ── QUIZ ── */}
          {view === "quiz" && (
            <div style={{ padding: "48px 0", animation: "fadeUp 0.4s ease" }}>
              <div style={{ display: "flex", gap: 8, marginBottom: 32 }}>
                {questions.map((_, i) => (
                  <div key={i} style={{ flex: 1, height: 4, borderRadius: 4, background: i <= quizStep ? E.teal : E.grayLight, transition: "background 0.3s" }} />
                ))}
              </div>
              <div style={{ color: E.teal, fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8 }}>
                Question {quizStep + 1} / {questions.length}
              </div>
              <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 22, fontWeight: 800, color: E.navy, marginBottom: 28, lineHeight: 1.3 }}>
                {questions[quizStep].question}
              </h2>
              <div style={{ display: "grid", gap: 10 }}>
                {questions[quizStep].options.map(opt => (
                  <button key={opt.value} onClick={() => handleAnswer(questions[quizStep].id, opt.value)}
                    style={{ background: E.white, border: `2px solid ${E.grayLight}`, borderRadius: E.radius, padding: "16px 20px", textAlign: "left", color: E.text, fontSize: 14, cursor: "pointer", fontFamily: "'Outfit', sans-serif", transition: "all 0.15s", display: "flex", alignItems: "center", gap: 12, boxShadow: E.shadow, fontWeight: 500 }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = E.teal; e.currentTarget.style.boxShadow = `0 4px 20px ${E.teal}22`; e.currentTarget.style.transform = "translateY(-1px)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = E.grayLight; e.currentTarget.style.boxShadow = E.shadow; e.currentTarget.style.transform = "none"; }}
                  >
                    <span style={{ fontSize: 20 }}>{opt.icon}</span>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── RESULTS ── */}
          {view === "results" && (
            <div style={{ padding: "40px 0", animation: "fadeUp 0.4s ease" }}>
              <div style={{ background: NAVY, borderRadius: E.radius, padding: "24px", marginBottom: 24 }}>
                <div style={{ color: E.teal, fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8 }}>Résultats de votre matching</div>
                <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 22, fontWeight: 800, color: E.white, marginBottom: 8 }}>Vos coaches recommandés</h2>
                {loadingMatch
                  ? <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>Analyse de votre profil en cours…</div>
                  : aiSummary && <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 14, lineHeight: 1.7 }}>{aiSummary}</p>
                }
              </div>
              <div style={{ display: "grid", gap: 14 }}>
                {matches.map(c => <CoachCard key={c.id} coach={c} score={c.score} onBook={handleBook} onSelect={setSelectedCoach} user={user} />)}
              </div>
              <div style={{ marginTop: 20 }}>
                <Btn variant="ghost" onClick={() => { setView("quiz"); setQuizStep(0); setAnswers({}); }}>← Refaire le questionnaire</Btn>
              </div>
            </div>
          )}

          {/* ── BROWSE ── */}
          {view === "browse" && (
            <div style={{ padding: "32px 0", animation: "fadeUp 0.4s ease" }}>
              <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 24, fontWeight: 800, color: E.navy, marginBottom: 20 }}>Nos coaches</h2>
              <div style={{ position: "relative", marginBottom: 14 }}>
                <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: E.gray, fontSize: 16 }}>🔍</span>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un coach ou une spécialité…"
                  style={{ width: "100%", background: E.white, border: `1.5px solid ${E.grayLight}`, borderRadius: E.radiusSm, padding: "12px 16px 12px 42px", color: E.text, fontSize: 14, fontFamily: "'Outfit', sans-serif", outline: "none", boxShadow: E.shadow }} />
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 24 }}>
                {allTypes.map(t => (
                  <button key={t} onClick={() => setFilterType(t)} style={{
                    background: filterType === t ? E.navy : E.white,
                    border: `1.5px solid ${filterType === t ? E.navy : E.grayLight}`,
                    color: filterType === t ? E.white : E.gray,
                    borderRadius: 20, padding: "6px 16px", fontSize: 12, cursor: "pointer",
                    fontFamily: "'Outfit', sans-serif", fontWeight: 600, transition: "all 0.15s",
                    boxShadow: filterType === t ? "none" : E.shadow,
                  }}>{t}</button>
                ))}
              </div>
              {loadingCoaches
                ? <div style={{ display: "grid", gridTemplateColumns: isWide ? "1fr 1fr" : "1fr", gap: 14, width: "100%", overflow: "hidden" }}>{[1,2,3,4].map(i => <SkeletonCard key={i} />)}</div>
                : (
                  <div style={{ display: "grid", gridTemplateColumns: isWide ? "1fr 1fr" : "1fr", gap: 14, width: "100%", overflow: "hidden" }}>
                    {filtered.map(c => <CoachCard key={c.id} coach={c} onBook={handleBook} onSelect={setSelectedCoach} user={user} />)}
                    {filtered.length === 0 && (
                      <div style={{ gridColumn: isWide ? "1/-1" : "1" }}>
                        <EmptyState icon="🔍" title="Aucun coach trouvé" desc="Essayez d'autres filtres ou revenez bientôt — de nouveaux coaches rejoignent Elivio chaque semaine !" cta="Voir tous les coaches" onCta={() => { setSearch(""); setFilterType("Tous"); }} />
                      </div>
                    )}
                  </div>
                )
              }
            </div>
          )}

          {view === "coach-onboarding" && user && <CoachOnboarding user={user} onDone={() => setView("coach-dashboard")} />}
          {view === "coach-dashboard" && user && <ErrorBoundary><CoachDashboard user={user} onTabChange={setCoachTab} initialTab={initialTab} /></ErrorBoundary>}
          {view === "client-dashboard" && user && !isCoach && <ErrorBoundary><ClientDashboard user={user} coaches={coaches} onNavigate={setView} onTabChange={setClientTab} pendingReviewBookingId={pendingReviewBookingId} initialTab={initialTab} /></ErrorBoundary>}
          {view === "admin" && isAdmin && <AdminPanel user={user} onNavigate={setView} />}

          {/* ── CTA INSCRIPTION (accueil uniquement) ── */}
          {view === "home" && (
            <div style={{ marginTop: 48, marginBottom: 8 }}>
              <div style={{ textAlign: "center", marginBottom: 24 }}>
                <div style={{ color: E.teal, fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8, fontFamily: "'Outfit', sans-serif" }}>Rejoignez Elivio</div>
                <h2 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 22, color: E.navy }}>Prêt à franchir le pas ?</h2>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div style={{ background: E.tealLight, borderRadius: E.radius * 1.5, padding: "24px 18px", textAlign: "center", border: `1px solid ${E.teal}22`, display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <div style={{ width: 48, height: 48, borderRadius: 12, background: E.white, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, margin: "0 auto 14px", boxShadow: E.shadow }}>🎯</div>
                  <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 14, color: E.navy, marginBottom: 8, height: 40, display: "flex", alignItems: "center", justifyContent: "center" }}>Je cherche un coach</div>
                  <p style={{ color: E.textSub, fontSize: 12, lineHeight: 1.6, flex: 1, height: 60 }}>Trouvez l'accompagnement idéal pour votre transition.</p>
                  <button onClick={() => openAuth("signup")} style={{ background: TEAL, color: E.white, border: "none", borderRadius: E.radiusSm, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit', sans-serif", width: "100%", boxShadow: `0 4px 14px ${E.teal}44`, height: 44, marginTop: 16 }}>
                    Trouver mon coach
                  </button>
                </div>
                <div style={{ background: NAVY, borderRadius: E.radius * 1.5, padding: "24px 18px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <div style={{ width: 48, height: 48, borderRadius: 12, background: `${E.teal}25`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, margin: "0 auto 14px" }}>🌟</div>
                  <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 14, color: E.white, marginBottom: 8, height: 40, display: "flex", alignItems: "center", justifyContent: "center" }}>Je suis coach</div>
                  <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 12, lineHeight: 1.6, flex: 1, height: 60 }}>Rejoignez notre réseau et développez votre activité.</p>
                  <button onClick={() => openAuth("signup")} style={{ background: "transparent", color: E.white, border: `2px solid rgba(255,255,255,0.4)`, borderRadius: E.radiusSm, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit', sans-serif", width: "100%", height: 44, marginTop: 16 }}>
                    Rejoindre Elivio
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── FOOTER ── */}
        <div style={{ background: NAVY, padding: "40px 24px", textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}><ElivioLogo size={28} /></div>
          <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 13, marginBottom: 8 }}>Votre partenaire de confiance pour une transition professionnelle réussie.</p>
          <p style={{ color: "rgba(255,255,255,0.3)", fontSize: 12 }}>© 2026 Elivio. Tous droits réservés.</p>
        </div>
      </div>

      {showAuth && <AuthModal defaultMode={authDefaultMode} onClose={() => setShowAuth(false)} onAuth={async u => {
        try {
          let realRole = u.role || "client";
          if (u.id) {
            const profile = await sb.q("profiles").eq("id", u.id).single().get();
            if (profile && !profile.error) realRole = profile.role || realRole;
          }
          const finalUser = { ...u, role: realRole };
          setUser(finalUser);
          localStorage.setItem("elivio_user", JSON.stringify(finalUser));

          // S'assure que l'email est bien dans profiles (utilisé pour les emails transactionnels)
          if (u.id && u.email) {
            sb.q("profiles").eq("id", u.id).update({ email: u.email }).catch(() => {});
          }
          if (realRole === "admin") {
            setView("admin");
          } else if (realRole === "coach") {
            // Cherche le profil coach sans .single() pour éviter l'erreur si inexistant
            const coachProfiles = await sb.q("coaches").eq("user_id", u.id).limit(1).get();
            if (Array.isArray(coachProfiles) && coachProfiles.length > 0) {
              setView("coach-dashboard");
            } else {
              setView("coach-onboarding");
            }
          } else {
            setView("client-dashboard");
            // Onboarding uniquement à la première connexion
            const firstLogin = !localStorage.getItem(`elivio_onboarded_${u.id}`);
            if (firstLogin) {
              localStorage.setItem(`elivio_onboarded_${u.id}`, "1");
              setShowClientOnboarding(true);
            }
          }
        } catch {
          // En cas d'erreur réseau, on garde l'utilisateur connecté sans changer la vue
          setUser(u);
          localStorage.setItem("elivio_user", JSON.stringify(u));
          if (u.role === "admin") setView("admin");
          else if (u.role === "coach") setView("coach-dashboard");
          else setView("client-dashboard");
        }
      }} />}
      {selectedCoach && <CoachDetailModal coach={selectedCoach} user={user} onBook={handleBook} onClose={() => setSelectedCoach(null)} />}
      {bookingCoach && user && <BookingModal coach={bookingCoach} user={user} onClose={() => setBookingCoach(null)} />}
      {showClientOnboarding && user && !isCoach && (
        <ClientOnboarding user={user} onDone={() => {
          setShowClientOnboarding(false);
          setView("quiz");
          setQuizStep(0);
          setAnswers({});
        }} />
      )}

      {/* ── CHATBOX IA ── */}
      <ChatBox onSuggestCoaches={() => setView("browse")} />
      <NetworkStatus />
      <ToastContainer />
      </PullToRefresh>
    </>
  );
}
